import {
  useCustomerDropDownList,
  useGomakeAxios,
  useGomakeRouter,
  useSnackBar,
} from "@/hooks";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ApprovalStatus,
  DELIVERY_NOTE_STATUSES,
  EStatisticsLabels,
  INVOICES_EXPORT_TYPES,
  LogActionType,
  QUOTE_STATUSES,
  StatusTitleText,
} from "./enums";
import { MoreMenuWidget } from "./more-circle";
import { getAllProductsForDropDownList } from "@/services/hooks";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import { printHouseHasPackagingSlipPrinter } from "@/store/print-house-profile";
import {
  agentsCategoriesState,
  customerSupportRepresentativesCategoriesState,
  closeOrderAsInvoiceOrDeliveryNoteState,
  isSelectedCLoseAsDeliveryNoteState,
  isSelectedCLoseAsProformaInvoiceState,
} from "@/pages/customers/customer-states";
import { getAndSetEmployees2 } from "@/services/api-service/customers/employees-api";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useDateFormat } from "@/hooks/use-date-format";
import { _renderStatus } from "@/utils/constants";
import {
  isPurchaseOrderCanceled,
  isPurchaseOrderStatusChangeLocked,
} from "@/utils/purchase-order-utils";
import { PurchaseOrderStatusSelector } from "@/widgets/purchas-orders-dialogs/purchase-order-status-selector";
import { selectedClientState } from "./states";
import {
  cancelDocumentApi,
  closeAndTrackOrderApi,
  createNewDocumentApi,
  duplicateDocumentApi,
  ExportDocumentTableToExcelApi,
  ExportOrderItemsTableToExcelApi,
  ExportDocumentTableToPdfApi,
  getAllDocumentLogsApi,
  getAllDocumentsApi,
  getDocumentApi,
  getDocumentInventoryApi,
  getDocumentPdfApi,
  manuallyCloseDocumentApi,
  moveOrderToReadyApi,
  OpenCanceledDocumentApi,
  OpenDocumentApi,
  pauseOrdResumeOrderApi,
  removeDraftDocumentApi,
  startOrderProductionApi,
  updateDocumentApi,
  updateDocumentStatusApi,
  undoDocumentIntakeApi,
  undoDocumentItemIntakeApi,
} from "@/services/api-service/generic-doc/documents-api";
import { DOCUMENT_TYPE } from "./enums";
import { useQuoteGetData } from "../quote-new/use-quote-get-data";
import { useStyle } from "./style";
import {
  CLIENT_TYPE,
  CLIENT_TYPE_Id,
  DEFAULT_VALUES,
} from "@/pages/customers/enums";
import {
  cancelReceiptApi,
  getAllReceiptsApi,
  getReceiptInvoicesPdfApi,
  getReceiptPdfApi,
} from "@/services/api-service/generic-doc/receipts-api";
import {
  renderDocumentTypeForSourceDocumentNumber,
  renderURLDocumentType,
} from "@/widgets/settings-documenting/documentDesign/enums/document-type";
import {
  AStatus,
  BoardMissionStatusForFilter,
  DocumentCloseStatus,
  PStatus,
  ReceiptAccountingStatus,
} from "../board-missions/widgets/enums";
import { getAndSetCustomerById } from "@/services/api-service/customers/customers-api";
import { useUserPermission } from "@/hooks/use-permission";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { EPreferences } from "@/enums/prefernces";
import { ShowPricesEnum } from "@/enums";
import { Permissions } from "@/components/CheckPermission/enum";
import { PermissionCheck } from "@/components/CheckPermission/check-permission";
import {
  StatePayment,
  sendPaymentState,
} from "./widgets/send-payment-modal/states";
import {
  getDocumentContactApi,
  sendDocumentToClientForPaymentApi,
} from "@/services/api-service/pay/pay-api";
import { useRouter } from "next/router";
import { ICard } from "./widgets/statistics-section/statistics-sections";
import { ClickableStatusButton } from "./widgets/delivery-status-button/delivery-status-button";
import { ProofingNotScheduledDateField } from "@/widgets/production-floor/components/proofing-not-scheduled-date-field";
import { DocumentDeliveryStatuses } from "./widgets/delivery-status-button/delivery-status-modal/use-delivery-status-modal";
import {
  EMessageStatus,
  PurchaseOrderStatuses,
} from "@/enums/document-type-enum";
import { useDocumentsTableSocketIo } from "@/hooks/socket-io/use-documents-table-socket-io";
import { QuoteIfExistState } from "../quote-new/store/quote";
import { userQuoteState } from "@/store/user-qoute-state";
import { useClientTypesList } from "@/hooks/use-client-types";
import { useModal } from "@/hooks/use-modal";
import { getTaxConfirmationNumberApi } from "@/services/api-service/generic-doc/invoices";
import { getDeliveryTickerPdfApi } from "@/services/api-service/board-missions/board-missions-table";
import {
  exportDeliveryTicketPDFApi,
  printDeliveryTicketPDFApi,
  toggleOrderLockApi,
} from "@/services/api-service/generic-doc/orders-api";
import { GomakeTextInput, GoMakeMenu } from "@/components";
import { assertTableExportRecordCountWithinLimit } from "@/utils/assert-table-export-record-count";
import { downloadPdf, generateFilename } from "@/utils/helpers";
import { format } from "date-fns";
import { PAYMENT_TYPE } from "../deposit/enums";
import { useListPagination } from "@/hooks/use-list-pagination";
import { useScheduledReports } from "@/hooks/use-scheduled-reports";
import {
  columnOrderStateFamily,
  columnVisibilityStateFamily,
} from "./states/documents-columns-settings-state";
import {
  DeliveryNotesTableColumns,
  OrdersTableColumns,
  QuotesTableColumns,
} from "./enums/document-table-columns-enum";
import { CircularProgress, MenuItem, Typography } from "@mui/material";
import { currenciesState } from "@/widgets/materials-widget/state";
import { getCurrencies } from "@/services/api-service/general/enums";
import { useFrontDeskEmailIntegration } from "./hooks/use-frontdesk-email-integration";
import { printHouseHasFrontDeskFeature } from "@/store/print-house-profile";
import { OrderJobsCell } from "./widgets/order-jobs-modal/order-jobs-cell";
import { useTheme } from "@/contexts/theme/useTheme";
import { formatTermOfPaymentCell } from "@/utils/format-term-of-payment";

type PaymentTypeOption = {
  label: string;
  value: PAYMENT_TYPE;
};

interface Query {
  customerId?: string;
  customerName?: string;
  agentId?: string;
  customerSupportRepresentativeId?: string;
  statusId?: string;
  approvalStatusId?: string;
  minPrice?: string;
  maxPrice?: string;
  fromDate?: string;
  toDate?: string;
  checkFromDate?: string;
  checkToDate?: string;
  accountingStatus?: string;
  deliveryStatus?: string;
  productionStatus?: string;
  isCanceled?: boolean;
  isPaused?: boolean;
  isOrdersWithoutJobs?: boolean;
  pageNumber?: number;
  productsIds?: string[];
  printHouseMaterialIds?: string[];
  paymentType?: string;
  currency?: string;
  finalPatternSearch?: string;
  documentNumber?: string;
}

const resolveOrderTotalQuantity = (order: any): number => {
  if (!order) return 0;

  const directQuantity =
    order.totalOrderQuantity ?? order.quantity ?? order.numberOfItems;
  if (directQuantity != null && Number(directQuantity) > 0) {
    return Number(directQuantity);
  }

  if (Array.isArray(order.documentItems)) {
    const itemsTotal = order.documentItems.reduce(
      (sum: number, item: any) => sum + Number(item?.quantity ?? 0),
      0,
    );
    if (itemsTotal > 0) {
      return itemsTotal;
    }
  }

  return 0;
};

const normalizeOrderForPackages = (order: any, fallbackOrderId?: string) => {
  if (!order && fallbackOrderId) {
    return { id: fallbackOrderId, totalOrderQuantity: 0 };
  }
  if (!order) {
    return null;
  }

  const normalizedId = order.id ?? order.orderId ?? fallbackOrderId;

  return {
    ...order,
    id: normalizedId,
    totalOrderQuantity: resolveOrderTotalQuantity(order),
  };
};

const findOrderDocumentForMoveToDone = (
  documents: any[] | null | undefined,
  orderId?: string | null,
  orderNumber?: string | null,
) => {
  if (!documents?.length) {
    return null;
  }

  if (orderId) {
    const byId = documents.find(
      (document) => String(document.id ?? document.orderId) === String(orderId),
    );
    if (byId) {
      return byId;
    }
  }

  if (orderNumber) {
    return (
      documents.find(
        (document) => String(document.number) === String(orderNumber),
      ) ?? null
    );
  }

  return null;
};

const TITLE_CELL_MAX_HEIGHT = "40px";

const TitleCell = ({
  title,
  expanded,
  onToggle,
}: {
  title: string | null | undefined;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const text = (title ?? "").trim();

  const updateOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el || expanded) {
      setIsOverflowing(false);
      return;
    }
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    updateOverflow();
  }, [text, updateOverflow]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) {
      return;
    }

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded, updateOverflow]);

  const shouldShowToggle = expanded || isOverflowing;

  return (
    <div>
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? "none" : TITLE_CELL_MAX_HEIGHT,
          overflow: expanded ? "visible" : "hidden",
          lineHeight: "1.4",
        }}
      >
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {text}
        </span>
      </div>
      {shouldShowToggle && (
        <Typography
          onClick={onToggle}
          sx={{
            cursor: "pointer",
            color: "#000",
            textDecoration: "underline",
            display: "block",
            mt: "5px",
            fontWeight: 500,
            fontSize: "0.9rem",
            "&:hover": { color: "#717273" },
          }}
        >
          {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
        </Typography>
      )}
    </div>
  );
};

const useQuotes = (
  docType: DOCUMENT_TYPE,
  isFromHomePage,
  homeVariant: "classic" | "modern" = "classic",
) => {
  const { t } = useTranslation();
  /** Modern-theme home: the embedded per-customer list is filterable + paginated. */
  const isModernHome = !!isFromHomePage && homeVariant === "modern";
  const { CheckPreferenceValue } = usePrintHousePreferences();
  const showCustomerSupportRepresentative = !!CheckPreferenceValue(
    EPreferences.SHOW_CUSTOMER_SUPPORT_REPRESENTATIVE,
  );
  const direction = t("direction");
  const { classes } = useStyle();
  const { callApi } = useGomakeAxios();
  const {
    alertFaultUpdate,
    alertFaultDuplicate,
    alertFaultGetData,
    alertSuccessUpdate,
    alertFault,
    alertFaultDelete,
    alertSuccessGetData,
    alertSuccess,
  } = useSnackBar();
  const router = useRouter();
  const documentPath =
    docType === DOCUMENT_TYPE.supplierPayments
      ? `${DOCUMENT_TYPE[DOCUMENT_TYPE.receipt]
      }?isOutgoingPayments=true&isNewCreation=true`
      : DOCUMENT_TYPE[docType];

  const searchInputRef = useRef(null);
  const composerOpenedRef = useRef(false);
  const moveOrderToDoneModalOpenedRef = useRef(false);
  const pendingMoveToDoneOrderIdRef = useRef<string | null>(null);
  const pendingMoveToDoneOrderRef = useRef<any>(null);
  const shouldAutoSearchRef = useRef(false);
  const { getCurrencyUnitText } = useQuoteGetData();
  const { navigate } = useGomakeRouter();
  const { CheckPermission } = useUserPermission();
  const { query, pathname } = useRouter();
  const {
    quoteNumber,
    deliveryOrderId,
    deliveryOrderNumber,
    deliveryCustomerId,
  } = query;
  const { errorColor, primaryColor } = useGomakeTheme();
  const { GetDateFormat, formatCustomDateTime, formatISODateTime } =
    useDateFormat();
  const isPrintHouseHasPackagingSlipPrinter = useRecoilValue(
    printHouseHasPackagingSlipPrinter,
  );

  /** Order is in the proofing process and not yet done in production. */
  const isOrderInProofingProcess = (quote: any) =>
    !!quote?.isInProofingProcess && !quote?.productionStatus;

  const renderOrderDeliveryDateCell = (quote: any) =>
    isOrderInProofingProcess(quote) ? (
      <ProofingNotScheduledDateField tooltipKey="sales.quote.orderProofingJobsTooltip" />
    ) : (
      formatISODateTime(quote?.dueDate)
    );

  const renderOrderEstimatedProductionDateCell = (quote: any) => {
    if (!quote?.estimatedProductionDate) {
      return "-";
    }
    const formatted = formatISODateTime(quote.estimatedProductionDate);
    if (!formatted) {
      return "-";
    }
    const splitAt = formatted.lastIndexOf(" ");
    if (splitAt <= 0) {
      return formatted;
    }
    return (
      <>
        {formatted.slice(0, splitAt)}
        <br />
        {formatted.slice(splitAt + 1)}
      </>
    );
  };

  const renderOrderProductionStatusCell = (quote: any) =>
    quote?.productionStatus
      ? t("boardMissions.done")
      : t("boardMissions.inProduction");

  const [patternSearch, setPatternSearch] = useState<any>(
    router.query.finalPatternSearch || null,
  );
  const [finalPatternSearch, setFinalPatternSearch] = useState<any>("");
  const [statusId, setStatusId] = useState<any>();
  const [approvalStatusId, setApprovalStatusId] = useState<any>();
  const [quoteStatusId, setQuoteStatusId] = useState<any>();
  const [customerId, setCustomerId] = useState<any>();
  const [isCanceledState, setIsCanceledState] = useState(false);
  const [isPausedOrders, setIsPausedOrders] = useState(false);
  const [isOrdersWithoutJobs, setIsOrdersWithoutJobs] = useState(false);
  const [openPurchaseOrderTypeModal, setOpenPurchaseOrderTypeModal] =
    useState(false);
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [selectedQuoteForUpload, setSelectedQuoteForUpload] =
    useState<any>(null);
  const [quoteForUploadModal, setQuoteForUploadModal] = useState<any>(null);
  const [dateRange, setDateRange] = useState<any>();
  const [agentId, setAgentId] = useState<any>();
  const [
    customerSupportRepresentativeId,
    setCustomerSupportRepresentativeId,
  ] = useState<any>();
  const [allQuotes, setAllQuotes] = useState<any[] | undefined>(undefined);
  // Loading/Error states for document table
  const [isDocumentTableLoading, setIsDocumentTableLoading] =
    useState<boolean>(false);
  const [isDocumentTableError, setIsDocumentTableError] =
    useState<boolean>(false);
  const [isSocketRefreshingDocuments, setIsSocketRefreshingDocuments] =
    useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openLogsModal, setOpenLogsModal] = useState(false);
  const [logsModalTitle, setLogsModalTitle] = useState<string>();
  const [selectedQuote, setSelectedQuote] = useState<any>();
  const [allDocuments, setAllDocuments] = useState([]);
  const [allStatistics, setAllStatistics] = useState<{
    documentStatisticsList: any[];
    totalItems: number;
  }>({
    documentStatisticsList: [],
    totalItems: 0,
  });
  const [activeCard, setActiveCard] = useState(null);
  const [page, setPage] = useState(1);
  const { pageSize, setPageSize, pagesCount, setPagesCount, initialPageSize } =
    useListPagination();

  useEffect(() => {
    if (initialPageSize && initialPageSize !== pageSize) {
      setPageSize(initialPageSize);
    }
  }, [initialPageSize]);
  const [currencies, setCurrencies] =
    useRecoilState<{ label: string; value: string }[]>(currenciesState);
  const [currencyFilter, setCurrencyFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const getCurrenciesApi = async () => {
    const callBack = (res) => {
      if (res.success) {
        setCurrencies(
          res.data.map(({ value, text }) => ({ label: text, value })),
        );
      }
    };
    await getCurrencies(callApi, callBack);
  };

  useEffect(() => {
    getCurrenciesApi();
  }, []);

  // Auto-trigger search when approvalStatusId changes from a card click
  useEffect(() => {
    if (shouldAutoSearchRef.current) {
      shouldAutoSearchRef.current = false;
      onClickSearchFilter();
    }
  }, [approvalStatusId]);

  const [openAddRule, setOpenAddRule] = useState<boolean>(false);
  const [openFilterConfirmationModal, setOpenFilterConfirmationModal] =
    useState<boolean>(false);
  const [openScheduledReportsModal, setOpenScheduledReportsModal] =
    useState<boolean>(false);

  // Use the scheduled reports hook
  const {
    scheduledReports,
    totalCount: scheduledReportsTotalCount,
    pageNumber: scheduledReportsPageNumber,
    pageSize: scheduledReportsPageSize,
    pagesCount: scheduledReportsPagesCount,
    isLoading: scheduledReportsIsLoading,
    createScheduledReport,
    getAllScheduledReports,
    deleteScheduledReport,
    toggleScheduledReport,
    handlePageChange: handleScheduledReportsPageChange,
    handlePageSizeChange: handleScheduledReportsPageSizeChange,
  } = useScheduledReports();
  const [resetDatePicker, setResetDatePicker] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const [checkFromDate, setCheckFromDate] = useState<Date>();
  const [checkToDate, setCheckToDate] = useState<Date>();
  const [documentIdLogState, setDocumentIdLogState] = useState<string>();
  const [documentLogsData, setDocumentLogsData] = useState<any>();
  const [employeeId, setEmployeeId] = useState<any>();
  const [resetLogsDatePicker, setResetLogsDatePicker] =
    useState<boolean>(false);
  const [fromLogsDate, setFromLogsDate] = useState<Date>();
  const [toLogsDate, setToLogsDate] = useState<Date>();
  const [agentsCategories, setAgentsCategories] = useRecoilState(
    agentsCategoriesState,
  );
  const [
    customerSupportRepresentativesCategories,
    setCustomerSupportRepresentativesCategories,
  ] = useRecoilState(customerSupportRepresentativesCategoriesState);
  const [accountingStatus, setAccountingStatus] = useState<{
    label: string;
    value: BoardMissionStatusForFilter;
  } | null>();

  const [productionStatus, setProductionStatus] = useState<{
    label: string;
    value: BoardMissionStatusForFilter;
  } | null>();

  const [deliveryStatus, setDeliveryStatus] = useState<{
    label: string;
    value: any;
  } | null>();

  const purchaseOrderStatusOptions = useMemo(
    () =>
      Object.keys(PurchaseOrderStatuses)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value:
            PurchaseOrderStatuses[key as keyof typeof PurchaseOrderStatuses],
          label: t(`documentStatus.PurchaseOrder.${key}`),
        })),
    [t],
  );
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  const [productsList, setProductsList] = useState([]);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [printHouseMaterialIds, setPrintHouseMaterialIds] = useState<string[]>(
    [],
  );
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [openCloseOrderNotesModal, setOpenCloseOrderNotesModal] =
    useState(false);
  const [selectedQuoteItemValue, setSelectedQuoteItemValue] = useState<any>();
  const [documentCloseNumber, setDocumentCloseNumber] = useState("");
  const selectedClient = useRecoilValue<any>(selectedClientState);

  // Modern home (customer view): pagination of the embedded document list.
  const [homePageSize, setHomePageSize] = useState(10);
  const [homePagesCount, setHomePagesCount] = useState(0);
  const [homeTotalItems, setHomeTotalItems] = useState(0);

  // FrontDesk Email Integration
  const {
    isEmailComposerOpen,
    emailData,
    isLoadingEmailData,
    openEmailComposer,
    closeEmailComposer,
    isFromPaymentPage,
  } = useFrontDeskEmailIntegration({ documentType: docType });
  const hasFrontDeskFeature = useRecoilValue(printHouseHasFrontDeskFeature);
  const isSelectedCLoseAsDeliveryNote = useRecoilValue(
    isSelectedCLoseAsDeliveryNoteState,
  );
  const isSelectedCLoseAsProformaInvoice = useRecoilValue(
    isSelectedCLoseAsProformaInvoiceState,
  );
  const [paymentType, setPaymentType] = useState<PaymentTypeOption | null>(
    null,
  );

  const [currentSortOrder, setCurrentSortOrder] = useState<any>(null);

  const typeOfPayments = [
    { label: t("payment.cash"), value: PAYMENT_TYPE.Cash },
    { label: t("payment.check"), value: PAYMENT_TYPE.Checks },
    { label: t("payment.creditCard"), value: PAYMENT_TYPE.CreditCard },
    { label: t("payment.transfer"), value: PAYMENT_TYPE.Transfer },
  ];

  const handlePaymentTypeChange = (e: any, value: any) => {
    setPaymentType(value);
  };

  const dir: "rtl" | "ltr" = t("direction");
  const isRTL = dir === "rtl";

  const isQuote = docType === DOCUMENT_TYPE.quote;
  const isOrder = docType === DOCUMENT_TYPE.order;
  const isReceipt = docType === DOCUMENT_TYPE.receipt;
  const isSupplierPayments = docType === DOCUMENT_TYPE.supplierPayments;
  const isDeliveryNote = docType === DOCUMENT_TYPE.deliveryNote;
  const isDeliveryNoteRefund = docType === DOCUMENT_TYPE.deliveryNoteRefund;
  const isProformaInvoice = docType === DOCUMENT_TYPE.proformaInvoice;
  const isInvoice = docType === DOCUMENT_TYPE.invoice;
  const isInvoiceRefund = docType === DOCUMENT_TYPE.invoiceRefund;
  const isPurchaseOrder = docType === DOCUMENT_TYPE.purchaseOrder;
  const isPurchaseInvoice = docType === DOCUMENT_TYPE.purchaseInvoice;
  const isPurchaseInvoiceRefund =
    docType === DOCUMENT_TYPE.purchaseInvoiceRefund;

  const isPurchaseDocument =
    isPurchaseOrder ||
    isPurchaseInvoice ||
    isPurchaseInvoiceRefund ||
    isSupplierPayments;

  const { renderOptions, checkWhatRenderArray, isFetching } =
    useCustomerDropDownList({
      clientType: isPurchaseDocument
        ? CLIENT_TYPE.SUPPLIER
        : CLIENT_TYPE.CUSTOMER,
      shouldRenderOnMount: !isFromHomePage,
    });

  const handleDocumentNumberChange = (event) => {
    setDocumentCloseNumber(event.target.value);
  };

  const [closeModalSource, setCloseModalSource] = useState<
    "closeAsInvoice" | "closeManually" | "closeAsDeliveryNote"
  >(null);
  const [onClickCloseOrderCallback, setOnClickCloseOrderCallback] = useState<
    (() => void) | null
  >(null);

  const onClickOpenCloseOrderNotesModal = (
    order,
    source: "closeAsInvoice" | "closeManually" | "closeAsDeliveryNote",
    onClickCloseOrder: () => void,
  ) => {
    setSelectedQuoteItemValue(order);
    setSelectedDocument(order);
    setCloseModalSource(source);
    setOnClickCloseOrderCallback(() => onClickCloseOrder);
    setOpenCloseOrderNotesModal(true);
  };
  const onClickCloseCloseOrderNotesModal = () => {
    setOpenCloseOrderNotesModal(false);
  };

  const onCloseAddRuleModal = () => {
    setOpenAddRule(false);
  };
  const onOpenAddRuleModal = () => {
    // Check if there are active filters by checking all filter state variables
    const hasActiveFilters =
      patternSearch ||
      finalPatternSearch ||
      statusId ||
      approvalStatusId ||
      quoteStatusId ||
      customerId ||
      agentId ||
      customerSupportRepresentativeId ||
      dateRange ||
      productionStatus ||
      accountingStatus ||
      deliveryStatus ||
      productIds?.length > 0 ||
      printHouseMaterialIds?.length > 0 ||
      currencyFilter ||
      isCanceledState ||
      isPausedOrders ||
      isOrdersWithoutJobs ||
      minPrice ||
      maxPrice ||
      fromDate ||
      toDate ||
      paymentType;

    if (hasActiveFilters) {
      setOpenFilterConfirmationModal(true);
    } else {
      setOpenAddRule(true);
    }
  };

  const onCloseFilterConfirmationModal = () => {
    setOpenFilterConfirmationModal(false);
  };

  const onContinueWithFilters = () => {
    setOpenAddRule(true);
  };

  const onResetFiltersAndGenerate = () => {
    // Reset all filters
    setPatternSearch("");
    setStatusId(null);
    setApprovalStatusId(null);
    setCustomerId(null);
    setAgentId(null);
    setCustomerSupportRepresentativeId(null);
    setProductionStatus(null);
    setAccountingStatus(null);
    setDeliveryStatus(null);
    setProductIds([]);
    setPrintHouseMaterialIds([]);
    setCurrencyFilter(null);
    setIsCanceledState(false);
    setIsPausedOrders(false);
    setIsOrdersWithoutJobs(false);
    setMinPrice("");
    setMaxPrice("");
    setFromDate(null);
    setToDate(null);
    setCheckFromDate(null);
    setCheckToDate(null);
    setPaymentType(null);

    // Clear URL query parameters
    router.push({
      pathname: router.pathname,
      query: {},
    });

    setOpenAddRule(true);
  };

  const onShowScheduledReports = () => {
    setOpenScheduledReportsModal(true);
    getAllScheduledReports(1, 10, docType); // Start with page 1 and page size 10, filter by document type
  };

  const onCloseScheduledReportsModal = () => {
    setOpenScheduledReportsModal(false);
  };

  const onToggleScheduledReportActive = (id: string, isActive: boolean) => {
    toggleScheduledReport(id, isActive);
  };

  const onDeleteScheduledReport = (id: string) => {
    deleteScheduledReport(id);
  };

  const handlePageSizeChange = (event) => {
    setPage(1);
    setPageSize(event.target.value);
  };

  const onClickCloseModal = () => {
    setOpenModal(false);
  };

  const onClickOpenModal = (quote: any) => {
    setSelectedQuote(quote);
    setOpenModal(true);
  };

  const userQuoteIfExist = useRecoilValue<any>(userQuoteState);
  const onClickEditQuote = (quote: any) => {
    if (userQuoteIfExist) onClickOpenModal(quote);
    else updateQuoteStatus(quote);
  };

  const onClickCloseLogsModal = () => {
    setOpenLogsModal(false);
    setEmployeeId(null);
    setFromLogsDate(null);
    setToLogsDate(null);
  };

  const onClickQuotePdf = async (
    id: string,
    withPrices?: boolean,
    withInvoices?: boolean,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        const pdfLink = res.data;
        let filename: string;

        if (isReceipt || isSupplierPayments) {
          if (withInvoices) {
            filename = generateFilename(
              "Receipt_With_Invoices",
              id,
              undefined,
              "pdf",
            );
          } else {
            filename = generateFilename("Receipt", id, undefined, "pdf");
          }
        } else {
          const docTypeName = DOCUMENT_TYPE[docType] || "Document";
          filename = generateFilename(docTypeName, id, undefined, "pdf");
        }

        // Open in new tab for viewing (user can still download from browser)
        downloadPdf(pdfLink, filename, true);
      } else {
        alertFaultGetData();
      }
    };
    if (isReceipt || isSupplierPayments) {
      if (withInvoices)
        await getReceiptInvoicesPdfApi(callApi, callBack, {
          receiptId: id,
        });
      else
        await getReceiptPdfApi(callApi, callBack, {
          receiptId: id,
        });
    } else {
      await getDocumentPdfApi(callApi, callBack, {
        documentId: id,
        documentType: docType,
        // withPrices: withPrices ?? false,
        ...(withPrices != null && { withPrices }),
      });
    }
  };

  const [openExportReceiptPdfModal, setOpenExportReceiptPdfModal] =
    useState<boolean>(false);

  const onOpenExportReceiptPdfModal = (receiptItem: any) => {
    setSelectedDocument(receiptItem);
    setOpenExportReceiptPdfModal(true);
  };

  const onCloseExportReceiptPdfModal = () => {
    setSelectedDocument({});
    setOpenExportReceiptPdfModal(false);
  };

  const onClickOpenUploadFilesModal = async (quote: any) => {
    setSelectedQuoteForUpload(quote);
    setOpenUploadFileModal(true);

    // Fetch the full quote data with documentItems for the modal
    const callBack = (res) => {
      if (res?.success) {
        const quoteData = res?.data || {};
        // Ensure documentItems have isSelected or isChecked based on quote type
        if (quoteData.documentItems) {
          quoteData.documentItems = quoteData.documentItems.map((item: any) => {
            // For quotes, use isSelected
            item.isSelected = item.isSelected ?? false;
            return item;
          });
        }
        setQuoteForUploadModal(quoteData);
      } else {
        alertFaultGetData();
        setOpenUploadFileModal(false);
      }
    };

    await getDocumentApi(callApi, callBack, {
      Id: quote?.id,
      documentType: docType,
    });
  };

  const onCloseUploadFilesModal = () => {
    setOpenUploadFileModal(false);
    setSelectedQuoteForUpload(null);
    setQuoteForUploadModal(null);
  };

  const getQuoteForUploadModal = async () => {
    if (!selectedQuoteForUpload?.id) return;

    const callBack = (res) => {
      if (res?.success) {
        const quoteData = res?.data || {};
        if (quoteData.documentItems) {
          quoteData.documentItems = quoteData.documentItems.map((item: any) => {
            // For quotes, use isSelected
            item.isSelected = item.isSelected ?? false;
            return item;
          });
        }
        setQuoteForUploadModal(quoteData);
      }
    };

    await getDocumentApi(callApi, callBack, {
      Id: selectedQuoteForUpload?.id,
      documentType: docType,
    });
  };

  const getEmployeeCategories = async (isAgent: boolean, setState: any) => {
    const callBack = (res) => {
      if (res.success) {
        const agentNames = res.data.map((agent) => ({
          label: agent.text,
          id: agent.value,
        }));
        setState(agentNames);
      }
    };
    await getAndSetEmployees2(callApi, callBack, { isAgent: isAgent });
  };

  const getCustomerSupportRepresentativeCategories = async () => {
    const callBack = (res) => {
      if (res.success) {
        const names = res.data.map((employee) => ({
          label: employee.text,
          id: employee.value,
        }));
        setCustomerSupportRepresentativesCategories(names);
      }
    };
    await getAndSetEmployees2(callApi, callBack, {
      isCustomerSupportRepresentative: true,
    });
  };

  useEffect(() => {
    if (!agentsCategories?.length) {
      getEmployeeCategories(true, setAgentsCategories);
    }
    if (!customerSupportRepresentativesCategories?.length) {
      getCustomerSupportRepresentativeCategories();
    }
  }, []);

  const onClickDocumentLogs = async (document: any) => {
    try {
      await getAllDocumentLogs(document?.id);
      setLogsModalTitle(
        `${t("sales.quote.logsFor")} ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setOpenLogsModal(true);
    } catch (error) {
      console.error("Error fetching document logs:", error);
    }
  };

  const _renderPaymentType = (paymentType) => {
    if (!paymentType) return "";
    const types = paymentType.split(",").map((type) => type.trim());
    const translatedTypes = types.map((type) => {
      switch (type) {
        case "מזומן":
          return t("payment.cash");
        case "המחאה":
          return t("payment.check");
        case "bit":
          return t("payment.bit");
        case "אשראי":
          return t("payment.creditCard");
        case "העברה":
          return t("payment.transfer");
        default:
          return type;
      }
    });
    return translatedTypes.join(", ");
  };

  const isOrdersTracking = pathname === "/ordersTracking";

  const ExportDocumentTableToExcel = async (
    exportType?: INVOICES_EXPORT_TYPES,
  ) => {
    const effectivePatternSearch = (() => {
      const trimmed =
        patternSearch != null && String(patternSearch).trim() !== ""
          ? String(patternSearch).trim()
          : "";
      if (trimmed) return trimmed;
      if (
        finalPatternSearch !== null &&
        finalPatternSearch !== undefined &&
        String(finalPatternSearch).trim() !== ""
      ) {
        return String(finalPatternSearch).trim();
      }
      const docNum = router.query.documentNumber;
      const docStr = Array.isArray(docNum) ? docNum[0] : docNum;
      return docStr ? String(docStr) : "";
    })();

    const totalForExport = await new Promise<number>((resolve) => {
      const callBack = (res: any) => {
        if (res?.success) {
          resolve(Number(res?.data?.totalItems ?? 0));
        } else {
          resolve(0);
        }
      };
      const sortOrder = currentSortOrder;
      if (isReceipt || isSupplierPayments) {
        void getAllReceiptsApi(callApi, callBack, {
          ...(!isReceipt && { isOutgoingPayments: true }),
          clientId: customerId?.id,
          agentId: agentId?.id,
          customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
          status: accountingStatus?.value,
          paymentType: paymentType?.value,
          patternSearch: effectivePatternSearch,
          ...(router.query.documentNumber
            ? {
              documentNumber: Array.isArray(router.query.documentNumber)
                ? router.query.documentNumber[0]
                : router.query.documentNumber,
            }
            : {}),
          ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
          ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
          ...(checkFromDate
            ? { checkFromDate: GetDateFormat(checkFromDate) }
            : {}),
          ...(checkToDate ? { checkToDate: GetDateFormat(checkToDate) } : {}),
          ...(minPrice !== "" && minPrice != null ? { minPrice } : {}),
          ...(maxPrice !== "" && maxPrice != null ? { maxPrice } : {}),
          model: { pageNumber: 1, pageSize: 1, order: sortOrder },
        });
      } else {
        void getAllDocumentsApi(callApi, callBack, {
          documentType: docType,
          data: {
            model: { pageNumber: 1, pageSize: 1, order: sortOrder },
            dateRange,
            customerId: customerId?.id,
            agentId: agentId?.id,
            customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
            statusId: quoteStatusId?.value || statusId?.value,
            approvalStatus: approvalStatusId?.value,
            currency: currencyFilter?.value,
            ...(minPrice !== "" && minPrice != null ? { minPrice } : {}),
            ...(maxPrice !== "" && maxPrice != null ? { maxPrice } : {}),
            patternSearch: effectivePatternSearch,
            ...(router.query.documentNumber
              ? { documentNumber: router.query.documentNumber }
              : {}),
            ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
            ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
            ...(productIds?.length ? { productList: productIds } : {}),
            ...(printHouseMaterialIds?.length
              ? { printHouseMaterialIds }
              : {}),
            closeStatus: accountingStatus?.value,
            deliveryStatus: deliveryStatus?.value,
            productionStatus: productionStatus?.value,
            isCanceled: isCanceledState,
            isPaused: isPausedOrders,
            isOrdersWithoutJobs: isOrdersWithoutJobs,
            isOrderInTracking: isOrdersTracking,
          },
        });
      }
    });

    if (
      !assertTableExportRecordCountWithinLimit(totalForExport, alertFault, t)
    ) {
      return;
    }

    const sortOrder = currentSortOrder;
    const callBack = (res) => {
      if (res?.success) {
        const downloadLink = document.createElement("a");
        if (typeof res.data === "string") {
          // For S3 URLs, we need to fetch the file and create a blob to force download
          fetch(res.data)
            .then((response) => {
              // Get appropriate filename based on export type
              const getFileName = () => {
                const docTypeName = DOCUMENT_TYPE[docType] || "Document";
                const dateRange =
                  fromDate && toDate
                    ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
                    : undefined;

                if (exportType === INVOICES_EXPORT_TYPES.ExtendedTxt) {
                  return generateFilename(
                    `${docTypeName}_Export`,
                    undefined,
                    dateRange,
                    "txt",
                  );
                } else if (exportType === INVOICES_EXPORT_TYPES.ExtendedExcel) {
                  return generateFilename(
                    `${docTypeName}_Extended`,
                    undefined,
                    dateRange,
                    "xlsx",
                  );
                } else if (
                  exportType === INVOICES_EXPORT_TYPES.SummeryExcelWithVat
                ) {
                  return generateFilename(
                    `${docTypeName}_Summary_With_VAT`,
                    undefined,
                    dateRange,
                    "xlsx",
                  );
                } else if (
                  exportType === INVOICES_EXPORT_TYPES.SummeryExcelWithoutVat
                ) {
                  return generateFilename(
                    `${docTypeName}_Summary_Without_VAT`,
                    undefined,
                    dateRange,
                    "xlsx",
                  );
                } else {
                  return generateFilename(
                    `${docTypeName}_Export`,
                    undefined,
                    dateRange,
                    "xlsx",
                  );
                }
              };

              const fileName = getFileName();

              // For text files, handle encoding properly to preserve Hebrew characters
              if (exportType === INVOICES_EXPORT_TYPES.ExtendedTxt) {
                return response.arrayBuffer().then((arrayBuffer) => {
                  // Try to decode as UTF-8 first, then fallback to other encodings
                  let text;
                  try {
                    // Try UTF-8 first
                    const decoder = new TextDecoder("utf-8");
                    text = decoder.decode(arrayBuffer);

                    // If we see replacement characters, try other encodings
                    if (text.includes("�")) {
                      // Try Windows-1255 (Hebrew encoding)
                      const hebrewDecoder = new TextDecoder("windows-1255");
                      const hebrewText = hebrewDecoder.decode(arrayBuffer);
                      if (!hebrewText.includes("�")) {
                        text = hebrewText;
                      } else {
                        // Try ISO-8859-8 (Hebrew encoding)
                        const iso8859Decoder = new TextDecoder("iso-8859-8");
                        const isoText = iso8859Decoder.decode(arrayBuffer);
                        if (!isoText.includes("�")) {
                          text = isoText;
                        }
                      }
                    }
                  } catch (error) {
                    console.error("Error decoding text:", error);
                    // Fallback to default UTF-8
                    const decoder = new TextDecoder("utf-8");
                    text = decoder.decode(arrayBuffer);
                  }

                  // Create blob with UTF-8 BOM for better Hebrew support
                  const BOM = "\uFEFF";
                  const textWithBOM = BOM + text;
                  const blob = new Blob([textWithBOM], {
                    type: "text/plain;charset=utf-8",
                  });
                  return { blob, fileName };
                });
              } else {
                // For Excel files, use blob directly
                return response.blob().then((blob) => ({ blob, fileName }));
              }
            })
            .then(({ blob, fileName }) => {
              const url = URL.createObjectURL(blob);
              downloadLink.href = url;
              downloadLink.download = fileName;
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              // Clean up the object URL
              URL.revokeObjectURL(url);
            })
            .catch((error) => {
              console.error("Error downloading file:", error);
              // Fallback to direct link if fetch fails
              const docTypeName = DOCUMENT_TYPE[docType] || "Document";
              const dateRange =
                fromDate && toDate
                  ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
                  : undefined;
              const fallbackFileName = generateFilename(
                `${docTypeName}_Export`,
                undefined,
                dateRange,
                "xlsx",
              );
              downloadLink.href = res.data;
              downloadLink.download = fallbackFileName;
              downloadLink.click();
            });
        } else {
          const link = URL.createObjectURL(res.data);
          const docTypeName = DOCUMENT_TYPE[docType] || "Document";
          const dateRange =
            fromDate && toDate
              ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
              : undefined;
          const fileName = generateFilename(
            `${docTypeName}_Export`,
            undefined,
            dateRange,
            "xlsx",
          );
          downloadLink.href = link;
          downloadLink.download = fileName;
          downloadLink.click();
        }
      } else {
      }
    };
    await ExportDocumentTableToExcelApi(callApi, callBack, {
      documentType: docType,
      exportType: exportType,
      data: {
        model: {
          pageNumber: page,
          pageSize: pageSize,
          order: sortOrder,
        },
        statusId: quoteStatusId?.value || statusId?.value,
        approvalStatus: approvalStatusId?.value,
        closeStatus: accountingStatus?.value,
        productionStatus: productionStatus?.value,
        // deliveryStatus: deliveryStatus?.value,
        patternSearch: effectivePatternSearch,
        customerId: customerId?.id,
        dateRange,
        agentId: agentId?.id,
        customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
        minPrice: minPrice,
        maxPrice: maxPrice,
        productList: productIds,
        printHouseMaterialIds,
        isCanceled: isCanceledState,
        isPaused: isPausedOrders,
        isOrdersWithoutJobs: isOrdersWithoutJobs,
        isOrderInTracking: isOrdersTracking,
        fromDate: fromDate && GetDateFormat(fromDate),
        toDate: toDate && GetDateFormat(toDate),
        paymentType: paymentType?.value,
      },
    });
  };

  const ExportOrderItemsTableToExcel = async () => {
    const effectivePatternSearch = (() => {
      const trimmed =
        patternSearch != null && String(patternSearch).trim() !== ""
          ? String(patternSearch).trim()
          : "";
      if (trimmed) return trimmed;
      if (
        finalPatternSearch !== null &&
        finalPatternSearch !== undefined &&
        String(finalPatternSearch).trim() !== ""
      ) {
        return String(finalPatternSearch).trim();
      }
      const docNum = router.query.documentNumber;
      const docStr = Array.isArray(docNum) ? docNum[0] : docNum;
      return docStr ? String(docStr) : "";
    })();

    const totalForExport = await new Promise<number>((resolve) => {
      const callBack = (res: any) => {
        if (res?.success) {
          resolve(Number(res?.data?.totalItems ?? 0));
        } else {
          resolve(0);
        }
      };
      const sortOrder = currentSortOrder;
      void getAllDocumentsApi(callApi, callBack, {
        documentType: docType,
        data: {
          model: { pageNumber: 1, pageSize: 1, order: sortOrder },
          dateRange,
          customerId: customerId?.id,
          agentId: agentId?.id,
          customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
          statusId: quoteStatusId?.value || statusId?.value,
          approvalStatus: approvalStatusId?.value,
          currency: currencyFilter?.value,
          ...(minPrice !== "" && minPrice != null ? { minPrice } : {}),
          ...(maxPrice !== "" && maxPrice != null ? { maxPrice } : {}),
          patternSearch: effectivePatternSearch,
          ...(router.query.documentNumber
            ? { documentNumber: router.query.documentNumber }
            : {}),
          ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
          ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
          ...(productIds?.length ? { productList: productIds } : {}),
          ...(printHouseMaterialIds?.length
            ? { printHouseMaterialIds }
            : {}),
          closeStatus: accountingStatus?.value,
          deliveryStatus: deliveryStatus?.value,
          productionStatus: productionStatus?.value,
          isCanceled: isCanceledState,
          isPaused: isPausedOrders,
          isOrdersWithoutJobs: isOrdersWithoutJobs,
          isOrderInTracking: isOrdersTracking,
        },
      });
    });

    if (
      !assertTableExportRecordCountWithinLimit(totalForExport, alertFault, t)
    ) {
      return;
    }

    const sortOrder = currentSortOrder;
    const callBack = (res) => {
      if (res?.success) {
        const downloadLink = document.createElement("a");
        if (typeof res.data === "string") {
          fetch(res.data)
            .then((response) => {
              const docTypeName = DOCUMENT_TYPE[docType] || "Document";
              const dateRangeLabel =
                fromDate && toDate
                  ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
                  : undefined;
              const fileName = generateFilename(
                `${docTypeName}_Items_Export`,
                undefined,
                dateRangeLabel,
                "xlsx",
              );
              return response.blob().then((blob) => ({ blob, fileName }));
            })
            .then(({ blob, fileName }) => {
              const url = URL.createObjectURL(blob);
              downloadLink.href = url;
              downloadLink.download = fileName;
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(url);
            })
            .catch((error) => {
              console.error("Error downloading file:", error);
              const docTypeName = DOCUMENT_TYPE[docType] || "Document";
              const dateRangeLabel =
                fromDate && toDate
                  ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
                  : undefined;
              const fallbackFileName = generateFilename(
                `${docTypeName}_Items_Export`,
                undefined,
                dateRangeLabel,
                "xlsx",
              );
              downloadLink.href = res.data;
              downloadLink.download = fallbackFileName;
              downloadLink.click();
            });
        } else {
          const link = URL.createObjectURL(res.data);
          const docTypeName = DOCUMENT_TYPE[docType] || "Document";
          const dateRangeLabel =
            fromDate && toDate
              ? `${format(fromDate, "yyyy-MM-dd")}_to_${format(toDate, "yyyy-MM-dd")}`
              : undefined;
          const fileName = generateFilename(
            `${docTypeName}_Items_Export`,
            undefined,
            dateRangeLabel,
            "xlsx",
          );
          downloadLink.href = link;
          downloadLink.download = fileName;
          downloadLink.click();
        }
      }
    };
    await ExportOrderItemsTableToExcelApi(callApi, callBack, {
      documentType: docType,
      data: {
        model: {
          pageNumber: page,
          pageSize: pageSize,
          order: sortOrder,
        },
        statusId: quoteStatusId?.value || statusId?.value,
        approvalStatus: approvalStatusId?.value,
        closeStatus: accountingStatus?.value,
        productionStatus: productionStatus?.value,
        patternSearch: effectivePatternSearch,
        customerId: customerId?.id,
        dateRange,
        agentId: agentId?.id,
        customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
        minPrice: minPrice,
        maxPrice: maxPrice,
        productList: productIds,
        printHouseMaterialIds,
        isCanceled: isCanceledState,
        isPaused: isPausedOrders,
        isOrdersWithoutJobs: isOrdersWithoutJobs,
        isOrderInTracking: isOrdersTracking,
        fromDate: fromDate && GetDateFormat(fromDate),
        toDate: toDate && GetDateFormat(toDate),
        paymentType: paymentType?.value,
      },
    });
  };

  const ExportDocumentTableToPdf = async () => {
    const effectivePatternSearch = (() => {
      const trimmed =
        patternSearch != null && String(patternSearch).trim() !== ""
          ? String(patternSearch).trim()
          : "";
      if (trimmed) return trimmed;
      if (
        finalPatternSearch !== null &&
        finalPatternSearch !== undefined &&
        String(finalPatternSearch).trim() !== ""
      ) {
        return String(finalPatternSearch).trim();
      }
      const docNum = router.query.documentNumber;
      const docStr = Array.isArray(docNum) ? docNum[0] : docNum;
      return docStr ? String(docStr) : "";
    })();

    const totalForExport = await new Promise<number>((resolve) => {
      const callBack = (res: any) => {
        if (res?.success) {
          resolve(Number(res?.data?.totalItems ?? 0));
        } else {
          resolve(0);
        }
      };
      const sortOrder = currentSortOrder;
      if (isReceipt || isSupplierPayments) {
        void getAllReceiptsApi(callApi, callBack, {
          ...(!isReceipt && { isOutgoingPayments: true }),
          clientId: customerId?.id,
          agentId: agentId?.id,
          customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
          status: accountingStatus?.value,
          paymentType: paymentType?.value,
          patternSearch: effectivePatternSearch,
          ...(router.query.documentNumber
            ? {
              documentNumber: Array.isArray(router.query.documentNumber)
                ? router.query.documentNumber[0]
                : router.query.documentNumber,
            }
            : {}),
          ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
          ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
          ...(checkFromDate
            ? { checkFromDate: GetDateFormat(checkFromDate) }
            : {}),
          ...(checkToDate ? { checkToDate: GetDateFormat(checkToDate) } : {}),
          ...(minPrice !== "" && minPrice != null ? { minPrice } : {}),
          ...(maxPrice !== "" && maxPrice != null ? { maxPrice } : {}),
          model: { pageNumber: 1, pageSize: 1, order: sortOrder },
        });
      } else {
        void getAllDocumentsApi(callApi, callBack, {
          documentType: docType,
          data: {
            model: { pageNumber: 1, pageSize: 1, order: sortOrder },
            dateRange,
            customerId: customerId?.id,
            agentId: agentId?.id,
            customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
            statusId: quoteStatusId?.value || statusId?.value,
            approvalStatus: approvalStatusId?.value,
            currency: currencyFilter?.value,
            ...(minPrice !== "" && minPrice != null ? { minPrice } : {}),
            ...(maxPrice !== "" && maxPrice != null ? { maxPrice } : {}),
            patternSearch: effectivePatternSearch,
            ...(router.query.documentNumber
              ? { documentNumber: router.query.documentNumber }
              : {}),
            ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
            ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
            ...(productIds?.length ? { productList: productIds } : {}),
            ...(printHouseMaterialIds?.length
              ? { printHouseMaterialIds }
              : {}),
            closeStatus: accountingStatus?.value,
            deliveryStatus: deliveryStatus?.value,
            productionStatus: productionStatus?.value,
            isCanceled: isCanceledState,
            isPaused: isPausedOrders,
            isOrdersWithoutJobs: isOrdersWithoutJobs,
            isOrderInTracking: isOrdersTracking,
          },
        });
      }
    });

    if (
      !assertTableExportRecordCountWithinLimit(totalForExport, alertFault, t)
    ) {
      return;
    }

    const sortOrder = currentSortOrder;
    const callBack = (res) => {
      if (res?.success) {
        let fileUrl =
          typeof res.data === "string"
            ? res.data
            : URL.createObjectURL(res.data);

        if (fileUrl) {
          window.open(fileUrl, "_blank");
        } else alertFaultGetData();
      } else {
        alertFaultGetData();
      }
    };
    await ExportDocumentTableToPdfApi(callApi, callBack, {
      documentType: docType,
      data: {
        model: {
          pageNumber: page,
          pageSize: pageSize,
          order: sortOrder,
        },
        statusId: quoteStatusId?.value || statusId?.value,
        approvalStatus: approvalStatusId?.value,
        closeStatus: accountingStatus?.value,
        productionStatus: productionStatus?.value,
        // deliveryStatus: deliveryStatus?.value,
        patternSearch: effectivePatternSearch,
        customerId: customerId?.id,
        dateRange,
        agentId: agentId?.id,
        customerSupportRepresentativeId: customerSupportRepresentativeId?.id,
        minPrice: minPrice,
        maxPrice: maxPrice,
        productList: productIds,
        printHouseMaterialIds,
        paymentType: paymentType,
        isCanceled: isCanceledState,
        isPaused: isPausedOrders,
        isOrdersWithoutJobs: isOrdersWithoutJobs,
        isOrderInTracking: isOrdersTracking,
        fromDate: fromDate && GetDateFormat(fromDate),
        toDate: toDate && GetDateFormat(toDate),
      },
    });
  };

  // At the table/page component level (outside getAllQuotes, once per table)
  const [cellsExpanded, setCellsExpanded] = useState<Record<string, boolean>>(
    {},
  );
  const toggleCellsExpanded = (rowId: string) =>
    setCellsExpanded((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));

  // Store original data to re-map when cellsExpanded changes
  const [originalData, setOriginalData] = useState<any[] | null>(null);

  // Re-map existing data when cellsExpanded changes to update JSX with new state
  useEffect(() => {
    if (
      originalData &&
      originalData.length > 0 &&
      !isReceipt &&
      !isSupplierPayments
    ) {
      // Re-map the original data with the new cellsExpanded value
      const remappedQuotes = originalData.map((quote: any) => {
        const baseRow = {
          creationDate: formatCustomDateTime(
            quote?.createdDate || quote?.creationDate,
          ),
          client: (
            <div
              style={{ cursor: "pointer" }}
              onClick={() =>
                onClickOpenCustomerModal(quote?.clientId || quote?.customerId)
              }
            >
              {quote?.clientName || quote?.customerName}
            </div>
          ),
          agent: quote?.agentName,
          customerSupportRepresentative: quote?.customerSupportRepresentativeName,
          number: quote?.number,
          purchaseNumber: (
            <PurchaseNumberCell
              key={`purchase-${quote?.id}`}
              purchaseNumbers={quote?.purchaseNumber}
              expanded={cellsExpanded[quote?.id] || false}
              onToggle={() => toggleCellsExpanded(quote?.id)}
              isRTL={isRTL}
            />
          ),
          worksName: (
            <WorksNameCell
              key={`works-${quote?.id}`}
              worksNames={quote?.worksNames || []}
              expanded={cellsExpanded[quote?.id] || false}
              onToggle={() => toggleCellsExpanded(quote?.id)}
              isRTL={isRTL}
            />
          ),
          title: (
            <TitleCell
              key={`title-${quote?.id}`}
              title={quote?.title ?? quote?.Title ?? ""}
              expanded={cellsExpanded[quote?.id] || false}
              onToggle={() => toggleCellsExpanded(quote?.id)}
            />
          ),
          termOfPayment: formatTermOfPaymentCell(quote?.termOfPayment, t),
          discount: quote?.discount,
          totalPriceWithoutVat: isRTL
            ? `${quote?.totalPriceWithoutVat} ${getCurrencyUnitText(
              quote?.currency,
            )}`
            : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPriceWithoutVat
            }`,
          totalPrice: isRTL
            ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
            : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice}`,
          internalNotes: quote?.internalNotes,
          notes: quote?.notes,
          status: renderStatusCell(quote),
          more: (
            <MoreMenuWidget
              quote={quote}
              documentType={docType}
              onClickEditQuote={onClickEditQuote}
              onClickPdf={onClickQuotePdf}
              onClickDuplicate={onClickDocumentDuplicate}
              onClickLoggers={onClickDocumentLogs}
              onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
              onClickOpenChangeProductionStatusModal={
                onClickOpenChangeProductionStatusModal
              }
              onClickSendPayment={onClickSendPayment}
              onClickOpenCloseOrderModal={onClickOpenCloseOrderModal}
              onClickOpenIrrelevantModal={onClickOpenIrrelevantModal}
              onClickReopenDocument={onClickOpenReopenDocumentModal}
              onClickOpenRemoveDraftModal={onClickOpenRemoveDraftModal}
              onClickOpenDeliveryTimeModal={onClickOpenDeliveryTimeModal}
              onClickOpenDuplicateModal={onClickOpenDuplicateModal}
              onClickOpenPriceModal={onClickOpenPriceModal}
              onClickOpenModal={onClickOpenOtherReasonModal}
              onClickMakePayment={onClickMakePayment}
              onClickPauseOrResume={onClickPauseOrResume}
              onClickToggleOrderLock={onClickToggleOrderLock}
              onClickStartProduction={onClickOpenStartProductionModal}
              onClickCloseAndTrack={onClickOpenTrackModal}
              onClickOpenMoveOrderToReadyModal={
                onClickOpenMoveOrderToReadyModal
              }
              onOpenPackagesModal={onOpenPackagesModal}
              onClickOpenCloseOrderNotesModal={onClickOpenCloseOrderNotesModal}
              onOpenDuplicateModal={onOpenDuplicateModal}
              handleGetTaxConfirmNo={handleGetTaxConfirmNo}
              onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
              onClickOpenInventoryIntakeModal={onClickOpenInventoryIntakeModal}
              onClickUndoDocumentIntake={handleUndoDocumentIntake}
              onClickSendToCustomerEmail={onClickSendToCustomerEmail}
              onClickSendToCustomerSMS={onClickSendToCustomerSMS}
              onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
              onClickSendPaymentEmail={onClickSendPaymentEmail}
              onClickSendPaymentSMS={onClickSendPaymentSMS}
              onClickSendPaymentWhatsApp={onClickSendPaymentWhatsApp}
            />
          ),
        };

        if (isPurchaseOrder) {
          return {
            ...baseRow,
            purchaseOrderNumber: quote?.number,
            orderNumber: (
              <OrderNumbersCell
                key={`po-order-nums-${quote?.id}`}
                orderNumbers={quote?.orderNumbers}
                legacyOrderNumber={quote?.orderNumber}
              />
            ),
            supplierName: quote?.supplierName,
            itemsNumber: quote?.itemsNumber,
          };
        }

        if (isPurchaseInvoice || isPurchaseInvoiceRefund) {
          return {
            ...baseRow,
            purchaseInvoiceNumber: quote?.number,
            invoiceNumber: quote?.invoiceNumber,
            purchaseOrderNumber: quote?.orderNumbers
              ? quote?.orderNumbers.join(", ")
              : "",
            supplierName: (
              <div
                style={{ cursor: "pointer" }}
                onClick={() => onClickOpenCustomerModal(quote?.customerId)}
              >
                {quote?.customerName}
              </div>
            ),
            itemsNumber: quote?.itemsNumber,
            status:
              (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
                <span style={{ color: "#e77676" }}>
                  {t("sales.quote.draft")}
                </span>
              ) : (
                _renderStatus(docType, quote, t)
              ),
          };
        }

        if (isQuote) {
          return {
            ...baseRow,
            agent: quote?.agentName,
            customerSupportRepresentative: quote?.customerSupportRepresentativeName,
            number: quote?.number,
            worksName: (
              <WorksNameCell
                key={`works-${quote?.id}`}
                worksNames={quote?.worksNames || []}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
                isRTL={isRTL}
              />
            ),
          };
        }

        if (isOrder) {
          const jobsElement = (
            <OrderJobsCell
              jobs={quote?.jobs}
              orderJobs={quote?.orderJobs || []}
              quoteNumber={quote?.number}
              hasPermission={CheckPermission(Permissions.SHOW_BOARD_MISSIONS)}
            />
          );

          const paymentStatus = `${isRTL
            ? `${quote?.sumApplied} ${getCurrencyUnitText(quote?.currency)}`
            : `${getCurrencyUnitText(quote?.currency)} ${quote?.sumApplied}`
            }/${isRTL
              ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
              : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice}`
            }`;

          return {
            ...baseRow,
            estimatedProductionDate: renderOrderEstimatedProductionDateCell(quote),
            deliveryDate: renderOrderDeliveryDateCell(quote),
            readyDate: quote?.readyDate
              ? formatISODateTime(quote?.readyDate)
              : "",
            orderNumber: quote?.number,
            quoteNumber: quote?.sourceDocumentNumber?.map((item, index) => (
              <span key={index}>
                {item?.documentNumber}
                <br />
              </span>
            )),
            purchaseNumber: (
              <PurchaseNumberCell
                key={`purchase-${quote?.id}`}
                purchaseNumbers={quote?.purchaseNumber}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
                isRTL={isRTL}
              />
            ),
            productionStatus: renderOrderProductionStatusCell(quote),
            jobs: jobsElement,
            cost: quote?.cost,
            paymentStatus: isOrdersTracking
              ? paymentStatus
              : baseRow.totalPrice,
            deliveryStatus: (
              <ClickableStatusButton
                id={quote?.id}
                document={quote}
                documentType={docType}
                onClickOpenMoveOrderToReadyModal={
                  onClickOpenMoveOrderToReadyModal
                }
                getAllQuotes={getAllQuotes}
              />
            ),
          };
        }

        return {
          ...baseRow,
          sourceDocument: (
            <SourceDocumentCell
              key={`source-${quote?.id}`}
              documents={quote?.sourceDocumentNumber || []}
              expanded={cellsExpanded[quote?.id] || false}
              onToggle={() => toggleCellsExpanded(quote?.id)}
            />
          ),
          deliveryStatus:
            isInvoice || isDeliveryNote ? (
              <ClickableStatusButton
                id={quote?.id}
                document={quote}
                documentType={docType}
                onClickOpenMoveOrderToReadyModal={
                  onClickOpenMoveOrderToReadyModal
                }
                getAllQuotes={getAllQuotes}
              />
            ) : (
              <div>—</div>
            ),
          status:
            (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
              <span style={{ color: "#e77676" }}>{t("sales.quote.draft")}</span>
            ) : (
              renderStatusCell(quote)
            ),
        };
      });

      setAllQuotes(remappedQuotes as any[]);
    }
  }, [cellsExpanded, originalData]);

  const SourceDocumentCell = ({
    documents,
    expanded,
    onToggle,
  }: {
    documents: Array<{
      sourceDocumentType: any;
      documentId: any;
      documentNumber: any;
    }>;
    expanded: boolean;
    onToggle: () => void;
  }) => {
    const { t } = useTranslation();
    const MAX_VISIBLE = 2;

    const visibleItems = expanded
      ? documents
      : documents?.slice(0, MAX_VISIBLE);

    return (
      <div
        style={{ maxHeight: expanded ? "none" : "100px", overflow: "hidden" }}
      >
        {visibleItems?.map((item, index) => (
          <a
            key={index}
            href={renderURLDocumentType(
              item?.sourceDocumentType,
              item.documentId,
            )}
            style={{
              cursor: "pointer",
              color: "inherit",
              textDecoration: "unset",
            }}
          >
            {t(
              renderDocumentTypeForSourceDocumentNumber(
                item?.sourceDocumentType,
              ),
            )}
            : {item?.documentNumber}
            <br />
          </a>
        ))}
        {documents?.length > MAX_VISIBLE && (
          <Typography
            onClick={onToggle}
            sx={{
              cursor: "pointer",
              color: "#000",
              textDecoration: "underline",
              display: "inline-block",
              mt: "5px",
              fontWeight: 500,
              fontSize: "0.9rem",
              "&:hover": { color: "#717273" },
            }}
          >
            {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
          </Typography>
        )}
      </div>
    );
  };

  const WorksNameCell = ({
    worksNames,
    expanded,
    onToggle,
    isRTL = false,
  }: {
    worksNames: string[] | string | null | undefined;
    expanded: boolean;
    onToggle: () => void;
    isRTL?: boolean;
  }) => {
    const { t } = useTranslation();
    const MAX_VISIBLE = 3;

    const safeWorksNames = (() => {
      if (typeof worksNames === "string") {
        return worksNames
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return Array.isArray(worksNames) ? worksNames : [];
    })();

    const visibleItems = expanded
      ? safeWorksNames
      : safeWorksNames.slice(0, MAX_VISIBLE);
    const displayText = visibleItems.join(", ");

    return (
      <div
        style={{
          maxHeight: expanded ? "none" : "60px",
          overflow: "hidden",
          lineHeight: "1.4",
        }}
      >
        <span>{displayText}</span>
        {safeWorksNames.length > MAX_VISIBLE && (
          <Typography
            onClick={onToggle}
            sx={{
              cursor: "pointer",
              color: "#000",
              textDecoration: "underline",
              display: "inline-block",
              [isRTL ? "mr" : "ml"]: 1,
              fontWeight: 500,
              fontSize: "0.9rem",
              "&:hover": { color: "#717273" },
            }}
          >
            {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
          </Typography>
        )}
      </div>
    );
  };

  const PurchaseNumberCell = ({
    purchaseNumbers,
    expanded,
    onToggle,
    isRTL = false,
  }: {
    purchaseNumbers: string[] | string | null | undefined;
    expanded: boolean;
    onToggle: () => void;
    isRTL?: boolean;
  }) => {
    const { t } = useTranslation();
    const MAX_VISIBLE = 2;

    const safePurchaseNumbers = (() => {
      if (typeof purchaseNumbers === "string") {
        return purchaseNumbers
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return Array.isArray(purchaseNumbers) ? purchaseNumbers : [];
    })();

    const visibleItems = expanded
      ? safePurchaseNumbers
      : safePurchaseNumbers.slice(0, MAX_VISIBLE);
    const displayText = visibleItems.join(", ");

    return (
      <div
        style={{
          maxHeight: expanded ? "none" : "40px",
          overflow: "hidden",
          lineHeight: "1.4",
        }}
      >
        <span>{displayText}</span>
        {safePurchaseNumbers.length > MAX_VISIBLE && (
          <Typography
            onClick={onToggle}
            sx={{
              cursor: "pointer",
              color: "#000",
              textDecoration: "underline",
              display: "inline-block",
              [isRTL ? "mr" : "ml"]: 1,
              fontWeight: 500,
              fontSize: "0.9rem",
              "&:hover": { color: "#717273" },
            }}
          >
            {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
          </Typography>
        )}
      </div>
    );
  };

  const OrderNumbersCell = ({
    orderNumbers,
    legacyOrderNumber,
  }: {
    orderNumbers: string[] | null | undefined;
    legacyOrderNumber?: string | number | null;
  }) => {
    const items = (() => {
      if (Array.isArray(orderNumbers) && orderNumbers.length > 0) {
        return orderNumbers.map(String).filter(Boolean);
      }
      if (legacyOrderNumber != null && legacyOrderNumber !== "") {
        return [String(legacyOrderNumber)];
      }
      return [];
    })();

    if (items.length === 0) {
      return null;
    }

    return (
      <div style={{ lineHeight: "1.4" }}>
        {items.map((num, index) => (
          <span key={index} style={{ display: "block" }}>
            {num}
          </span>
        ))}
      </div>
    );
  };

  const ChecksCell = ({
    checkNumbers,
    checkDates,
    expanded,
    onToggle,
    isRTL = false,
  }: {
    checkNumbers: string[] | null | undefined;
    checkDates: string[] | null | undefined;
    expanded: boolean;
    onToggle: () => void;
    isRTL?: boolean;
  }) => {
    const { t } = useTranslation();
    const MAX_VISIBLE = 2;

    const safeCheckNumbers = Array.isArray(checkNumbers) ? checkNumbers : [];
    const safeCheckDates = Array.isArray(checkDates) ? checkDates : [];

    // Pair check numbers with dates (they should be in parallel/ordered)
    const checks = safeCheckNumbers.map((number, index) => ({
      number: number || "-",
      date: safeCheckDates[index] || "-",
    }));

    const visibleItems = expanded ? checks : checks.slice(0, MAX_VISIBLE);

    return (
      <div
        style={{
          maxHeight: expanded ? "none" : "40px",
          ...classes.checkCellContainer,
        }}
      >
        {visibleItems.map((check, index) => (
          <div
            key={index}
            style={{
              marginBottom: index < visibleItems.length - 1 ? "4px" : "0",
            }}
          >
            {check.number}
          </div>
        ))}
        {checks.length > MAX_VISIBLE && (
          <Typography
            onClick={onToggle}
            sx={{
              ...classes.showMoreLink,
              [isRTL ? "mr" : "ml"]: 1,
            }}
          >
            {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
          </Typography>
        )}
      </div>
    );
  };

  const CheckDatesCell = ({
    checkNumbers,
    checkDates,
    expanded,
    onToggle,
    isRTL = false,
  }: {
    checkNumbers: string[] | null | undefined;
    checkDates: string[] | null | undefined;
    expanded: boolean;
    onToggle: () => void;
    isRTL?: boolean;
  }) => {
    const { t } = useTranslation();
    const MAX_VISIBLE = 2;

    const safeCheckNumbers = Array.isArray(checkNumbers) ? checkNumbers : [];
    const safeCheckDates = Array.isArray(checkDates) ? checkDates : [];

    // Pair check numbers with dates (they should be in parallel/ordered)
    const checks = safeCheckNumbers.map((number, index) => ({
      number: number || "-",
      date: safeCheckDates[index] || "-",
    }));

    const visibleItems = expanded ? checks : checks.slice(0, MAX_VISIBLE);

    return (
      <div
        style={{
          maxHeight: expanded ? "none" : "40px",
          ...classes.checkCellContainer,
        }}
      >
        {visibleItems.map((check, index) => (
          <div
            key={index}
            style={{
              marginBottom: index < visibleItems.length - 1 ? "4px" : "0",
            }}
          >
            {check.date}
          </div>
        ))}
        {checks.length > MAX_VISIBLE && (
          <Typography
            onClick={onToggle}
            sx={{
              ...classes.showMoreLink,
              [isRTL ? "mr" : "ml"]: 1,
            }}
          >
            {expanded ? t("sales.quote.showLess") : t("sales.quote.showMore")}
          </Typography>
        )}
      </div>
    );
  };

  const getAllQuotes = useCallback(
    async (order?: any, invokedBySocket: boolean = false) => {
      const sortOrder = order || currentSortOrder;
      setIsDocumentTableError(false);
      setIsDocumentTableLoading(!invokedBySocket);

      const callBack = (res) => {
        if (res?.success) {
          const data = res?.data?.data;
          const totalItems = res?.data?.totalItems;
          const mapData = data?.map((quote: any) => {
            const baseRow = {
              creationDate: formatCustomDateTime(
                quote?.createdDate || quote?.creationDate,
              ),
              client: (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    onClickOpenCustomerModal(
                      quote?.clientId || quote?.customerId,
                    )
                  }
                >
                  {quote?.clientName || quote?.customerName}
                </div>
              ),
              agent: quote?.agentName,
              customerSupportRepresentative: quote?.customerSupportRepresentativeName,
              number: quote?.number,
              purchaseNumber: (
                <PurchaseNumberCell
                  key={`purchase-${quote?.id}`}
                  purchaseNumbers={quote?.purchaseNumber}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                  isRTL={isRTL}
                />
              ),
              worksName: (
                <WorksNameCell
                  worksNames={quote?.worksNames || []}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                  isRTL={isRTL}
                />
              ),
              title: (
                <TitleCell
                  key={`title-${quote?.id}`}
                  title={quote?.title ?? quote?.Title ?? ""}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                />
              ),
              termOfPayment: formatTermOfPaymentCell(quote?.termOfPayment, t),
              discount: quote?.discount,
              totalPriceWithoutVat: isRTL
                ? `${quote?.totalPriceWithoutVat} ${getCurrencyUnitText(
                  quote?.currency,
                )}`
                : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPriceWithoutVat
                }`,

              totalPrice: isRTL
                ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
                : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice
                }`,
              internalNotes: quote?.internalNotes,
              notes: quote?.notes,
              status: renderStatusCell(quote),
              more: (
                <MoreMenuWidget
                  quote={quote}
                  documentType={docType}
                  onClickEditQuote={onClickEditQuote}
                  onClickPdf={onClickQuotePdf}
                  onClickDuplicate={onClickDocumentDuplicate}
                  onClickLoggers={onClickDocumentLogs}
                  onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
                  onClickOpenChangeProductionStatusModal={
                    onClickOpenChangeProductionStatusModal
                  }
                  onClickSendPayment={onClickSendPayment}
                  onClickOpenCloseOrderModal={onClickOpenCloseOrderModal}
                  onClickOpenIrrelevantModal={onClickOpenIrrelevantModal}
                  onClickReopenDocument={onClickOpenReopenDocumentModal}
                  onClickOpenRemoveDraftModal={onClickOpenRemoveDraftModal}
                  onClickOpenDeliveryTimeModal={onClickOpenDeliveryTimeModal}
                  onClickOpenDuplicateModal={onClickOpenDuplicateModal}
                  onClickOpenPriceModal={onClickOpenPriceModal}
                  onClickOpenModal={onClickOpenOtherReasonModal}
                  onClickMakePayment={onClickMakePayment}
                  onClickPauseOrResume={onClickPauseOrResume}
                  onClickToggleOrderLock={onClickToggleOrderLock}
                  onClickStartProduction={onClickOpenStartProductionModal}
                  onClickCloseAndTrack={onClickOpenTrackModal}
                  onClickOpenMoveOrderToReadyModal={
                    onClickOpenMoveOrderToReadyModal
                  }
                  onOpenPackagesModal={onOpenPackagesModal}
                  onClickOpenCloseOrderNotesModal={
                    onClickOpenCloseOrderNotesModal
                  }
                  onOpenDuplicateModal={onOpenDuplicateModal}
                  handleGetTaxConfirmNo={handleGetTaxConfirmNo}
                  onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
                  onClickOpenInventoryIntakeModal={
                    onClickOpenInventoryIntakeModal
                  }
                  onClickUndoDocumentIntake={handleUndoDocumentIntake}
                  onClickSendToCustomerEmail={onClickSendToCustomerEmail}
                  onClickSendToCustomerSMS={onClickSendToCustomerSMS}
                  onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
                />
              ),
            };

            if (isPurchaseOrder) {
              return {
                ...baseRow,
                purchaseOrderNumber: quote?.number,
                orderNumber: (
                  <OrderNumbersCell
                    key={`po-order-nums-${quote?.id}`}
                    orderNumbers={quote?.orderNumbers}
                    legacyOrderNumber={quote?.orderNumber}
                  />
                ),
                supplierName: quote?.supplierName,
                itemsNumber: quote?.itemsNumber,
              };
            }

            if (isPurchaseInvoice || isPurchaseInvoiceRefund) {
              return {
                ...baseRow,
                purchaseInvoiceNumber: quote?.number,
                invoiceNumber: quote?.invoiceNumber,
                purchaseOrderNumber: quote?.orderNumbers
                  ? quote?.orderNumbers.join(", ")
                  : "",
                supplierName: (
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() => onClickOpenCustomerModal(quote?.customerId)}
                  >
                    {quote?.customerName}
                  </div>
                ),
                itemsNumber: quote?.itemsNumber,
                status:
                  (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
                    <span style={{ color: "#e77676" }}>
                      {t("sales.quote.draft")}
                    </span>
                  ) : (
                    renderStatusCell(quote)
                  ),
              };
            }

            if (isQuote) {
              return {
                ...baseRow,
                agent: quote?.agentName,
                customerSupportRepresentative: quote?.customerSupportRepresentativeName,
                number: quote?.number,
                worksName: (
                  <WorksNameCell
                    worksNames={quote?.worksNames || []}
                    expanded={cellsExpanded[quote?.id] || false}
                    onToggle={() => toggleCellsExpanded(quote?.id)}
                    isRTL={isRTL}
                  />
                ),
              };
            }

            if (isOrder) {
              const jobsElement = (
                <OrderJobsCell
                  jobs={quote?.jobs}
                  orderJobs={quote?.orderJobs || []}
                  quoteNumber={quote?.number}
                  hasPermission={CheckPermission(
                    Permissions.SHOW_BOARD_MISSIONS,
                  )}
                />
              );

              const paymentStatus = `${isRTL
                ? `${quote?.sumApplied} ${getCurrencyUnitText(
                  quote?.currency,
                )}`
                : `${getCurrencyUnitText(quote?.currency)} ${quote?.sumApplied
                }`
                }/${isRTL
                  ? `${quote?.totalPrice} ${getCurrencyUnitText(
                    quote?.currency,
                  )}`
                  : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice
                  }`
                }`;

              return {
                ...baseRow,
                estimatedProductionDate:
                  renderOrderEstimatedProductionDateCell(quote),
                deliveryDate: renderOrderDeliveryDateCell(quote),
                readyDate: quote?.readyDate
                  ? formatISODateTime(quote?.readyDate)
                  : "",
                orderNumber: quote?.number,
                quoteNumber: quote?.sourceDocumentNumber?.map((item, index) => (
                  <span key={index}>
                    {item?.documentNumber}
                    <br />
                  </span>
                )),
                purchaseNumber: (
                  <PurchaseNumberCell
                    key={`purchase-${quote?.id}`}
                    purchaseNumbers={quote?.purchaseNumber}
                    expanded={cellsExpanded[quote?.id] || false}
                    onToggle={() => toggleCellsExpanded(quote?.id)}
                    isRTL={isRTL}
                  />
                ),
                productionStatus: renderOrderProductionStatusCell(quote),
                jobs: jobsElement,
                cost: quote?.cost,
                paymentStatus: isOrdersTracking
                  ? paymentStatus
                  : baseRow.totalPrice,
                deliveryStatus: (
                  <ClickableStatusButton
                    id={quote?.id}
                    document={quote}
                    documentType={docType}
                    onClickOpenMoveOrderToReadyModal={
                      onClickOpenMoveOrderToReadyModal
                    }
                    getAllQuotes={getAllQuotes}
                  />
                ),
              };
            }

            return {
              ...baseRow,
              sourceDocument: (
                <SourceDocumentCell
                  documents={quote?.sourceDocumentNumber || []}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                />
              ),
              deliveryStatus:
                isInvoice || isDeliveryNote ? (
                  <ClickableStatusButton
                    id={quote?.id}
                    document={quote}
                    documentType={docType}
                    onClickOpenMoveOrderToReadyModal={
                      onClickOpenMoveOrderToReadyModal
                    }
                    getAllQuotes={getAllQuotes}
                  />
                ) : (
                  <div>—</div>
                ),
              status:
                (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
                  <span style={{ color: "#e77676" }}>
                    {t("sales.quote.draft")}
                  </span>
                ) : (
                  _renderStatus(docType, quote, t)
                ),
            };
          });

          // For receipts
          const mapReceiptData = data?.map((quote: any) => {
            const baseReceiptRow = {
              creationDate: formatCustomDateTime(quote?.creationDate).split(
                " ",
              )[0],
              [isSupplierPayments ? "supplier" : "client"]: (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => onClickOpenCustomerModal(quote?.customerId)}
                >
                  {quote?.customerName}
                </div>
              ),
              supplier: (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => onClickOpenCustomerModal(quote?.customerId)}
                >
                  {quote?.customerName}
                </div>
              ),
              agent: quote?.agentName,
              customerSupportRepresentative: quote?.customerSupportRepresentativeName,
              number: quote?.number,
              paymentMethod: _renderPaymentType(quote?.paymentType),
              totalPrice: isRTL
                ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
                : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice
                }`,
              notes: quote?.notes,
              status: t(`documentStatus.${quote?.status}`),
              more: (
                <MoreMenuWidget
                  quote={quote}
                  documentType={docType}
                  onClickPdf={onOpenExportReceiptPdfModal}
                  onClickDuplicate={onClickDocumentDuplicate}
                  onClickLoggers={onClickDocumentLogs}
                  onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
                  onOpenCancelReceiptModal={onOpenCancelReceiptModal}
                  onOpenCancelReceiptModalRefund={
                    onOpenCancelReceiptModalRefund
                  }
                  onClickOpenChangeProductionStatusModal={
                    onClickOpenChangeProductionStatusModal
                  }
                  onClickSendPayment={onClickSendPayment}
                  onClickSendToCustomerEmail={onClickSendToCustomerEmail}
                  onClickSendToCustomerSMS={onClickSendToCustomerSMS}
                  onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
                  onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
                />
              ),
            };

            // Add check fields for receipts and supplier payments
            if (isReceipt || isSupplierPayments) {
              return {
                ...baseReceiptRow,
                checkNumber: (
                  <ChecksCell
                    key={`check-number-${quote?.id}`}
                    checkNumbers={quote?.checkNumbers || quote?.CheckNumbers}
                    checkDates={quote?.checkDates || quote?.CheckDates}
                    expanded={cellsExpanded[quote?.id] || false}
                    onToggle={() => toggleCellsExpanded(quote?.id)}
                    isRTL={isRTL}
                  />
                ),
                checkDueDate: (
                  <CheckDatesCell
                    key={`check-date-${quote?.id}`}
                    checkNumbers={quote?.checkNumbers || quote?.CheckNumbers}
                    checkDates={quote?.checkDates || quote?.CheckDates}
                    expanded={cellsExpanded[quote?.id] || false}
                    onToggle={() => toggleCellsExpanded(quote?.id)}
                    isRTL={isRTL}
                  />
                ),
              };
            }

            return baseReceiptRow;
          });

          // Store original data for re-mapping when cellsExpanded changes
          setOriginalData(data);
          setAllQuotes(
            isReceipt || isSupplierPayments ? mapReceiptData : mapData,
          );
          setPagesCount(Math.ceil(totalItems / pageSize));
          setAllStatistics({
            documentStatisticsList: res?.data?.documentStatisticsList ?? [],
            totalItems: totalItems ?? 0,
          });
        } else {
          // Mark error only for non-200 responses
          setIsDocumentTableError(res?.status && res.status !== 200);
          setAllQuotes([]);
        }
      };

      try {
        if (!router.isReady) return;

        const currentPageNumber = router.query.pageNumber;
        const currentStatusId = router.query.statusId;
        const currentFinalPatternSearch = router.query.finalPatternSearch;
        const currentDocumentNumber = router.query.documentNumber;
        const currentCustomerId = router.query.customerId;
        const currentAgentId = router.query.agentId;
        const currentCustomerSupportRepresentativeId =
          router.query.customerSupportRepresentativeId;
        const currentApprovalStatusId = router.query.approvalStatusId;
        const currentMinPrice = router.query.minPrice || minPrice;
        const currentMaxPrice = router.query.maxPrice || maxPrice;
        const currentFromDate = router.query.fromDate
          ? new Date(
            Array.isArray(router.query.fromDate)
              ? router.query.fromDate[0]
              : router.query.fromDate,
          )
          : null;
        const currentToDate = router.query.toDate
          ? new Date(
            Array.isArray(router.query.toDate)
              ? router.query.toDate[0]
              : router.query.toDate,
          )
          : null;

        const currentCheckFromDate = router.query.checkFromDate
          ? new Date(
            Array.isArray(router.query.checkFromDate)
              ? router.query.checkFromDate[0]
              : router.query.checkFromDate,
          )
          : null;

        const currentCheckToDate = router.query.checkToDate
          ? new Date(
            Array.isArray(router.query.checkToDate)
              ? router.query.checkToDate[0]
              : router.query.checkToDate,
          )
          : null;

        const currentAccountingStatus = router.query.accountingStatus
          ? accountingStatuses.find(
            (status) =>
              status.value === Number(router.query.accountingStatus),
          )
          : null;

        const currentDeliveryStatusStatus = router.query.deliveryStatus
          ? deliveryStatuses.find(
            (status) => status.value === Number(router.query.deliveryStatus),
          )
          : null;

        const currentProductionStatus = router.query.productionStatus
          ? productionStatuses.find(
            (status) =>
              status.value === Number(router.query.productionStatus),
          )
          : null;

        const currentIsCanceled = router.query.isCanceled
          ? Array.isArray(router.query.isCanceled)
            ? router.query.isCanceled[0] === "true"
            : router.query.isCanceled === "true"
          : false;
        const currentIsPaused = router.query.isPaused
          ? Array.isArray(router.query.isPaused)
            ? router.query.isPaused[0] === "true"
            : router.query.isPaused === "true"
          : false;
        const currentIsOrdersWithoutJobs = router.query.isOrdersWithoutJobs
          ? Array.isArray(router.query.isOrdersWithoutJobs)
            ? router.query.isOrdersWithoutJobs[0] === "true"
            : router.query.isOrdersWithoutJobs === "true"
          : false;

        const currentProductsIds = Array.isArray(router.query.productsIds)
          ? router.query.productsIds
          : router.query.productsIds
            ? [router.query.productsIds]
            : productIds;

        const currentPrintHouseMaterialIds = Array.isArray(
          router.query.printHouseMaterialIds,
        )
          ? router.query.printHouseMaterialIds
          : router.query.printHouseMaterialIds
            ? [router.query.printHouseMaterialIds]
            : printHouseMaterialIds;

        const currentCurrency = router.query.currency
          ? currencies.find((curr) => curr.value === router.query.currency)
          : null;

        const currentPaymentType = router.query.paymentType
          ? typeOfPayments.find(
            (type) => type.value === Number(router.query.paymentType),
          )
          : null;

        // keep your API call logic unchanged
        if (isReceipt || isSupplierPayments) {
          await getAllReceiptsApi(callApi, callBack, {
            ...(!isReceipt && {
              isOutgoingPayments: true,
            }),
            clientId: currentCustomerId || customerId?.id,
            agentId: currentAgentId || agentId?.id,
            customerSupportRepresentativeId:
              currentCustomerSupportRepresentativeId ||
              customerSupportRepresentativeId?.id,
            status: currentAccountingStatus?.value ?? accountingStatus?.value,
            paymentType: currentPaymentType?.value ?? paymentType?.value,
            patternSearch:
              finalPatternSearch !== null &&
                finalPatternSearch !== undefined &&
                finalPatternSearch !== ""
                ? finalPatternSearch
                : currentFinalPatternSearch || currentDocumentNumber,
            ...(currentDocumentNumber
              ? { documentNumber: currentDocumentNumber }
              : {}),

            ...(currentFromDate
              ? { fromDate: GetDateFormat(currentFromDate) }
              : {}),
            ...(currentToDate ? { toDate: GetDateFormat(currentToDate) } : {}),
            ...(currentCheckFromDate
              ? { checkFromDate: GetDateFormat(currentCheckFromDate) }
              : {}),
            ...(currentCheckToDate
              ? { checkToDate: GetDateFormat(currentCheckToDate) }
              : {}),
            ...(currentMinPrice && currentMinPrice !== ""
              ? { minPrice: currentMinPrice }
              : {}),
            ...(currentMaxPrice && currentMaxPrice !== ""
              ? { maxPrice: currentMaxPrice }
              : {}),

            model: {
              pageNumber: currentPageNumber || page,
              pageSize: pageSize,
              order: sortOrder,
            },
          });
        } else {
          await getAllDocumentsApi(callApi, callBack, {
            documentType: docType,
            data: {
              model: {
                pageNumber: currentPageNumber || page,
                pageSize: pageSize,
                order: sortOrder,
              },
              dateRange,
              customerId: currentCustomerId || customerId?.id,
              agentId: currentAgentId || agentId?.id,
              customerSupportRepresentativeId:
                currentCustomerSupportRepresentativeId ||
                customerSupportRepresentativeId?.id,
              statusId:
                quoteStatusId?.value || currentStatusId || statusId?.value,
              approvalStatus:
                currentApprovalStatusId || approvalStatusId?.value,
              currency: currentCurrency?.value ?? currencyFilter?.value,

              ...(currentMinPrice && currentMinPrice !== ""
                ? { minPrice: currentMinPrice }
                : {}),
              ...(currentMaxPrice && currentMaxPrice !== ""
                ? { maxPrice: currentMaxPrice }
                : {}),

              patternSearch:
                finalPatternSearch !== null &&
                  finalPatternSearch !== undefined &&
                  finalPatternSearch !== ""
                  ? finalPatternSearch
                  : currentFinalPatternSearch || currentDocumentNumber,
              ...(currentDocumentNumber
                ? { documentNumber: currentDocumentNumber }
                : {}),

              ...(currentFromDate
                ? { fromDate: GetDateFormat(currentFromDate) }
                : {}),
              ...(currentToDate
                ? { toDate: GetDateFormat(currentToDate) }
                : {}),

              ...(currentProductsIds?.length
                ? { productList: currentProductsIds }
                : {}),
              ...(currentPrintHouseMaterialIds?.length
                ? { printHouseMaterialIds: currentPrintHouseMaterialIds }
                : {}),

              closeStatus:
                currentAccountingStatus?.value ?? accountingStatus?.value,
              deliveryStatus:
                currentDeliveryStatusStatus?.value ?? deliveryStatus?.value,
              productionStatus:
                currentProductionStatus?.value ?? productionStatus?.value,

              isCanceled: currentIsCanceled || isCanceledState,
              isPaused: currentIsPaused || isPausedOrders,
              isOrdersWithoutJobs:
                currentIsOrdersWithoutJobs || isOrdersWithoutJobs,
              isOrderInTracking: isOrdersTracking,
            },
          });
        }
      } finally {
        setIsDocumentTableLoading(false);
        if (invokedBySocket) setIsSocketRefreshingDocuments(false);
      }
    },
    [
      docType,
      callApi,
      finalPatternSearch,
      customerId,
      agentId,
      customerSupportRepresentativeId,
      isReceipt,
      isSupplierPayments,
      page,
      pageSize,
      statusId,
      approvalStatusId,
      quoteStatusId,
      accountingStatus,
      deliveryStatus,
      productionStatus,
      dateRange,
      minPrice,
      maxPrice,
      productIds,
      printHouseMaterialIds,
      isCanceledState,
      isPausedOrders,
      isOrdersWithoutJobs,
      fromDate,
      toDate,
      onClickOpenModal,
      onClickQuotePdf,
      onClickDocumentLogs,
      paymentType,
    ],
  );

  const getAllQuotesInitial = async () => {
    setIsDocumentTableError(false);
    setIsDocumentTableLoading(true);
    const callBack = (res) => {
      if (res?.success) {
        const data = res?.data?.data;
        const totalItems = res?.data?.totalItems;

        const mapData = data?.map((quote: any) => {
          const baseRow = {
            creationDate: formatCustomDateTime(
              quote?.createdDate || quote?.creationDate,
            ),
            client: (
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  onClickOpenCustomerModal(quote?.clientId || quote?.customerId)
                }
              >
                {quote?.clientName || quote?.customerName}
              </div>
            ),
            agent: quote?.agentName,
            customerSupportRepresentative: quote?.customerSupportRepresentativeName,
            number: quote?.number,
            purchaseNumber: (
              <PurchaseNumberCell
                key={`purchase-${quote?.id}`}
                purchaseNumbers={quote?.purchaseNumber}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
                isRTL={isRTL}
              />
            ),
            worksName: (
              <WorksNameCell
                worksNames={quote?.worksNames || []}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
                isRTL={isRTL}
              />
            ),
            title: (
              <TitleCell
                key={`title-${quote?.id}`}
                title={quote?.title ?? quote?.Title ?? ""}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
              />
            ),
            termOfPayment: formatTermOfPaymentCell(quote?.termOfPayment, t),
            discount: quote?.discount,
            totalPriceWithoutVat: isRTL
              ? `${quote?.totalPriceWithoutVat} ${getCurrencyUnitText(
                quote?.currency,
              )}`
              : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPriceWithoutVat
              }`,

            totalPrice: isRTL
              ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
              : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice}`,
            internalNotes: quote?.internalNotes,
            notes: quote?.notes,
            status: renderStatusCell(quote),
            more: (
              <MoreMenuWidget
                quote={quote}
                documentType={docType}
                onClickEditQuote={onClickEditQuote}
                onClickPdf={onClickQuotePdf}
                onClickDuplicate={onClickDocumentDuplicate}
                onClickLoggers={onClickDocumentLogs}
                onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
                onClickOpenChangeProductionStatusModal={
                  onClickOpenChangeProductionStatusModal
                }
                onClickSendPayment={onClickSendPayment}
                onClickOpenCloseOrderModal={onClickOpenCloseOrderModal}
                onClickOpenIrrelevantModal={onClickOpenIrrelevantModal}
                onClickReopenDocument={onClickOpenReopenDocumentModal}
                onClickOpenRemoveDraftModal={onClickOpenRemoveDraftModal}
                onClickOpenDeliveryTimeModal={onClickOpenDeliveryTimeModal}
                onClickOpenDuplicateModal={onClickOpenDuplicateModal}
                onClickOpenPriceModal={onClickOpenPriceModal}
                onClickOpenModal={onClickOpenOtherReasonModal}
                onClickMakePayment={onClickMakePayment}
                onClickPauseOrResume={onClickPauseOrResume}
                onClickStartProduction={onClickOpenStartProductionModal}
                onClickCloseAndTrack={onClickOpenTrackModal}
                onClickOpenMoveOrderToReadyModal={
                  onClickOpenMoveOrderToReadyModal
                }
                onOpenPackagesModal={onOpenPackagesModal}
                onClickOpenCloseOrderNotesModal={
                  onClickOpenCloseOrderNotesModal
                }
                onOpenDuplicateModal={onOpenDuplicateModal}
                handleGetTaxConfirmNo={handleGetTaxConfirmNo}
                onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
                onClickOpenInventoryIntakeModal={
                  onClickOpenInventoryIntakeModal
                }
                onClickUndoDocumentIntake={handleUndoDocumentIntake}
                onClickSendToCustomerEmail={onClickSendToCustomerEmail}
                onClickSendToCustomerSMS={onClickSendToCustomerSMS}
                onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
              />
            ),
          };

          if (isPurchaseOrder) {
            return {
              ...baseRow,
              purchaseOrderNumber: quote?.number,
              orderNumber: (
                <OrderNumbersCell
                  key={`po-order-nums-${quote?.id}`}
                  orderNumbers={quote?.orderNumbers}
                  legacyOrderNumber={quote?.orderNumber}
                />
              ),
              supplierName: quote?.supplierName,
              itemsNumber: quote?.itemsNumber,
            };
          }

          if (isPurchaseInvoice || isPurchaseInvoiceRefund) {
            return {
              ...baseRow,
              purchaseInvoiceNumber: quote?.number,
              invoiceNumber: quote?.invoiceNumber,
              purchaseOrderNumber: quote?.orderNumbers
                ? quote?.orderNumbers.join(", ")
                : "",
              supplierName: (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => onClickOpenCustomerModal(quote?.customerId)}
                >
                  {quote?.customerName}
                </div>
              ),
              itemsNumber: quote?.itemsNumber,
              status:
                (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
                  <span style={{ color: "#e77676" }}>
                    {t("sales.quote.draft")}
                  </span>
                ) : (
                  _renderStatus(docType, quote, t)
                ),
            };
          }

          if (isQuote) {
            return {
              ...baseRow,
              agent: quote?.agentName,
              customerSupportRepresentative: quote?.customerSupportRepresentativeName,
              number: quote?.number,
              worksName: (
                <WorksNameCell
                  worksNames={quote?.worksNames || []}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                  isRTL={isRTL}
                />
              ),
            };
          }

          if (isOrder) {
            const jobsElement = (
              <OrderJobsCell
                jobs={quote?.jobs}
                orderJobs={quote?.orderJobs || []}
                quoteNumber={quote?.number}
                hasPermission={CheckPermission(Permissions.SHOW_BOARD_MISSIONS)}
              />
            );

            const paymentStatus = `${isRTL
              ? `${quote?.sumApplied} ${getCurrencyUnitText(quote?.currency)}`
              : `${getCurrencyUnitText(quote?.currency)} ${quote?.sumApplied}`
              }/${isRTL
                ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
                : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice}`
              }`;

            return {
              ...baseRow,
              estimatedProductionDate: renderOrderEstimatedProductionDateCell(quote),
              deliveryDate: renderOrderDeliveryDateCell(quote),
              readyDate: quote?.readyDate
                ? formatISODateTime(quote?.readyDate)
                : "",
              orderNumber: quote?.number,
              quoteNumber: quote?.sourceDocumentNumber?.map((item, index) => (
                <span key={index}>
                  {item?.documentNumber}
                  <br />
                </span>
              )),
              purchaseNumber: (
                <PurchaseNumberCell
                  key={`purchase-${quote?.id}`}
                  purchaseNumbers={quote?.purchaseNumber}
                  expanded={cellsExpanded[quote?.id] || false}
                  onToggle={() => toggleCellsExpanded(quote?.id)}
                  isRTL={isRTL}
                />
              ),
              productionStatus: renderOrderProductionStatusCell(quote),
              jobs: jobsElement,
              cost: quote?.cost,
              paymentStatus: isOrdersTracking
                ? paymentStatus
                : baseRow.totalPrice,
              deliveryStatus: (
                <ClickableStatusButton
                  id={quote?.id}
                  document={quote}
                  documentType={docType}
                  onClickOpenMoveOrderToReadyModal={
                    onClickOpenMoveOrderToReadyModal
                  }
                  getAllQuotes={getAllQuotes}
                />
              ),
            };
          }

          return {
            ...baseRow,
            sourceDocument: (
              <SourceDocumentCell
                documents={quote?.sourceDocumentNumber || []}
                expanded={cellsExpanded[quote?.id] || false}
                onToggle={() => toggleCellsExpanded(quote?.id)}
              />
            ),
            deliveryStatus:
              isInvoice || isDeliveryNote ? (
                <ClickableStatusButton
                  id={quote?.id}
                  document={quote}
                  documentType={docType}
                  onClickOpenMoveOrderToReadyModal={
                    onClickOpenMoveOrderToReadyModal
                  }
                  getAllQuotes={getAllQuotes}
                />
              ) : (
                <div>—</div>
              ),
            status:
              (isInvoice || isPurchaseInvoice) && quote?.isDraft ? (
                <span style={{ color: "#e77676" }}>
                  {t("sales.quote.draft")}
                </span>
              ) : (
                _renderStatus(docType, quote, t)
              ),
          };
        });

        // For receipts
        const mapReceiptData = data?.map((quote: any) => ({
          creationDate: formatCustomDateTime(quote?.creationDate).split(" ")[0],
          [isSupplierPayments ? "supplier" : "client"]: (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => onClickOpenCustomerModal(quote?.customerId)}
            >
              {quote?.customerName}
            </div>
          ),
          agent: quote?.agentName,
          customerSupportRepresentative: quote?.customerSupportRepresentativeName,
          number: quote?.number,
          paymentMethod: _renderPaymentType(quote?.paymentType),
          totalPrice: isRTL
            ? `${quote?.totalPrice} ${getCurrencyUnitText(quote?.currency)}`
            : `${getCurrencyUnitText(quote?.currency)} ${quote?.totalPrice}`,
          notes: quote?.notes,
          status: t(`documentStatus.${quote?.status}`),
          more: (
            <MoreMenuWidget
              quote={quote}
              documentType={docType}
              onClickPdf={onOpenExportReceiptPdfModal}
              onClickDuplicate={onClickDocumentDuplicate}
              onClickLoggers={onClickDocumentLogs}
              onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
              onOpenCancelReceiptModal={onOpenCancelReceiptModal}
              onOpenCancelReceiptModalRefund={onOpenCancelReceiptModalRefund}
              onClickOpenChangeProductionStatusModal={
                onClickOpenChangeProductionStatusModal
              }
              onClickSendPayment={onClickSendPayment}
              onOpenDuplicateModal={onOpenDuplicateModal}
              onClickOpenInventoryIntakeModal={onClickOpenInventoryIntakeModal}
              onClickUndoDocumentIntake={handleUndoDocumentIntake}
              onClickSendToCustomerEmail={onClickSendToCustomerEmail}
              onClickSendToCustomerSMS={onClickSendToCustomerSMS}
              onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
              onClickSendPaymentEmail={onClickSendPaymentEmail}
              onClickSendPaymentSMS={onClickSendPaymentSMS}
              onClickSendPaymentWhatsApp={onClickSendPaymentWhatsApp}
              onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
            />
          ),
        }));

        // Store original data for re-mapping when cellsExpanded changes
        setOriginalData(data);
        setAllQuotes(
          isReceipt || isSupplierPayments ? mapReceiptData : mapData,
        );
        setPagesCount(Math.ceil(totalItems / pageSize));
        setAllStatistics({
          documentStatisticsList: res?.data?.documentStatisticsList ?? [],
          totalItems: totalItems ?? 0,
        });
      } else {
        setIsDocumentTableError(res?.status && res.status !== 200);
        setAllQuotes([]);
      }
    };

    try {
      if (!router.isReady) return;

      if (isReceipt || isSupplierPayments) {
        await getAllReceiptsApi(callApi, callBack, {
          model: {
            pageNumber: page,
            pageSize: pageSize,
          },
          ...(!isReceipt && {
            isOutgoingPayments: true,
          }),
        });
      } else {
        await getAllDocumentsApi(callApi, callBack, {
          documentType: docType,
          data: {
            isOrderInTracking: isOrdersTracking,
            model: {
              pageNumber: page,
              pageSize: pageSize,
            },
          },
        });
      }
    } finally {
      setIsDocumentTableLoading(false);
    }
  };

  // State for intake validation dialog
  const [showIntakeValidationDialog, setShowIntakeValidationDialog] =
    useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    documentId: string;
    status: number;
  } | null>(null);
  const [selectedDocumentForValidation, setSelectedDocumentForValidation] =
    useState<any>(null);

  // State for undo intake dialog when changing from Received status
  const [showUndoIntakeDialog, setShowUndoIntakeDialog] = useState(false);
  const [pendingStatusChangeWithUndo, setPendingStatusChangeWithUndo] =
    useState<{
      documentId: string;
      status: number;
      shouldUndoIntake: boolean;
    } | null>(null);

  const handlePurchaseOrderStatusChange = useCallback(
    async (
      documentId: string,
      nextStatus: number,
      document?: any,
      shouldUndoIntake: boolean | null = null,
    ) => {
      if (document && isPurchaseOrderStatusChangeLocked(document)) {
        return;
      }

      // Get current status - use status property (as per API response structure)
      const currentStatus = document?.status ?? document?.documentStatus ?? 0;

      // Check if changing FROM Received status to another status
      // Only show dialog if shouldUndoIntake is null (not explicitly set from dialog handlers)
      if (
        shouldUndoIntake === null &&
        currentStatus === PurchaseOrderStatuses.Received &&
        nextStatus !== PurchaseOrderStatuses.Received
      ) {
        // Use document data already available in the table (no need to fetch - avoids 500 error)
        // Check document-level isIntake flag (available in list response)
        const documentIsIntake = document?.isIntake === true;

        // Show dialog if document-level flag is true OR if any items have isIntake = true
        if (documentIsIntake) {
          // Show dialog asking if user wants to undo intake
          setPendingStatusChangeWithUndo({
            documentId,
            status: nextStatus,
            shouldUndoIntake: false,
          });
          setSelectedDocumentForValidation(document);
          setShowUndoIntakeDialog(true);
          return;
        }
      }

      // Default shouldUndoIntake to false if not explicitly set
      const shouldUndo = shouldUndoIntake === null ? false : shouldUndoIntake;

      // Check if changing from Under Creation to Received status
      if (nextStatus === PurchaseOrderStatuses.Received) {
        const hasNotFullyIntakedItems =
          document?.isFullyIntaked === false ||
          document?.documentItems?.some(
            (item: any) => item.isFullyIntaked !== true,
          );

        if (hasNotFullyIntakedItems) {
          // Show validation dialog
          setPendingStatusChange({ documentId, status: nextStatus });
          setSelectedDocumentForValidation(document);
          setShowIntakeValidationDialog(true);
          return;
        }
      }

      // Proceed with status change
      setStatusUpdatingId(documentId);
      try {
        // If shouldUndo is true, call undo intake API (which also updates status)
        if (shouldUndo && document) {
          const undoResponse = await undoDocumentIntakeApi(callApi, () => { }, {
            documentType: docType,
            purchaseOrderId: documentId,
            status: nextStatus,
            shouldUndoIntake: true,
          });
          if (undoResponse?.success) {
            alertSuccessUpdate();
            await getAllQuotes(undefined, true);
          } else {
            alertFaultUpdate();
          }
        } else {
          // If not undoing intake, just update status normally
          const response = await updateDocumentStatusApi(callApi, () => { }, {
            documentType: docType,
            documentId,
            status: nextStatus,
          });

          if (response?.success) {
            alertSuccessUpdate();
            await getAllQuotes(undefined, true);
          } else {
            alertFaultUpdate();
          }
        }
      } catch (error) {
        alertFaultUpdate();
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [alertFaultUpdate, alertSuccessUpdate, callApi, docType, getAllQuotes],
  );

  // Handle undo intake dialog - yes (undo intake)
  const handleConfirmUndoIntake = () => {
    if (pendingStatusChangeWithUndo && selectedDocumentForValidation) {
      setShowUndoIntakeDialog(false);
      handlePurchaseOrderStatusChange(
        pendingStatusChangeWithUndo.documentId,
        pendingStatusChangeWithUndo.status,
        selectedDocumentForValidation,
        true,
      );
      setPendingStatusChangeWithUndo(null);
      setSelectedDocumentForValidation(null);
    }
  };

  // Handle undo intake dialog - no (don't undo intake)
  const handleCancelUndoIntake = () => {
    if (pendingStatusChangeWithUndo && selectedDocumentForValidation) {
      setShowUndoIntakeDialog(false);
      handlePurchaseOrderStatusChange(
        pendingStatusChangeWithUndo.documentId,
        pendingStatusChangeWithUndo.status,
        selectedDocumentForValidation,
        false,
      );
      setPendingStatusChangeWithUndo(null);
      setSelectedDocumentForValidation(null);
    }
  };

  // Handle intake validation dialog - open intake modal
  const handleOpenIntakeFromValidation = () => {
    if (selectedDocumentForValidation) {
      setShowIntakeValidationDialog(false);
      onClickOpenInventoryIntakeModal(selectedDocumentForValidation);
      // Don't clear pendingStatusChange here - we'll use it in handleIntakeSuccess
    }
  };

  // Handle undo document intake
  const handleUndoDocumentIntake = useCallback(
    async (document: any) => {
      if (!isPurchaseOrder || !document?.id) {
        return;
      }

      setStatusUpdatingId(document.id);
      try {
        const response = await undoDocumentIntakeApi(callApi, () => { }, {
          documentType: docType,
          purchaseOrderId: document.id,
          status: document.documentStatus || document.status || 0,
          shouldUndoIntake: true,
        });
        if (response?.success) {
          alertSuccessUpdate();
          await getAllQuotes(undefined, true);
        } else {
          alertFaultUpdate();
        }
      } catch (error) {
        alertFaultUpdate();
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [
      isPurchaseOrder,
      callApi,
      docType,
      getAllQuotes,
      alertSuccessUpdate,
      alertFaultUpdate,
    ],
  );

  // Handle undo item intake
  const handleUndoItemIntake = useCallback(
    async (item: any, document: any) => {
      if (!isPurchaseOrder || !item?.id) {
        return;
      }

      try {
        const response = await undoDocumentItemIntakeApi(callApi, () => { }, {
          documentType: docType,
          itemId: item.id,
        });
        if (response?.success) {
          alertSuccessUpdate();
          await getAllQuotes(undefined, true);
        } else {
          alertFaultUpdate();
        }
      } catch (error) {
        alertFaultUpdate();
      }
    },
    [
      isPurchaseOrder,
      callApi,
      docType,
      getAllQuotes,
      alertSuccessUpdate,
      alertFaultUpdate,
    ],
  );

  // Handle intake validation dialog - cancel
  const handleCancelIntakeValidation = () => {
    setShowIntakeValidationDialog(false);
    setPendingStatusChange(null);
    setSelectedDocumentForValidation(null);
  };

  function renderStatusCell(document: any) {
    if (isPurchaseOrder && !isPurchaseOrderCanceled(document)) {
      return (
        <PurchaseOrderStatusSelector
          document={document}
          statusOptions={purchaseOrderStatusOptions}
          isLoading={statusUpdatingId === document?.id}
          onStatusChange={(status) =>
            void handlePurchaseOrderStatusChange(document?.id, status, document)
          }
          renderStatus={() => _renderStatus(docType, document, t)}
        />
      );
    }
    return _renderStatus(docType, document, t);
  }

  const { isSocketUpdateDocumentsTable, setIsSocketUpdateDocumentsTable } =
    useDocumentsTableSocketIo(docType);

  useEffect(() => {
    if (isSocketUpdateDocumentsTable) {
      setIsSocketRefreshingDocuments(true);
      getAllQuotes(undefined, true);
      setIsSocketUpdateDocumentsTable(false);
    }
  }, [isSocketUpdateDocumentsTable]);

  const onClickSearchFilter = async () => {
    const query: Query = { ...router.query };

    // Add currency filter
    if (currencyFilter?.value) query.currency = currencyFilter.value;
    else delete query.currency;

    if (productIds.length > 0) {
      query.productsIds = productIds;
    } else {
      delete query.productsIds;
    }

    if (printHouseMaterialIds.length > 0) {
      query.printHouseMaterialIds = printHouseMaterialIds;
    } else {
      delete query.printHouseMaterialIds;
    }

    if (customerId?.id) query.customerId = customerId.id;
    else delete query.customerId;

    if (customerId?.name) query.customerName = customerId.name;
    else delete query.customerName;

    if (agentId?.id) query.agentId = agentId.id;
    else delete query.agentId;

    if (customerSupportRepresentativeId?.id)
      query.customerSupportRepresentativeId = customerSupportRepresentativeId.id;
    else delete query.customerSupportRepresentativeId;

    if (statusId?.value) query.statusId = statusId.value;
    else delete query.statusId;

    if (approvalStatusId?.value)
      query.approvalStatusId = approvalStatusId.value;
    else delete query.approvalStatusId;

    if (minPrice) query.minPrice = minPrice;
    else delete query.minPrice;

    if (maxPrice) query.maxPrice = maxPrice;
    else delete query.maxPrice;

    // Always set page to 1 when searching
    delete query.pageNumber;
    query.pageNumber = 1;
    setPage(1);

    if (fromDate instanceof Date) query.fromDate = fromDate.toISOString();
    else delete query.fromDate;

    if (toDate instanceof Date) query.toDate = toDate.toISOString();
    else delete query.toDate;

    if (checkFromDate instanceof Date)
      query.checkFromDate = checkFromDate.toISOString();
    else delete query.checkFromDate;

    if (checkToDate instanceof Date)
      query.checkToDate = checkToDate.toISOString();
    else delete query.checkToDate;

    if (accountingStatus)
      query.accountingStatus = accountingStatus.value.toString();
    else delete query.accountingStatus;

    if (paymentType) query.paymentType = paymentType.value.toString();
    else delete query.paymentType;

    if (deliveryStatus) query.deliveryStatus = deliveryStatus.value.toString();
    else delete query.deliveryStatus;

    if (productionStatus)
      query.productionStatus = productionStatus.value.toString();
    else delete query.productionStatus;

    if (isCanceledState) query.isCanceled = isCanceledState;
    else delete query.isCanceled;

    if (isPausedOrders) query.isPaused = isPausedOrders;
    else delete query.isPaused;

    if (isOrdersWithoutJobs) query.isOrdersWithoutJobs = isOrdersWithoutJobs;
    else delete query.isOrdersWithoutJobs;

    // Handle documentNumber from URL - add it to patternSearch
    const documentNumberFromUrl = router.query.documentNumber as string;
    let trimmedSearch = patternSearch?.trim() || "";

    // If documentNumber is in URL, include it in patternSearch
    if (documentNumberFromUrl) {
      if (trimmedSearch && !trimmedSearch.includes(documentNumberFromUrl)) {
        // Combine existing patternSearch with documentNumber
        trimmedSearch = `${trimmedSearch} ${documentNumberFromUrl}`.trim();
      } else if (!trimmedSearch) {
        // Use documentNumber as patternSearch if patternSearch is empty
        trimmedSearch = documentNumberFromUrl;
      }
      query.documentNumber = documentNumberFromUrl;
    } else {
      delete query.documentNumber;
    }

    setFinalPatternSearch(trimmedSearch);

    if (trimmedSearch) {
      query.finalPatternSearch = trimmedSearch;
    } else {
      delete query.finalPatternSearch;
    }

    handleClose();

    if (statusId !== null && statusId !== undefined) {
      handleSecondCardClick();
    }

    await router.push({
      pathname: router.pathname,
      query: { ...query, pageNumber: 1 },
    });
  };

  const onClickClearFilter = () => {
    handleClose();
    handleSecondCardClick();
    setAgentId(null);
    setCustomerSupportRepresentativeId(null);
    setCustomerId(null);
    setStatusId(null);
    setApprovalStatusId(null);
    setAccountingStatus(null);
    setPaymentType(null);
    setDeliveryStatus(null);
    setProductionStatus(null);
    setFromDate(null);
    setToDate(null);
    setCheckFromDate(null);
    setCheckToDate(null);
    setResetDatePicker(true);
    setIsCanceledState(false);
    setIsPausedOrders(false);
    setIsOrdersWithoutJobs(false);
    setPage(1);
    setMinPrice("");
    setMaxPrice("");
    setProductIds([]);
    setPrintHouseMaterialIds([]);
    setFinalPatternSearch("");
    setPatternSearch("");
    setCurrentSortOrder(null);
    setCurrencyFilter(null);

    setHasFetchedQuery(null);

    const query = {};
    router.push({
      pathname: router.pathname,
      query: query,
    });
  };

  const [hasFetchedQuery, setHasFetchedQuery] = useState<string | null>(null);
  useEffect(() => {
    const isNotFilteredData = Object.keys(router?.query || {}).length === 0;
    const serializedQuery = JSON.stringify(router.query);

    if (!isFromHomePage) {
      if (isNotFilteredData) {
        getAllQuotesInitial();
        setHasFetchedQuery(null);
      } else if (hasFetchedQuery !== serializedQuery) {
        getAllQuotes();
        setHasFetchedQuery(serializedQuery); // Mark this query as fetched
      }
    }
  }, [pageSize, router.query]);

  const { currentTheme } = useTheme();

  const isHPClient = currentTheme === "hp-client";
  const csrHeaderLabel = t("usersSettings.isCustomerSupportRepresentative");

  const tableHeadersRaw = isPurchaseOrder
    ? [
      t("sales.quote.creationDate"),
      t("sales.quote.purchaseOrderNumber"),
      t("sales.quote.orderNumbers"),
      t("sales.quote.supplierName"),
      t("sales.quote.client"),
      t("sales.quote.itemsNumber"),
      t("sales.quote.totalPrice"),
      t("sales.quote.notes"),
      t("sales.quote.status"),
      isHPClient ? "" : t("sales.quote.more"),
    ]
    : isPurchaseInvoice || isPurchaseInvoiceRefund
      ? [
        t("sales.quote.creationDate"),
        t("sales.quote.purchaseInvoiceNumber"),
        t("sales.quote.invoiceNumber"),
        t("sales.quote.purchaseOrderNumber"),
        t("sales.quote.supplierName"),
        t("sales.quote.itemsNumber"),
        t("sales.quote.totalPrice"),
        t("sales.quote.notes"),
        t("sales.quote.status"),
        isHPClient ? "" : t("sales.quote.more"),
      ]
      : isQuote
        ? [
          t("sales.quote.createdDate"),
          t("sales.quote.client"),
          t("sales.quote.agent"),
          t("usersSettings.isCustomerSupportRepresentative"),
          t("documentingDesign.TableSetting.number"),
          t("sales.quote.worksName"),
          t("sales.quote.totalPrice"),
          t("sales.quote.notes"),
          t("sales.quote.status"),
          isHPClient ? "" : t("sales.quote.more"),
        ]
        : [
          t("sales.quote.createdDate"),
          isSupplierPayments
            ? t("sales.quote.supplier")
            : t("sales.quote.client"),
          t("sales.quote.agent"),
          t("usersSettings.isCustomerSupportRepresentative"),
          (() => {
            switch (docType) {
              case DOCUMENT_TYPE.order:
                return t("sales.quote.orderNumber");
              default:
                return t("documentingDesign.TableSetting.number");
            }
          })(),
          isOrder
            ? t("sales.quote.quoteNumber")
            : isReceipt || isSupplierPayments
              ? null
              : t("sales.quote.sourceDocument"),

          isOrder && t("sales.quote.purchaseNumber"),
          isOrder && t("sales.quote.productionStatus"),
          isOrder && t("sales.quote.jobs"),
          isOrder && CheckPermission(Permissions.SHOW_COSTS_IN_ORDERS) && (
            <PermissionCheck
              userPermission={Permissions.SHOW_COSTS_IN_ORDERS}
            >
              {t("sales.quote.cost")}
            </PermissionCheck>
          ),
          isReceipt || isSupplierPayments
            ? t("sales.quote.paymentMethod")
            : t("sales.quote.worksName"),
          (isReceipt || isSupplierPayments) && t("sales.quote.checkNumber"),
          (isReceipt || isSupplierPayments) && t("sales.quote.checkDueDate"),
          isOrdersTracking
            ? t("customers.modal.paymentStatus")
            : t("sales.quote.totalPrice"),
          t("sales.quote.notes"),
          (isOrder ||
            isInvoice ||
            isInvoiceRefund ||
            isDeliveryNote ||
            isDeliveryNoteRefund) &&
          t("sales.documentDeliveries.deliveryStatus"),
          t("sales.quote.status"),
          isHPClient ? "" : t("sales.quote.more"),
        ].filter(Boolean);
  const tableHeaders = showCustomerSupportRepresentative
    ? tableHeadersRaw
    : tableHeadersRaw.filter((header) => header !== csrHeaderLabel);

  const logsTableHeaders = [
    t("sales.quote.actionDate"),
    t("sales.quote.employeeName"),
    t("sales.quote.actionDescription"),
    t("sales.quote.status"),
  ];

  const quoteStatuses = [
    {
      label: t("sales.quote.create"),
      value: QUOTE_STATUSES.Create,
    },
    {
      label: t("sales.quote.open"),
      value: QUOTE_STATUSES.Open,
    },
    {
      label: t("sales.quote.closed"),
      value: QUOTE_STATUSES.Closed,
    },
    {
      label: t("sales.quote.canceled"),
      value: QUOTE_STATUSES.Canceled,
    },
    {
      label: t("sales.quote.waiting"),
      value: QUOTE_STATUSES.Waiting,
    },
    {
      label: t("sales.quote.approved"),
      value: QUOTE_STATUSES.Approved,
    },
    {
      label: t("sales.quote.canceledIrrelvant"),
      value: QUOTE_STATUSES.CanceledIrrelvant,
    },
    {
      label: t("sales.quote.canceledPrice"),
      value: QUOTE_STATUSES.CanceledPrice,
    },
    {
      label: t("sales.quote.canceledDeliveryTime"),
      value: QUOTE_STATUSES.CanceledDeliveryTime,
    },
    {
      label: t("QuoteStatuses.CANCELED_DUPLICATE"),
      value: QUOTE_STATUSES.CanceledDuplicate,
    },
    {
      label: t("sales.quote.canceledOther"),
      value: QUOTE_STATUSES.CanceledOther,
    },
    {
      label: t("sales.quote.partialClosed"),
      value: QUOTE_STATUSES.PartialClosed,
    },
  ];

  const documentsLabels = [
    {
      label: t("sales.quote.quoteList"),
      value: DOCUMENT_TYPE.quote,
    },
    {
      label: t("sales.quote.orderList"),
      value: DOCUMENT_TYPE.order,
    },
    {
      label: t("sales.quote.invoiceList"),
      value: DOCUMENT_TYPE.invoice,
    },
    {
      label: t("sales.quote.deliveryNoteList"),
      value: DOCUMENT_TYPE.deliveryNote,
    },
    {
      label: t("sales.quote.proformaInvoiceList"),
      value: DOCUMENT_TYPE.proformaInvoice,
    },
    {
      label: t("sales.quote.receiptList"),
      value: DOCUMENT_TYPE.receipt,
    },
    {
      label: t("sales.quote.deliveryNoteRefundList"),
      value: DOCUMENT_TYPE.deliveryNoteRefund,
    },
    {
      label: t("sales.quote.invoiceRefundList"),
      value: DOCUMENT_TYPE.invoiceRefund,
    },
    {
      label: t("sales.quote.purchaseOrderList"),
      value: DOCUMENT_TYPE.purchaseOrder,
    },
    {
      label: t("sales.quote.purchaseInvoiceList"),
      value: DOCUMENT_TYPE.purchaseInvoice,
    },
    {
      label: t("sales.quote.purchaseInvoiceRefundList"),
      value: DOCUMENT_TYPE.purchaseInvoiceRefund,
    },
    {
      label: t("sales.quote.supplierPaymentsList"),
      value: DOCUMENT_TYPE.supplierPayments,
    },
  ];

  const documentLabel = isOrdersTracking
    ? t("tabs.ordersTracking")
    : documentsLabels.find((item) => item.value === docType)?.label;

  const updateQuoteStatus = async (quote?) => {
    const callBack = (res) => {
      if (res?.success) {
        navigate("/quote");
      } else {
        if (res?.statusCode === 85) {
          alertFault(res?.message);
        } else {
          alertFaultUpdate();
        }
      }
    };
    await updateDocumentApi(callApi, callBack, {
      documentType: 0,
      document: {
        documentId: quote?.id ?? selectedQuote?.id,
      },
    });
  };

  const onClickDocumentDuplicate = async (
    id: string,
    withJobNumbers: boolean | null = false,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        const isAnotherQuoteInCreate = res?.data?.isAnotherQuoteInCreate;
        const documentId = res?.data?.documentId;
        if (isPurchaseOrder) {
          navigate(`/purchaseOrder?Id=${documentId}`);
        } else if (!isAnotherQuoteInCreate) {
          navigate("/quote");
        } else {
          onClickOpenModal({ id: documentId });
        }
      } else {
        alertFaultDuplicate();
      }
    };

    await duplicateDocumentApi(callApi, callBack, {
      documentId: id,
      documentType: docType,
      withJobNumbers,
    });
  };

  const onclickCreateNew = async () => {
    const callBack = (res) => {
      if (res?.success) {
        const isAnotherQuoteInCreate = res?.data?.isAnotherQuoteInCreate;
        const documentId = res?.data?.documentId;
        if (!isAnotherQuoteInCreate) {
          navigate("/quote");
        } else {
          onClickOpenModal({ id: documentId });
        }
      } else {
        alertFaultUpdate();
      }
    };
    await createNewDocumentApi(callApi, callBack, { documentType: 0 });
  };

  const onOpenPurchaseOrderTypeModal = () => {
    setOpenPurchaseOrderTypeModal(true);
  };

  const onClosePurchaseOrderTypeModal = () => {
    setOpenPurchaseOrderTypeModal(false);
  };

  const handleSelectPurchaseOrderType = (isForMaterial: boolean) => {
    setOpenPurchaseOrderTypeModal(false);
    const url = isForMaterial
      ? `/${documentPath}?isNewCreation=true&isMaterial=true`
      : `/${documentPath}?isNewCreation=true`;
    navigate(url);
  };

  // table in home page
  /** Modern home list: chip tone for the status column. */
  const modernHomeStatusTone = (document: any): "open" | "closed" | "cancelled" => {
    if (isReceipt) {
      return String(document?.status).toLowerCase().includes("cancel")
        ? "cancelled"
        : "closed";
    }
    if (isInvoice) {
      const s = Number(document?.documentStatus);
      if (s === 1) return "cancelled";
      return [5, 6, 7, 8].includes(s) ? "closed" : "open";
    }
    if (isDeliveryNote) {
      const s = Number(document?.documentStatus);
      if (s === DELIVERY_NOTE_STATUSES.Canceled || s === DELIVERY_NOTE_STATUSES.Rejected)
        return "cancelled";
      return [
        DELIVERY_NOTE_STATUSES.ClosedAsInvoice,
        DELIVERY_NOTE_STATUSES.ClosedByMultiDocuments,
        DELIVERY_NOTE_STATUSES.ManualClose,
      ].includes(s)
        ? "closed"
        : "open";
    }
    const s = Number(document?.documentStatus);
    if (
      [
        QUOTE_STATUSES.Canceled,
        QUOTE_STATUSES.CanceledIrrelvant,
        QUOTE_STATUSES.CanceledPrice,
        QUOTE_STATUSES.CanceledDeliveryTime,
        QUOTE_STATUSES.CanceledOther,
        QUOTE_STATUSES.CanceledDuplicate,
        QUOTE_STATUSES.RejectedByManager,
      ].includes(s)
    )
      return "cancelled";
    return [QUOTE_STATUSES.Closed, QUOTE_STATUSES.PartialClosed].includes(s)
      ? "closed"
      : "open";
  };

  const openDocumentFromHome = (document: any) => {
    if (isQuote) {
      onClickEditQuote(document);
      return;
    }
    navigate(`/${DOCUMENT_TYPE[docType]}?Id=${document?.id}`);
  };

  const canOpenDocumentFromHome = (document: any) =>
    isQuote
      ? Number(document?.documentStatus) === QUOTE_STATUSES.Open ||
        Number(document?.documentStatus) === QUOTE_STATUSES.Create
      : document?.isEditable !== false;

  const getAllDocuments = async (docType) => {
    const callBack = (res) => {
      if (res?.success) {
        const data = res?.data?.data;
        if (isModernHome) {
          const totalItems = Number(res?.data?.totalItems) || 0;
          setHomeTotalItems(totalItems);
          setHomePagesCount(Math.max(1, Math.ceil(totalItems / homePageSize)));
        }
        const mapData = data?.map((document: any, index: number) => {
          const moreMenu = (
            <MoreMenuWidget
              quote={document}
              documentType={docType}
              onClickEditQuote={onClickEditQuote}
              onClickPdf={
                isReceipt || isSupplierPayments
                  ? onOpenExportReceiptPdfModal
                  : onClickQuotePdf
              }
              onClickDuplicate={onClickDocumentDuplicate}
              onClickLoggers={onClickDocumentLogs}
              onClickOpenIrrelevantModal={onClickOpenIrrelevantModal}
              onClickOpenPrintPDFModal={onClickOpenPrintPDFModal}
              onClickOpenDeliveryTimeModal={onClickOpenDeliveryTimeModal}
              onClickOpenDuplicateModal={onClickOpenDuplicateModal}
              onClickOpenPriceModal={onClickOpenPriceModal}
              onClickOpenModal={onClickOpenOtherReasonModal}
              onClickOpenCloseOrderModal={onClickOpenCloseOrderModal}
              onClickOpenCloseOrderNotesModal={onClickOpenCloseOrderNotesModal}
              onClickSendPayment={onClickSendPayment}
              onClickMakePayment={onClickMakePayment}
              onClickCloseAndTrack={onClickOpenTrackModal}
              onClickOpenChangeProductionStatusModal={
                onClickOpenChangeProductionStatusModal
              }
              onClickReopenDocument={onClickOpenReopenDocumentModal}
              onClickOpenMoveOrderToReadyModal={
                onClickOpenMoveOrderToReadyModal
              }
              onOpenPackagesModal={onOpenPackagesModal}
              onOpenDuplicateModal={onOpenDuplicateModal}
              onClickOpenInventoryIntakeModal={onClickOpenInventoryIntakeModal}
              onClickUndoDocumentIntake={handleUndoDocumentIntake}
              onClickSendToCustomerEmail={onClickSendToCustomerEmail}
              onClickSendToCustomerSMS={onClickSendToCustomerSMS}
              onClickSendToCustomerWhatsApp={onClickSendToCustomerWhatsApp}
              onClickSendPaymentEmail={onClickSendPaymentEmail}
              onClickSendPaymentSMS={onClickSendPaymentSMS}
              onClickSendPaymentWhatsApp={onClickSendPaymentWhatsApp}
              onClickOpenUploadFilesModal={onClickOpenUploadFilesModal}
            />
          );

          if (isModernHome) {
            const price = isRTL
              ? `${document?.totalPrice ?? ""} ${getCurrencyUnitText(document?.currency)}`
              : `${getCurrencyUnitText(document?.currency)} ${document?.totalPrice ?? ""}`;
            const dateCell = isReceipt
              ? document?.creationDate?.split("T")[0]
              : formatCustomDateTime(document?.createdDate);
            const tone = modernHomeStatusTone(document);
            const modernRow: any[] = [
              <span key="idx" className="gm-doclist-index">
                {(page - 1) * homePageSize + index + 1}
              </span>,
              canOpenDocumentFromHome(document) ? (
                <button
                  key="num"
                  type="button"
                  className="gm-doclist-number"
                  onClick={() => openDocumentFromHome(document)}
                >
                  {document?.number}
                </button>
              ) : (
                <span key="num" className="gm-doclist-number is-static">
                  {document?.number}
                </span>
              ),
              isReceipt
                ? document?.paymentType || "—"
                : document?.worksNames || "—",
              dateCell,
              ...(isQuote || isOrder
                ? [
                    document?.dueDate
                      ? formatCustomDateTime(document?.dueDate)
                      : "—",
                  ]
                : []),
              ...(isReceipt ? [] : [document?.clientType || "—"]),
              <span key="price" className="gm-doclist-price">
                {price}
              </span>,
              <span
                key="status"
                className={`gm-doclist-status gm-doclist-status--${tone}`}
              >
                {renderStatusCell(document)}
              </span>,
              ...(isOrder
                ? [
                    document?.productionStatus === true
                      ? t("boardMissions.done")
                      : t("boardMissions.inProduction"),
                  ]
                : []),
              <div key="more" className="gm-doclist-actions">
                {moreMenu}
              </div>,
            ];
            return modernRow;
          }

          const baseRow = [
            <div
              style={{ cursor: "pointer" }}
              onClick={() => onClickOpenCustomerModal(document?.customerId)}
            >
              {document?.customerName}
            </div>,
            document?.agentName,
            ...(showCustomerSupportRepresentative
              ? [document?.customerSupportRepresentativeName]
              : []),
            document?.number,
            document?.clientType,
            document?.worksNames,
            isReceipt
              ? document?.creationDate?.split("T")[0]
              : formatCustomDateTime(document?.createdDate),
            isRTL
              ? document?.totalPrice +
              " " +
              getCurrencyUnitText(document?.currency)
              : getCurrencyUnitText(document?.currency) +
              " " +
              document?.totalPrice,
            <div
              style={{
                display: "flex",
                justifyContent: isHPClient ? "flex-start" : "center",
              }}
            >
              <h2
                style={
                  isHPClient
                    ? {
                      ...(document?.documentStatus == 2
                        ? classes.openBtnStyle
                        : classes.closeBtnStyle),
                      justifyContent: "center",
                      color: "#101020",
                    }
                    : document?.documentStatus == 2
                      ? classes.openBtnStyle
                      : classes.closeBtnStyle
                }
              >
                {renderStatusCell(document)}
              </h2>
            </div>,
          ];

          if (isOrder) {
            baseRow.push(
              document?.productionStatus === true
                ? t("boardMissions.done")
                : t("boardMissions.inProduction"),
            );
          }

          baseRow.push(moreMenu);

          return baseRow;
        });

        setAllDocuments(mapData);
      }
    };
    if (!router.isReady) {
      return;
    }
    if (!selectedClient?.id) {
      return;
    }
    if (!isModernHome) {
      await getAllDocumentsApi(callApi, callBack, {
        documentType: docType,
        data: {
          model: {
            pageNumber: page,
            pageSize: 10,
          },
          customerId: selectedClient?.id,
        },
      });
      return;
    }

    // Modern home: same list, but filterable (search / dates / status / products)
    // and paginated. Receipts go through the receipts endpoint, which is the only
    // one that honours search + status for that document type.
    const search =
      finalPatternSearch !== null &&
      finalPatternSearch !== undefined &&
      String(finalPatternSearch).trim() !== ""
        ? String(finalPatternSearch).trim()
        : undefined;
    const hasAccountingStatus =
      accountingStatus?.value !== undefined && accountingStatus?.value !== null;
    const homeModel = { pageNumber: page, pageSize: homePageSize };
    setIsDocumentTableLoading(true);
    try {
      if (isReceipt) {
        await getAllReceiptsApi(callApi, callBack, {
          clientId: selectedClient.id,
          ...(search ? { patternSearch: search } : {}),
          ...(hasAccountingStatus ? { status: accountingStatus.value } : {}),
          ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
          ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
          model: homeModel,
        });
      } else {
        await getAllDocumentsApi(callApi, callBack, {
          documentType: docType,
          data: {
            model: homeModel,
            customerId: selectedClient.id,
            ...(search ? { patternSearch: search } : {}),
            ...(statusId?.value ? { statusId: statusId.value } : {}),
            ...(hasAccountingStatus ? { closeStatus: accountingStatus.value } : {}),
            ...(deliveryStatus?.value ? { deliveryStatus: deliveryStatus.value } : {}),
            ...(fromDate ? { fromDate: GetDateFormat(fromDate) } : {}),
            ...(toDate ? { toDate: GetDateFormat(toDate) } : {}),
            ...(productIds?.length ? { productList: productIds } : {}),
          },
        });
      }
    } finally {
      setIsDocumentTableLoading(false);
    }
  };

  const homeStatusValue = isQuote || isOrder
    ? statusId
    : isDeliveryNote
      ? deliveryStatus
      : accountingStatus;

  const setHomeStatus = (value: any) => {
    setPage(1);
    if (isQuote || isOrder) setStatusId(value ?? undefined);
    else if (isDeliveryNote) setDeliveryStatus(value ?? null);
    else setAccountingStatus(value ?? null);
  };

  const applyHomeSearch = (value: string) => {
    setPatternSearch(value);
    setFinalPatternSearch(value);
    setPage(1);
  };

  const onSelectHomeDates = (value) => {
    setPage(1);
    onSelectDeliveryTimeDates(value);
  };

  const setHomeProducts = (values: string[]) => {
    setPage(1);
    handleMultiSelectChange(values);
  };

  const clearHomeFilters = () => {
    setPatternSearch("");
    setFinalPatternSearch("");
    setStatusId(undefined);
    setAccountingStatus(null);
    setDeliveryStatus(null);
    setFromDate(undefined);
    setToDate(undefined);
    setProductIds([]);
    setPage(1);
  };

  const hasHomeFilters =
    !!(finalPatternSearch && String(finalPatternSearch).trim()) ||
    !!statusId ||
    !!accountingStatus ||
    !!deliveryStatus ||
    !!fromDate ||
    !!toDate ||
    productIds.length > 0;

  const handleHomePageSizeChange = (event: any) => {
    const next = Number(event?.target?.value) || 10;
    setHomePageSize(next);
    setPage(1);
  };

  const handleCardClick = (cardKey, statusValue) => {
    setPage(1);
    setActiveCard(cardKey);
    setStatusId(null);
    setQuoteStatusId(null);

    // Map statistics cards to approval status values
    let approvalStatusValue = null;
    if (cardKey === EStatisticsLabels.WAITING) {
      approvalStatusValue = ApprovalStatus.WaitingForClient;
    } else if (cardKey === EStatisticsLabels.APPROVED) {
      approvalStatusValue = ApprovalStatus.ApprovedByClient;
    } else if (cardKey === EStatisticsLabels.CANCELED) {
      approvalStatusValue = ApprovalStatus.RejectedByClient;
    }

    // Set approval status filter
    if (approvalStatusValue) {
      // Create approval status object directly using the enum value and translation
      const approvalStatus = {
        value: approvalStatusValue,
        label: t(`documentStatus.${ApprovalStatus[approvalStatusValue]}`),
      };
      // Set flag to trigger auto-search after state update
      shouldAutoSearchRef.current = true;
      setApprovalStatusId(approvalStatus);
    } else {
      shouldAutoSearchRef.current = true;
      setApprovalStatusId(null);
    }
  };

  const handleSecondCardClick = () => {
    setQuoteStatusId(undefined);
    setActiveCard(null);
    // Set flag to trigger auto-search after clearing the filter
    shouldAutoSearchRef.current = true;
    setApprovalStatusId(null);
  };

  const onSelectDeliveryTimeDates = (value) => {
    if (value && value.length === 2) {
      const [firstDate, secondDate] = value;

      // Handle null values
      if (!firstDate || !secondDate) {
        setFromDate(null);
        setToDate(null);
        return;
      }

      // Ensure fromDate is always the earlier date and toDate is always the later date
      if (firstDate <= secondDate) {
        setFromDate(firstDate);
        setToDate(secondDate);
      } else {
        setFromDate(secondDate);
        setToDate(firstDate);
      }
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  const onSelectCheckDueDateRange = (value) => {
    if (value && value.length === 2) {
      const [firstDate, secondDate] = value;

      // Handle null values
      if (!firstDate || !secondDate) {
        setCheckFromDate(null);
        setCheckToDate(null);
        return;
      }

      // Ensure checkFromDate is always the earlier date and checkToDate is always the later date
      if (firstDate <= secondDate) {
        setCheckFromDate(firstDate);
        setCheckToDate(secondDate);
      } else {
        setCheckFromDate(secondDate);
        setCheckToDate(firstDate);
      }
    } else {
      setCheckFromDate(null);
      setCheckToDate(null);
    }
  };

  /** Modern home list headers (customer is fixed, so no client/agent columns). */
  const tableHomeHeaderModern = [
    "#",
    t("home.headers.documentNumber"),
    isReceipt
      ? t("modernHome.paymentType", { defaultValue: "Payment type" })
      : t("home.headers.jobName"),
    t("home.headers.productDate"),
    ...(isQuote || isOrder
      ? [
          isQuote
            ? t("modernHome.validUntil", { defaultValue: "Valid until" })
            : t("modernHome.dueDate", { defaultValue: "Due date" }),
        ]
      : []),
    ...(isReceipt ? [] : [t("home.headers.clientType")]),
    t("home.headers.finalPrice"),
    t("home.headers.status"),
    ...(isOrder ? [t("boardMissions.productionStatus")] : []),
    "",
  ];

  const tableHomeHeader = [
    t("sales.quote.client"),
    t("sales.quote.agent"),
    ...(showCustomerSupportRepresentative
      ? [t("usersSettings.isCustomerSupportRepresentative")]
      : []),
    t("home.headers.documentNumber"),
    t("home.headers.clientType"),
    t("home.headers.jobName"),
    t("home.headers.productDate"),
    t("home.headers.finalPrice"),
    t("home.headers.status"),
    ...(isOrder ? [t("boardMissions.productionStatus")] : []),
    isHPClient ? "" : t("home.headers.more"),
  ];

  const onSelectDateRange = (fromDate: Date, toDate: Date) => {
    setResetLogsDatePicker(false);
    setFromLogsDate(fromDate);
    setToLogsDate(toDate);
  };

  const handleSelectEmployee = (e: any, value: any) => {
    setEmployeeId(value);
  };

  ////////////// LOGS //////////////

  const getLogDescription = (
    logAction: string,
    values: any,
    documentType: DOCUMENT_TYPE,
  ): string => {
    switch (logAction) {
      case LogActionType[1]:
        return `${t("logs.theWorkMission")} "${values[0]}" ${t(
          "logs.ITEM_ADD",
        )}`;
      case LogActionType[2]:
        return `${t("logs.theWorkMission")} "${values[0]}" ${t(
          "logs.ITEM_UPADTED",
        )}`;
      case LogActionType[3]:
        return `${t("logs.theWorkMission")} "${values[0]}" ${t(
          "logs.ITEM_DELETED",
        )}`;
      case LogActionType[4]:
        return `${t("logs.ITEM_PRICE_UPDATE")} : "${values[0]}" ${t(
          "logs.from",
        )} ${parseFloat(values[1]).toFixed(2)} ${t("logs.to")} ${parseFloat(
          values[2],
        ).toFixed(2)}`;
      case LogActionType[5]:
        return `${t("logs.DOCUMENT_PRICE_UPDATE")} ${t(
          "logs.from",
        )} ${parseFloat(values[0]).toFixed(2)} ${t("logs.to")} ${parseFloat(
          values[1],
        ).toFixed(2)}`;
      case LogActionType[6]:
        if (values[0] === "" || null) {
          return `${t("logs.DOCUMENT_DISCUOUNT_UPDATE")} ${t(
            "logs.from",
          )} ${0.0} ${t("logs.to")} ${parseFloat(values[1]).toFixed(2)}`;
        } else {
          return `${t("logs.DOCUMENT_DISCUOUNT_UPDATE")} ${t(
            "logs.from",
          )} ${parseFloat(values[0]).toFixed(2)} ${t("logs.to")} ${parseFloat(
            values[1],
          ).toFixed(2)}`;
        }
      case LogActionType[7]:
        return t("logs.ADDRESS_UPDATED");
      case LogActionType[8]:
        return t("logs.CONTACTS_UPDATED");
      case LogActionType[9]:
        return `${t("logs.CLIENT_UPDATED")} ${t("logs.from")} "${values[0]
          }" ${t("logs.to")} "${values[1]}"`;
      case LogActionType[10]:
        if (values[0] === null) {
          return `${t("logs.AGENT_UPDATED")} ${t("logs.to")} ${values[1]}`;
        } else {
          return `${t("logs.AGENT_UPDATED")} ${t("logs.from")} ${values[0]} ${t(
            "logs.to",
          )} ${values[1]}`;
        }
      case LogActionType[11]:
        if (values[0] === null) {
          return `${t("logs.PURCHASE_NUMBER_UPDATE")} ${t("logs.to")} "${values[1]
            }"`;
        } else {
          return `${t("logs.PURCHASE_NUMBER_UPDATE")} ${t("logs.from")} ${values[0]
            } ${t("logs.to")} ${values[1]}`;
        }
      case LogActionType[12]:
        if (values[0] === null) {
          return `${t("logs.COMMENTS_UPDATE")} ${t("logs.to")} "${values[1]}"`;
        } else {
          return `${t("logs.COMMENTS_UPDATE")} ${t("logs.from")} "${values[0]
            }" ${t("logs.to")} "${values[1]}"`;
        }
      case LogActionType[13]:
        return `${t("logs.DOCUMENT_DUPLICATED", {
          documentType: t(`sales.quote.${DOCUMENT_TYPE[documentType]}`),
          documentNumber: values[0],
        })}`;
      case LogActionType[14]:
        return `${t("logs.DOCUMENT_DUPLICATED_FROM_ORDER", {
          documentNumber: values[0],
        })}`;
      // New cases
      case LogActionType[15]: // SEND_MAIL
        return `${t("logs.SEND_MAIL")} ${t("logs.to")} "${values.join(" , ")}"`;
      case LogActionType[16]: // SEND_SMS
        return `${t("logs.SEND_SMS")} ${t("logs.from")} "${values.join(
          " , ",
        )}"`;
      case LogActionType[17]:
        return `${t("logs.PRINTING_PDF")}`;
      case LogActionType[18]:
        return `${t("logs.DOCUMENT_CANCEL")}`;
      case LogActionType[19]:
        return `${t("logs.REJECTED_BY_CLIENT")}`;
      case LogActionType[20]:
        return `${t("logs.APPROVAL_BY_CLIENT")}`;
      case LogActionType[21]:
        return `${t("logs.ADD")}`;
      case LogActionType[22]:
        if (values[0] === "DeliveryNote" || values[0] === "Invoice") {
          return `${t("logs.CLOSE2", {
            documentType:
              values[0] === "DeliveryNote"
                ? t("sales.quote.deliveryNote")
                : t("sales.quote.invoice"),
          })}`;
        } else {
          return `${t("logs.CLOSE")}`;
        }
      case LogActionType[23]:
        return `${t("logs.SEND_READY_BOARDMISSION_MAIL")}`;
      case LogActionType[24]:
        return `${t("logs.SEND_READY_BOARDMISSION_SMS")}`;
      case LogActionType[25]:
        return `${t("logs.SEND_READY_ORDER_MAIL")}`;
      case LogActionType[26]:
        return `${t("logs.SEND_READY_ORDER_SMS")}`;
      case LogActionType[27]:
        return `${t("logs.PAYMENT_LINK_GENERATED")}`;
      case LogActionType[28]:
        return `${t("logs.PAYMENT_SUCCESS")}`;
      case LogActionType[29]:
        return `${t("logs.PAYMENT_FAILED")}`;
      case LogActionType[30]:
        return `${t("logs.MOVE_JOB_TO_READY", {
          jobNumber: values[0],
        })}`;
      case LogActionType[31]:
        return `${t("logs.CURRENCY_UPDATE", {
          from: values[0],
          to: values[1],
        })}`;
      case LogActionType[32]:
        return `${t("logs.ITEM_DETAILS_UPDATE")}`;
      case LogActionType[33]:
        return `${t("logs.ITEM_SELECTED")}`;
      case LogActionType[34]:
        return `${t("logs.ITEM_UNSELECTED")}`;
      case LogActionType[35]:
        return `${t("logs.DOCUMENT_TITLE_UPDATE")}`;
      case LogActionType[36]:
        return `${t("logs.DELIVERY_ADDRESS_ADD")}`;
      case LogActionType[37]:
        return `${t("logs.BILLING_ADDRESS_ADD")}`;
      case LogActionType[38]:
        return `${t("logs.DELIVERY_ADDRESS_UPDATED")}`;
      case LogActionType[39]:
        return `${t("logs.BILLING_ADDRESS_UPDATED")}`;
      case LogActionType[40]:
        return `${t("logs.DELIVERY_ADDRESS_REMOVE")}`;
      case LogActionType[41]:
        return `${t("logs.BILLING_ADDRESS_REMOVE")}`;
      case LogActionType[42]:
        return `${t("logs.DOCUMENT_APPROVAL_BY_MANAGER")}`;
      case LogActionType[43]:
        return `${t("logs.DOCUMENT_OPENED_BY_EMPLOYEE")}`;
      case LogActionType[44]:
        return `${t("logs.DELIVERY_ITEM_ADD")}`;
      case LogActionType[45]:
        return `${t("logs.FULLY_DELIVERED")}`;
      case LogActionType[46]:
        return `${t("logs.PARTIAL_DELIVERED")}`;
      case LogActionType[47]:
        return `${t("logs.REMOVE_DELIVERED")}`;
      case LogActionType[48]:
        return `${t("logs.BACK_JOB_TO_PRODUCTION")}`;
      case LogActionType[49]:
        return `${t("logs.PACKAGING_SLIP_PRINTED_FOR_JOB")}`;
      case LogActionType[50]:
        return `${t("logs.PACKAGING_SLIP_PRINTED_FOR_ORDER")}`;
      case LogActionType[51]:
        return `${t("logs.DOCUMENT_DELIVERY_DATE_UPDATE")} ${t("logs.from")} "${values[0]}" ${t("logs.to")} "${values[1]}"`;
      case LogActionType[52]:
        return `${t("logs.TAX_CONFIRMATION_NUMBER_UPDATE")}`;
      case LogActionType[LogActionType.SEND_FRONTDESK]: {
        // values: [customer destination, provider, inbox name]
        const [destination, provider, inbox] = values ?? [];
        const via = [provider, inbox].filter(Boolean).join(" · ");
        return `${t("logs.SEND_FRONTDESK")}${destination ? ` ${t("logs.to")} "${destination}"` : ""}${via ? ` (${via})` : ""}`;
      }
      default:
        return "";
    }
  };

  const getMessageStatus = (status: number) => {
    const message = t(`messageStatus.${EMessageStatus[status]}`);
    return EMessageStatus[status] ? message : "";
  };

  const getAllDocumentLogs = (
    documentId?: string,
    isClear: boolean = false,
  ): Promise<void> => {
    setDocumentIdLogState(documentId);
    return new Promise(async (resolve, reject) => {
      const callBack = (res) => {
        if (res?.success) {
          const mapData = res?.data?.map((log: any) => [
            formatISODateTime(log?.actionDate),
            log?.employeeName,
            getLogDescription(log?.logAction, log?.values, log?.documentType),
            getMessageStatus(log?.messageStatus),
          ]);
          setDocumentLogsData(mapData);
          resolve();
        } else {
          alertFaultGetData();
          setDocumentIdLogState("");
          reject();
        }
      };
      try {
        await getAllDocumentLogsApi(callApi, callBack, {
          documentType: docType,
          documentId: documentId,
          data: isClear
            ? {
              fromDate: fromLogsDate && GetDateFormat(fromLogsDate),
              toDate: toLogsDate && GetDateFormat(toLogsDate),
            }
            : {
              userId: employeeId?.userId,
              fromDate: fromLogsDate && GetDateFormat(fromLogsDate),
              toDate: toLogsDate && GetDateFormat(toLogsDate),
            },
        });
      } catch (error) {
        reject();
      }
    });
  };

  const onClickClearLogsFilter = () => {
    setEmployeeId(null);
    getAllDocumentLogs(documentIdLogState, true);
  };

  const onClickSearchLogsFilter = () => {
    getAllDocumentLogs(documentIdLogState);
  };

  useEffect(() => {
    if (!isFromHomePage) {
      // this for client type list in customer modal
      getClientTypesCategories();
    }
  }, []);

  useEffect(() => {
    if (quoteNumber) {
      setPage(1);
      const pattern = quoteNumber?.toString();
      setPatternSearch(pattern);
      setFinalPatternSearch(pattern);
    }
  }, [quoteNumber, query]);

  // For HomeTableWidget
  useEffect(() => {
    if (isFromHomePage && !isModernHome) {
      getAllDocuments(docType);
    }
  }, [selectedClient]);

  // Modern home: refetch on every filter / paging change; a customer switch
  // resets paging (and the export helpers read the customer from `customerId`).
  const modernHomeClientRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isModernHome) return;
    const clientId = selectedClient?.id;
    if (modernHomeClientRef.current !== clientId) {
      modernHomeClientRef.current = clientId;
      setCustomerId(
        clientId ? { id: clientId, name: selectedClient?.name } : undefined,
      );
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    getAllDocuments(docType);
  }, [
    selectedClient?.id,
    router.isReady,
    page,
    homePageSize,
    finalPatternSearch,
    statusId,
    accountingStatus,
    deliveryStatus,
    fromDate,
    toDate,
    productIds,
  ]);

  const getAllProducts = useCallback(async () => {
    const products = await getAllProductsForDropDownList(
      callApi,
      setProductsList,
    );
    setProductsList(
      products.map(({ id, name }) => ({ label: name, value: id })),
    );
  }, []);

  const handleMinPriceChange = (e) => {
    setMinPrice(e.target.value);
  };

  const handleMaxPriceChange = (e) => {
    setMaxPrice(e.target.value);
  };

  const handleMultiSelectChange = (newValues: string[]) => {
    setProductIds(newValues);
  };

  const productionStatuses = [
    { label: t("boardMissions.inProduction"), value: PStatus.IN_PROCESS },
    { label: t("boardMissions.done"), value: PStatus.DONE },
  ];

  const accountingStatuses =
    isReceipt || isSupplierPayments
      ? [
        {
          label: t("documentStatus.Receipt.Created"),
          value: ReceiptAccountingStatus.CREATED,
        },
        {
          label: t("documentStatus.Receipt.Canceled"),
          value: ReceiptAccountingStatus.CANCELED,
        },
      ]
      : isInvoice || isPurchaseInvoice
        ? [
          { label: t("sales.quote.open"), value: AStatus.OPEN },
          {
            label: t("sales.quote.partialClosed"),
            value: AStatus.PARTIAL_CLOSED,
          },
          { label: t("sales.quote.closed"), value: AStatus.CLOSED },
          { label: t("sales.quote.draft"), value: AStatus.DRAFT },
        ]
        : isPurchaseOrder
          ? [
            {
              label: t("documentStatus.PurchaseOrder.UnderCreation"),
              value: DocumentCloseStatus.Open,
            },
            {
              label: t("sales.quote.partialClosed"),
              value: DocumentCloseStatus.PartialClosed,
            },
            {
              label: t("sales.quote.closed"),
              value: DocumentCloseStatus.Closed,
            },
            // {
            //   label: t("documentStatus.PurchaseOrder.Draft"),
            //   value: DocumentCloseStatus.Draft
            // },
            {
              label: t("documentStatus.PurchaseOrder.SentToSupplier"),
              value: DocumentCloseStatus.SentToSupplier,
            },
            {
              label: t("documentStatus.PurchaseOrder.Received"),
              value: DocumentCloseStatus.Received,
            },
            {
              label: t("documentStatus.PurchaseOrder.PartiallyReceived"),
              value: PurchaseOrderStatuses.PartiallyReceived,
            },
            {
              label: t(
                "documentStatus.PurchaseOrder.SupplierRequestingApproval",
              ),
              value: DocumentCloseStatus.SupplierRequestingApproval,
            },
          ]
          : [
            {
              label: t("sales.quote.open"),
              value: AStatus.OPEN,
            },
            {
              label: t("sales.quote.partialClosed"),
              value: AStatus.PARTIAL_CLOSED,
            },
            { label: t("sales.quote.closed"), value: AStatus.CLOSED },
          ];

  const deliveryStatuses = useMemo(
    () => [
      {
        label: t("sales.documentDeliveries.NotDelivered"),
        value: DocumentDeliveryStatuses.NotDelivered,
      },
      {
        label: t("sales.documentDeliveries.PartiallyDelivered"),
        value: DocumentDeliveryStatuses.PartiallyDelivered,
      },
      {
        label: t("sales.documentDeliveries.FullyDelivered"),
        value: DocumentDeliveryStatuses.FullyDelivered,
      },
    ],
    [t],
  );

  /** Modern home: status options for the active document type. */
  const homeStatusOptions = useMemo(() => {
    if (isQuote || isOrder) return quoteStatuses;
    if (isDeliveryNote) return deliveryStatuses;
    return accountingStatuses;
  }, [isQuote, isOrder, isDeliveryNote, deliveryStatuses, t]);

  const approvalStatuses: { value: number; label: string }[] = Object.values(
    ApprovalStatus,
  )
    .filter((value) => typeof value === "number")
    .map((value) => ({
      value: value as number,
      label: t(`documentStatus.${ApprovalStatus[value]}`),
    }));

  const handleProductionStatusChange = (e: any, value: any) => {
    setProductionStatus(value);
  };

  const handleAccountingStatusChange = (e: any, value: any) => {
    setAccountingStatus(value);
  };

  const [filterData, setFilterData] = useState({});
  const [filterDTO, setFilterDTO] = useState({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const removeEmptyValues = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0),
      ),
    );
  };

  useEffect(() => {
    const filteredData = {
      statusId: quoteStatusId?.value || statusId?.value,
      approvalStatus: approvalStatusId?.value,
      closeStatus: accountingStatus?.value && accountingStatus?.value,
      deliveryStatus: deliveryStatus?.value && deliveryStatus?.value,
      productionStatus: productionStatus?.value && productionStatus?.value,
      patternSearch: finalPatternSearch && finalPatternSearch,
      customerId: customerId?.id && customerId?.id,
      dateRange: dateRange && dateRange,
      agentId: agentId?.id && agentId?.id,
      customerSupportRepresentativeId:
        customerSupportRepresentativeId?.id &&
        customerSupportRepresentativeId?.id,
      minPrice: minPrice && minPrice,
      maxPrice: maxPrice && maxPrice,
      productList: productIds && productIds,
      printHouseMaterialIds:
        printHouseMaterialIds?.length > 0 ? printHouseMaterialIds : undefined,
      fromDate: fromDate,
      toDate: toDate,
      currency: currencyFilter?.value,
      checkFromDate:
        isReceipt || isSupplierPayments ? checkFromDate : undefined,
      checkToDate: isReceipt || isSupplierPayments ? checkToDate : undefined,
      // paymentType: paymentType?.value && paymentType?.value,
    };

    const filteredDataWithoutEmptyValues = removeEmptyValues(filteredData);

    if (
      JSON.stringify(filteredDataWithoutEmptyValues) !==
      JSON.stringify(filterData)
    ) {
      const sortOrder = currentSortOrder;

      setFilterData(filteredDataWithoutEmptyValues);
      setFilterDTO({
        model: {
          pageNumber: page,
          pageSize: pageSize,
          order: sortOrder,
        },
        ...filteredDataWithoutEmptyValues,
      });
    }
  }, [
    quoteStatusId,
    approvalStatusId,
    statusId,
    accountingStatus,
    deliveryStatus,
    productionStatus,
    finalPatternSearch,
    customerId,
    dateRange,
    agentId,
    customerSupportRepresentativeId,
    minPrice,
    maxPrice,
    productIds,
    printHouseMaterialIds,
    fromDate,
    toDate,
    checkFromDate,
    checkToDate,
    paymentType,
    currencyFilter,
    currentSortOrder,
    page,
    pageSize,
    isSupplierPayments,
  ]);

  const [openIrrelevantCancelModal, setOpenIrrelevantCancelModal] =
    useState(false);
  const [openPriceCancelModal, setOpenPriceCancelModal] = useState(false);
  const [openCloseOrderModal, setOpenCloseOrderModal] = useState(false);
  const [openDeliveryTimeCancelModal, setOpenDeliveryTimeCancelModal] =
    useState(false);
  const [openDuplicateCancelModal, setOpenDuplicateCancelModal] =
    useState(false);
  const [openOtherReasonModal, setOpenOtherReasonModal] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [openStartProductionModal, setOpenStartProductionModal] =
    useState(false);

  const [openReopenDocModal, setOpenReopenDocModal] = useState(false);

  // Use this state when opening any modal from the "More" menu list.
  const [selectedDocument, setSelectedDocument] = useState<any>({});

  const onClickOpenIrrelevantModal = (order) => {
    setSelectedDocument(order);
    setOpenIrrelevantCancelModal(true);
  };

  const onClickCloseIrrelevantModal = () => {
    setOpenIrrelevantCancelModal(false);
  };

  const onClickOpenPriceModal = (order) => {
    setSelectedDocument(order);
    setOpenPriceCancelModal(true);
  };
  const onClickClosePriceModal = () => {
    setSelectedDocument(null);
    setOpenPriceCancelModal(false);
  };

  const onClickOpenCloseOrderModal = (order) => {
    setSelectedDocument(order);
    setOpenCloseOrderModal(true);
  };
  const onClickCloseCloseOrderModal = () => {
    setOpenCloseOrderModal(false);
    setDocumentCloseNumber("");
  };

  const onClickOpenReopenDocumentModal = (document) => {
    setSelectedDocument(document);
    setOpenReopenDocModal(true);
  };

  const onClickCloseReopenDocumentModal = () => {
    setSelectedDocument(null);
    setOpenReopenDocModal(false);
  };

  const onClickOpenStartProductionModal = (order) => {
    setSelectedDocument(order);
    setOpenStartProductionModal(true);
  };

  const onClickCloseStartProductionModal = () => {
    setSelectedDocument(null);
    setOpenStartProductionModal(false);
  };

  const onClickOpenDeliveryTimeModal = (order) => {
    setSelectedDocument(order);
    setOpenDeliveryTimeCancelModal(true);
  };
  const onClickCloseDeliveryTimeModal = () => {
    setSelectedDocument(null);
    setOpenDeliveryTimeCancelModal(false);
  };

  const onClickOpenDuplicateModal = (order) => {
    setSelectedDocument(order);
    setOpenDuplicateCancelModal(true);
  };
  const onClickCloseDuplicateModal = () => {
    setSelectedDocument(null);
    setOpenDuplicateCancelModal(false);
  };

  const onClickOpenOtherReasonModal = (order) => {
    setSelectedDocument(order);
    setReasonText("");
    setOpenOtherReasonModal(true);
  };
  const onClickCloseOtherReasonModal = () => {
    setSelectedDocument(null);
    setReasonText("");
    setOpenOtherReasonModal(false);
  };

  const onClickCancelOffer = async () => {
    await updateCancelQuote(QUOTE_STATUSES.CanceledOther, reasonText);
  };

  // Inventory Intake Modal State
  const [openInventoryIntakeModal, setOpenInventoryIntakeModal] =
    useState(false);
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  const onClickOpenInventoryIntakeModal = async (quote) => {
    setSelectedDocument(quote);
    setOpenInventoryIntakeModal(true);
    setInventoryLoading(true);

    const purchaseOrderId = quote?.id;
    const inventoryParams = {
      documentType: docType,
      documentId: purchaseOrderId,
      purchaseOrderId,
    };

    const inventoryRes = await new Promise<any>((resolve) => {
      void getDocumentInventoryApi(callApi, resolve, inventoryParams);
    });

    if (inventoryRes?.success) {
      setInventoryData(inventoryRes?.data || []);
    } else {
      alertFaultGetData();
    }

    setInventoryLoading(false);
  };

  const onClickCloseInventoryIntakeModal = () => {
    setOpenInventoryIntakeModal(false);
    setInventoryData([]);
    setSelectedDocument(null);
  };

  const MoveOrderToReady = useModal();

  const onClickOpenMoveOrderToReadyModal = (order) => {
    const orderId =
      order?.id ??
      order?.orderId ??
      deliveryOrderId ??
      router.query.orderId;
    const resolvedOrderId = Array.isArray(orderId) ? orderId[0] : orderId;
    const normalizedOrder = normalizeOrderForPackages(order, resolvedOrderId);

    pendingMoveToDoneOrderIdRef.current = resolvedOrderId
      ? String(resolvedOrderId)
      : null;
    pendingMoveToDoneOrderRef.current = normalizedOrder;
    setSelectedDocument(normalizedOrder ?? {});
    MoveOrderToReady.open();
  };

  const onClickCloseMoveOrderToReadyModal = () => {
    setSelectedDocument({});
    MoveOrderToReady.close();
    moveOrderToDoneModalOpenedRef.current = false;
    pendingMoveToDoneOrderIdRef.current = null;
    pendingMoveToDoneOrderRef.current = null;
  };

  const fetchOrderDocumentDetails = async (orderId: string) =>
    new Promise<any | null>((resolve) => {
      getDocumentApi(
        callApi,
        (res) => {
          resolve(res?.success ? res.data : null);
        },
        {
          Id: orderId,
          documentType: docType,
        },
      );
    });

  const resolveOrderDocumentForPackages = async (
    orderId: string,
    orderNumber?: string | null,
    urlTotalOrderQuantity?: string | null,
  ) => {
    const quantityFromUrl =
      urlTotalOrderQuantity != null && urlTotalOrderQuantity !== ""
        ? Number(urlTotalOrderQuantity)
        : 0;

    const pendingOrder = pendingMoveToDoneOrderRef.current;
    const pendingOrderId = pendingOrder?.id ?? pendingOrder?.orderId;
    const pendingOrderMatchesRequest =
      pendingOrder &&
      orderId &&
      String(pendingOrderId) === String(orderId);

    let orderDocument = pendingOrderMatchesRequest
      ? pendingOrder
      : normalizeOrderForPackages(
          findOrderDocumentForMoveToDone(originalData, orderId, orderNumber),
          orderId,
        ) ??
        normalizeOrderForPackages(
          { totalOrderQuantity: quantityFromUrl > 0 ? quantityFromUrl : undefined },
          orderId,
        );

    if (!orderDocument?.totalOrderQuantity && orderId) {
      const details = await fetchOrderDocumentDetails(orderId);
      if (details) {
        orderDocument = normalizeOrderForPackages(
          { ...orderDocument, ...details },
          orderId,
        );
        pendingMoveToDoneOrderRef.current = orderDocument;
      }
    }

    return orderDocument;
  };

  useEffect(() => {
    if (!router.isReady || !isOrder) {
      moveOrderToDoneModalOpenedRef.current = false;
      return;
    }

    const shouldOpenModal = router.query.isOpenMoveToDoneModal === "true";
    const orderIdParam = router.query.orderId;

    if (!shouldOpenModal || !orderIdParam) {
      moveOrderToDoneModalOpenedRef.current = false;
      return;
    }

    if (originalData === null || moveOrderToDoneModalOpenedRef.current) {
      return;
    }

    const orderId = Array.isArray(orderIdParam) ? orderIdParam[0] : orderIdParam;
    if (!orderId) {
      return;
    }

    const orderNumberParam =
      router.query.finalPatternSearch || router.query.orderNumber;
    const orderNumber = Array.isArray(orderNumberParam)
      ? orderNumberParam[0]
      : orderNumberParam;
    const totalOrderQuantityParam = router.query.totalOrderQuantity;
    const totalOrderQuantityFromUrl = Array.isArray(totalOrderQuantityParam)
      ? totalOrderQuantityParam[0]
      : totalOrderQuantityParam;

    if (orderNumber) {
      setPatternSearch(orderNumber);
      setFinalPatternSearch(orderNumber);
    }

    const matchedOrder = findOrderDocumentForMoveToDone(
      originalData,
      orderId,
      orderNumber,
    );
    const normalizedOrder = normalizeOrderForPackages(
      matchedOrder
        ? {
          ...matchedOrder,
          totalOrderQuantity:
            matchedOrder.totalOrderQuantity ??
            (totalOrderQuantityFromUrl
              ? Number(totalOrderQuantityFromUrl)
              : undefined),
        }
        : {
          id: orderId,
          totalOrderQuantity: totalOrderQuantityFromUrl
            ? Number(totalOrderQuantityFromUrl)
            : 0,
        },
      orderId,
    );

    moveOrderToDoneModalOpenedRef.current = true;
    pendingMoveToDoneOrderIdRef.current = String(orderId);
    pendingMoveToDoneOrderRef.current = normalizedOrder;
    setSelectedDocument(normalizedOrder);
    MoveOrderToReady.open();

    if (!normalizedOrder?.totalOrderQuantity) {
      void fetchOrderDocumentDetails(String(orderId)).then((details) => {
        if (!details) {
          return;
        }
        const enrichedOrder = normalizeOrderForPackages(
          { ...normalizedOrder, ...details },
          orderId,
        );
        pendingMoveToDoneOrderRef.current = enrichedOrder;
        setSelectedDocument(enrichedOrder);
      });
    }

    router.replace(
      {
        pathname: router.pathname,
        query: orderNumber ? { finalPatternSearch: orderNumber } : {},
      },
      undefined,
      { shallow: true },
    );
  }, [router.isReady, router.query, isOrder, originalData]);

  const exportPdf = async () => {
    const callBack = (res) => {
      if (res?.success) {
        const pdfLink = res.data;
        const filename = generateFilename(
          "Delivery_Ticket",
          selectedDocument?.id,
          undefined,
          "pdf",
        );
        downloadPdf(pdfLink, filename);
        onClosePackagesModal();
      } else {
        alertFaultGetData();
      }
    };
    await exportDeliveryTicketPDFApi(callApi, callBack, {
      orderId: selectedDocument?.id,
      packageQuantities,
    });
  };

  const printPackingSlip = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccess("boardMissions.packingSlipPrinted");
        onClosePackagesModal();
      } else {
        alertFault("boardMissions.alertPrintPackingSlip");
      }
    };
    await printDeliveryTicketPDFApi(callApi, callBack, {
      orderId: selectedDocument?.id,
      packageQuantities,
    });
  };

  const onClickPrintPackagingSlip = async () => {
    if (quantityPerPackage > selectedDocument?.totalOrderQuantity) {
      alertFault("boardMissions.alertQuantity");
      return;
    }

    // If PrintNode is enabled and packaging slip printer is configured, show selection modal
    if (isPrintHouseHasPackagingSlipPrinter) {
      setOpenPrintExportModal(true);
      return;
    }

    // Otherwise, export PDF directly
    await exportPdf();
  };

  const [openPackagesModal, setOpenPackagesModal] = useState<boolean>(false);
  const [openPrintExportModal, setOpenPrintExportModal] =
    useState<boolean>(false);
  const [triggeredFromMoveOrderToReady, setTriggeredFromMoveOrderToReady] =
    useState(false);

  const onOpenPackagesModal = (order: any, fromMoveOrderToReady = false) => {
    const normalizedOrder = normalizeOrderForPackages(order);
    if (normalizedOrder?.id) {
      pendingMoveToDoneOrderIdRef.current = String(normalizedOrder.id);
      pendingMoveToDoneOrderRef.current = normalizedOrder;
    }
    setSelectedDocument(normalizedOrder);
    setTriggeredFromMoveOrderToReady(fromMoveOrderToReady);
    setOpenPackagesModal(true);
  };

  const onClosePackagesModal = () => {
    setOpenPackagesModal(false);
    openPathAfterPackageModal();
    setSelectedDocument({});
  };

  //  opening the path in new tab when closing order as invoice/delivery note after opening package slip modal
  const openPathAfterPackageModal = () => {
    if (!triggeredFromMoveOrderToReady) return;

    const orderId =
      pendingMoveToDoneOrderIdRef.current ??
      selectedDocument?.id ??
      selectedDocument?.orderId;

    if (!orderId) {
      setTriggeredFromMoveOrderToReady(false);
      return;
    }

    const path = isSelectedCLoseAsDeliveryNote
      ? `/deliveryNote?isNewCreation=true&orderId=${orderId}`
      : isOrder
        ? `/invoice?isNewCreation=true&orderId=${orderId}`
        : `/invoice?isNewCreation=true&deliveryNoteId=${orderId}`;

    window.open(path, "_blank");
    setTriggeredFromMoveOrderToReady(false);
  };

  useEffect(() => {
    const total = selectedDocument?.totalOrderQuantity || 0;
    setQuantityOfPackages(1);
    setQuantityPerPackage(total);
  }, [selectedDocument]);

  const [quantityOfPackages, setQuantityOfPackages] = useState(1);
  const [quantityPerPackage, setQuantityPerPackage] = useState(
    selectedDocument?.totalOrderQuantity || 0,
  );

  const handleQuantityOfPackagesChange = (event) => {
    const newQuantityOfPackages = parseInt(event.target.value, 10);
    const totalQuantity = selectedDocument?.totalOrderQuantity || 0;
    const newQuantityPerPackage = Math.ceil(
      totalQuantity / newQuantityOfPackages,
    );
    setQuantityOfPackages(newQuantityOfPackages);
    setQuantityPerPackage(newQuantityPerPackage);
  };

  const handleQuantityPerPackageChange = (event) => {
    const newQuantityPerPackage = parseInt(event.target.value, 10);
    const totalQuantity = selectedDocument?.totalOrderQuantity;
    const newQuantityOfPackages = Math.ceil(
      totalQuantity / newQuantityPerPackage,
    );
    setQuantityPerPackage(newQuantityPerPackage);
    setQuantityOfPackages(newQuantityOfPackages);
  };

  const [packageQuantities, setPackageQuantities] = useState<number[]>([]);

  useEffect(() => {
    if (selectedDocument?.totalOrderQuantity && quantityPerPackage > 0) {
      const totalQuantity = selectedDocument.totalOrderQuantity;
      const fullPackages = Math.max(
        0,
        Math.floor(totalQuantity / quantityPerPackage),
      );
      const remainingQuantity = totalQuantity % quantityPerPackage;

      let newPackageQuantities = Array(fullPackages).fill(quantityPerPackage);
      if (remainingQuantity > 0) {
        newPackageQuantities = [...newPackageQuantities, remainingQuantity];
      }

      setPackageQuantities(newPackageQuantities);
    } else {
      setPackageQuantities([]);
    }
  }, [selectedDocument, quantityPerPackage]);

  const handlePackageQuantityChange = (index: number, newValue: number) => {
    setPackageQuantities((prevQuantities) => {
      const updatedQuantities = [...prevQuantities];
      const totalQuantity = selectedDocument?.totalOrderQuantity || 0;

      // Set the new value for the changed package
      updatedQuantities[index] = newValue;

      if (index === quantityOfPackages - 1) {
        // If last package is changed, put remaining in first package
        const sumOfOtherPackages = updatedQuantities
          .slice(1)
          .reduce((acc, val) => acc + val, 0);
        updatedQuantities[0] = totalQuantity - sumOfOtherPackages;
      } else {
        // If any other package is changed, put remaining in last package
        const sumOfOtherPackages = updatedQuantities
          .slice(0, -1)
          .reduce((acc, val) => acc + val, 0);
        updatedQuantities[quantityOfPackages - 1] =
          totalQuantity - sumOfOtherPackages;
      }

      return updatedQuantities;
    });
  };

  const renderPackageInput = useCallback(
    (index: number) => (
      <div key={index} style={{ width: "30%" }}>
        <h3 style={classes.packageLabelStyle}>
          {`${t("boardMissions.package")} ${index + 1}`}
        </h3>
        <GomakeTextInput
          type="number"
          value={packageQuantities[index]}
          onChange={(e) => {
            const newValue = parseInt(e.target.value, 10);
            handlePackageQuantityChange(index, newValue);
          }}
        />
      </div>
    ),
    [packageQuantities, classes, t],
  );

  const updateCancelQuote = async (
    quoteStatus: number,
    cancelText?: string,
  ) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getAllQuotes();
        if (cancelText) {
          setReasonText("");
          setOpenOtherReasonModal(false);
        }
      } else {
        alertFaultUpdate();
      }
    };
    await cancelDocumentApi(callApi, callBack, {
      DocumentType: docType,
      Document: {
        documentId: selectedDocument?.id,
        quoteStatus: quoteStatus,
        ...(cancelText && { cancelText }),
      },
    });
  };

  const ManuallyCloseDocument = async (quoteItemValue) => {
    setSelectedQuoteItemValue(quoteItemValue);
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        isFromHomePage ? getAllDocuments(docType) : getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await manuallyCloseDocumentApi(callApi, callBack, {
      documentType: docType,
      document: {
        documentId: quoteItemValue?.id,
        documentNumber: documentCloseNumber,
      },
    });
  };

  const OpenDocument = async (document) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        isFromHomePage ? getAllDocuments(docType) : getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    if (document?.statusTitleText === StatusTitleText.OrderCanceled) {
      await OpenCanceledDocumentApi(callApi, callBack, {
        documentType: docType,
        data: { documentId: document?.id },
      });
    } else {
      await OpenDocumentApi(callApi, callBack, {
        documentType: docType,
        documentId: document?.id,
      });
    }
  };

  const startOrderProduction = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        isFromHomePage ? getAllDocuments(docType) : getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await startOrderProductionApi(callApi, callBack, {
      orderId: selectedDocument?.id,
    });
  };

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerForEdit, setCustomerForEdit] = useState([]);
  const onClickOpenCustomerModal = (customerId: any) => {
    setShowCustomerModal(true);
    getCustomerForEdit(customerId);
  };

  const getCustomerForEdit = async (customerId) => {
    const callBack = (res) => {
      if (res.success) {
        let customer = res.data;
        if (customer.contacts && customer.contacts.length > 0) {
          let index = 0;
          customer.contacts.forEach((x) => {
            x.index = index;
            index++;
          });
        }
        if (customer.addresses && customer.addresses.length > 0) {
          let index = 0;
          customer.addresses.forEach((x) => {
            x.index = index;
            index++;
          });
        }
        if (customer.users && customer.users.length > 0) {
          let index = 0;
          customer.users.forEach((x) => {
            x.index = index;
            index++;
          });
        }
        setCustomerForEdit(customer);
        setShowCustomerModal(true);
      }
    };
    await getAndSetCustomerById(callApi, callBack, { customerId: customerId });
  };

  const { getClientTypesCategories } = useClientTypesList();

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [isSendToClient, setIsSendToClient] = useState(false);
  const [isSendOrderReadyToClient, setIsSendOrderReadyToClient] =
    useState(false);
  const [hideCheckboxes, setHideCheckboxes] = useState(false);
  const [preSelectedMethod, setPreSelectedMethod] = useState<
    "sms" | "whatsapp" | "email" | null
  >(null);

  const [paymentModalTitle, setPaymentModalTitle] = useState("");
  const [sendPayment, setSendPayment] =
    useRecoilState<StatePayment>(sendPaymentState);

  const onClickClosePaymentModal = () => {
    setOpenPaymentModal(false);
    setHideCheckboxes(false);
    setPreSelectedMethod(null);
  };

  const onClickSendPaymentOld = (
    document: any,
    isSendToClient: boolean,
  ): Promise<void> => {
    if (isSendToClient) {
      return new Promise<void>((resolve, reject) => {
        const callBack = (res) => {
          if (res?.success) {
            // Use functional update to avoid stale state
            setSendPayment((prevState) => {
              const newSendPaymentState = { ...prevState };
              newSendPaymentState.documentId = document?.id;
              newSendPaymentState.documentContacts = res.data || [];

              // CRITICAL: Set selectedContactIndex and populate contact fields
              if (res.data && res.data.length > 0) {
                const firstContact = res.data[0];
                newSendPaymentState.selectedContactIndex = 0;
                newSendPaymentState.contactName = (
                  firstContact.contactName || ""
                ).trim();
                newSendPaymentState.contactPhone = (
                  firstContact.contactPhone || ""
                ).trim();
                newSendPaymentState.contactMail = (
                  firstContact.contactMail || ""
                ).trim();
              } else {
                newSendPaymentState.selectedContactIndex = -1;
                newSendPaymentState.contactName = "";
                newSendPaymentState.contactPhone = "";
                newSendPaymentState.contactMail = "";
              }

              return newSendPaymentState;
            });
            // Use requestAnimationFrame to ensure state update is processed
            requestAnimationFrame(() => resolve());
          } else {
            reject(new Error("Failed to update payment state"));
          }
        };
        getDocumentContactApi(callApi, callBack, {
          documentId: document?.id,
          documentType: docType,
        });
      });
    } else {
      return new Promise<void>((resolve, reject) => {
        const callBack = (res) => {
          if (res?.success) {
            const newSendPaymentState = { ...sendPayment, ...res.data };
            setSendPayment(newSendPaymentState);
            resolve();
          } else {
            reject(new Error("Failed to update payment state"));
          }
        };
        sendDocumentToClientForPaymentApi(callApi, callBack, {
          id: document?.id,
          documentType: docType,
        });
      });
    }
  };

  const onClickSendPayment = async (
    document: any,
    isSendToClient: boolean,
    isSendOrderReadyToClient: boolean = false,
  ) => {
    setIsSendToClient(isSendToClient);
    setIsSendOrderReadyToClient(isSendOrderReadyToClient);
    try {
      await onClickSendPaymentOld(document, isSendToClient);
      setPaymentModalTitle(
        `${t("prePayment.paymentPageFor")} ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setHideCheckboxes(false); // Show checkboxes to allow selecting email/phone/WhatsApp
      setPreSelectedMethod(null); // No pre-selected method
      setOpenPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };

  const onClickPauseOrResume = async (documentId: string) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await pauseOrdResumeOrderApi(callApi, callBack, {
      documentId,
      documentType: docType,
    });
  };

  const onClickToggleOrderLock = async (order: any) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await toggleOrderLockApi(callApi, callBack, {
      orderId: order?.id,
    });
  };
  const [documentForEmail, setDocumentForEmail] = useState<any>(null);
  // Send to Customer - Email Handler
  const onClickSendToCustomerEmail = async (document: any) => {
    setDocumentForEmail(document);
    try {
      // First, get contact information
      await onClickSendPaymentOld(document, true);
      // Then open email composer with document data
      const selectedContact =
        sendPayment?.documentContacts?.[sendPayment?.selectedContactIndex];
      const contactEmail = selectedContact?.contactMail;
      const contactPhone = selectedContact?.contactPhone;
      const contactName = selectedContact?.contactName || selectedContact?.name;
      const contactID = selectedContact?.contactID;
      await openEmailComposer(
        document?.id,
        contactEmail,
        contactPhone,
        undefined, // messageType - not needed, will use default
        undefined, // sendMailIfNotVerifiedAgent - will use default true
        undefined, // sendWithInvoices - will use default false
        contactID,
        contactName,
        contactName,
      );
    } catch (error) {
      alertFaultGetData();
    }
  };

  // Send to Customer - SMS Handler
  const onClickSendToCustomerSMS = async (document: any) => {
    try {
      await onClickSendPaymentOld(document, true);
      // Use functional update to get latest state after contacts are loaded
      setSendPayment((prevState) => {
        // Ensure contact fields are populated from documentContacts if they exist
        let updatedState = { ...prevState };
        if (
          prevState.documentContacts &&
          prevState.documentContacts.length > 0
        ) {
          const contactIndex =
            prevState.selectedContactIndex >= 0
              ? prevState.selectedContactIndex
              : 0;
          const selectedContact = prevState.documentContacts[contactIndex];
          if (selectedContact) {
            updatedState.selectedContactIndex = contactIndex;
            updatedState.contactName = (
              selectedContact.contactName || ""
            ).trim();
            updatedState.contactPhone = (
              selectedContact.contactPhone || ""
            ).trim();
            updatedState.contactMail = (
              selectedContact.contactMail || ""
            ).trim();
          }
        }
        return {
          ...updatedState,
          sendBySms: true,
          sendByMail: false,
          sendByWhatsapp: false,
        };
      });
      setPaymentModalTitle(
        `${t("sales.quote.sendToClient")} - ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setIsSendToClient(true);
      setHideCheckboxes(true);
      setPreSelectedMethod("sms");
      setOpenPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };

  // Send to Customer - WhatsApp Handler
  const onClickSendToCustomerWhatsApp = async (document: any) => {
    try {
      await onClickSendPaymentOld(document, true);
      // Use functional update to get latest state after contacts are loaded
      setSendPayment((prevState) => {
        // Ensure contact fields are populated from documentContacts if they exist
        let updatedState = { ...prevState };
        if (
          prevState.documentContacts &&
          prevState.documentContacts.length > 0
        ) {
          const contactIndex =
            prevState.selectedContactIndex >= 0
              ? prevState.selectedContactIndex
              : 0;
          const selectedContact = prevState.documentContacts[contactIndex];
          if (selectedContact) {
            updatedState.selectedContactIndex = contactIndex;
            updatedState.contactName = (
              selectedContact.contactName || ""
            ).trim();
            updatedState.contactPhone = (
              selectedContact.contactPhone || ""
            ).trim();
            updatedState.contactMail = (
              selectedContact.contactMail || ""
            ).trim();
          }
        }
        return {
          ...updatedState,
          sendByWhatsapp: true,
          sendByMail: false,
          sendBySms: false,
        };
      });
      setPaymentModalTitle(
        `${t("sales.quote.sendToClient")} - ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setIsSendToClient(true);
      setHideCheckboxes(true);
      setPreSelectedMethod("whatsapp");
      setOpenPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };
  // Send Payment - Email Handler
  const onClickSendPaymentEmail = async (document: any) => {
    setDocumentForEmail(document);
    try {
      // First, get contact information
      await onClickSendPaymentOld(document, false);

      if (hasFrontDeskFeature) {
        // When FrontDesk is enabled, open the modal with Email pre-selected
        setSendPayment((prev) => ({
          ...prev,
          sendByMail: true,
          sendBySms: false,
          sendByWhatsapp: false,
          selectedContactIndex:
            prev.documentContacts?.length > 0 ? 0 : prev.selectedContactIndex,
        }));
        setPaymentModalTitle(
          `${t("prePayment.paymentPageFor")} ${t(
            `sales.quote.${DOCUMENT_TYPE[docType]}`,
          ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
        );
        setIsSendToClient(false);
        setHideCheckboxes(true); // Hide checkboxes when opened from submenu
        setPreSelectedMethod("email"); // Set email as pre-selected method
        setOpenPaymentModal(true);
      } else {
        // When FrontDesk is disabled, open email composer directly (old behavior)
        // Handle both documentContacts array and direct contact properties
        const selectedContact =
          sendPayment?.documentContacts?.[sendPayment?.selectedContactIndex];
        const contactEmail =
          selectedContact?.contactMail || sendPayment?.contactMail || "";
        const contactPhone =
          selectedContact?.contactPhone || sendPayment?.contactPhone || "";
        const contactName =
          selectedContact?.contactName ||
          selectedContact?.name ||
          document?.customerName ||
          document?.clientName ||
          "";
        await openEmailComposer(
          document?.id,
          contactEmail,
          contactPhone,
          undefined, // messageType - not needed, will use default
          undefined, // sendMailIfNotVerifiedAgent - will use default true
          undefined, // sendWithInvoices - will use default false
          document?.customerId || document?.clientId,
          document?.customerName || document?.clientName,
          contactName,
        );
      }
    } catch (error) {
      alertFaultGetData();
    }
  };

  // Send Payment - SMS Handler
  const onClickSendPaymentSMS = async (document: any) => {
    try {
      await onClickSendPaymentOld(document, false);
      // Wait a bit to ensure state is updated, then update only the send method flags
      setSendPayment((prev) => ({
        ...prev,
        sendBySms: true,
        sendByMail: false,
        sendByWhatsapp: false,
        selectedContactIndex:
          prev.documentContacts?.length > 0 ? 0 : prev.selectedContactIndex,
      }));
      setPaymentModalTitle(
        `${t("prePayment.paymentPageFor")} ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setIsSendToClient(false);
      setHideCheckboxes(true); // Hide checkboxes when opened from submenu
      setPreSelectedMethod("sms");
      setOpenPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };

  // Send Payment - WhatsApp Handler
  const onClickSendPaymentWhatsApp = async (document: any) => {
    try {
      await onClickSendPaymentOld(document, false);
      // Wait a bit to ensure state is updated, then update only the send method flags
      setSendPayment((prev) => ({
        ...prev,
        sendByWhatsapp: true,
        sendByMail: false,
        sendBySms: false,
        selectedContactIndex:
          prev.documentContacts?.length > 0 ? 0 : prev.selectedContactIndex,
      }));
      setPaymentModalTitle(
        `${t("prePayment.paymentPageFor")} ${t(
          `sales.quote.${DOCUMENT_TYPE[docType]}`,
        ).toLowerCase()} ${t("sales.quote.number")} - ${document?.number}`,
      );
      setIsSendToClient(false);
      setHideCheckboxes(true); // Hide checkboxes when opened from submenu
      setPreSelectedMethod("whatsapp");
      setOpenPaymentModal(true);
    } catch (error) {
      alertFaultGetData();
    }
  };

  const [openMakePaymentModal, setOpenMakePaymentModal] = useState(false);
  const onClickMakePayment = (order) => {
    setSelectedQuoteItemValue(order);
    setOpenMakePaymentModal(true);
  };

  const onClickCloseMakePaymentModal = () => {
    setOpenMakePaymentModal(false);
  };

  const [isMoveToReady, setIsMoveToReady] = useState(false);
  const [moveToReadyOption, setMoveToReadyOption] = useState<
    "withMessage" | "withoutMessage"
  >("withoutMessage");

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMoveToReadyOption(
      event.target.value as "withMessage" | "withoutMessage",
    );
  };

  const handleCheckboxChange = () => {
    const newValue = !isMoveToReady;
    setIsMoveToReady(newValue);

    if (newValue) {
      setMoveToReadyOption("withoutMessage"); // default to "without message" when checked
    }
  };

  const [openCloseAndTrackOrderModal, setOpenCloseAndTrackOrderModal] =
    useState(false);
  const [openPrintPDFModal, setOpenPrintPDFModal] = useState(false);
  const [openChangeProductionStatusModal, setOpenChangeProductionStatusModal] =
    useState(false);

  const onClickCloseChangeProductionStatusModal = () => {
    setOpenChangeProductionStatusModal(false);
    setIsMoveToReady(false);
  };

  const onClickOpenChangeProductionStatusModal = (order) => {
    const normalizedOrder = normalizeOrderForPackages(order);
    pendingMoveToDoneOrderIdRef.current = normalizedOrder?.id
      ? String(normalizedOrder.id)
      : null;
    pendingMoveToDoneOrderRef.current = normalizedOrder;
    setSelectedDocument(normalizedOrder ?? order);
    setOpenChangeProductionStatusModal(true);
  };

  const onClickClosePrintPDFModal = () => {
    setOpenPrintPDFModal(false);
  };

  const isDeliveryNoteHideAllPrices = (document) =>
    Number(document?.showPrices ?? document?.ShowPrices) ===
    ShowPricesEnum.HidePrices;

  const onClickOpenPrintPDFModal = async (order) => {
    setSelectedDocument(order);

    if (isDeliveryNoteHideAllPrices(order)) {
      onClickQuotePdf(order?.id, false);
      return;
    }

    const pricesSetting = order?.showPrices ?? order?.ShowPrices;
    if (pricesSetting != null) {
      setOpenPrintPDFModal(true);
      return;
    }

    const document = await new Promise<any>((resolve) => {
      getDocumentApi(
        callApi,
        (res) => {
          resolve(res?.success ? res.data : null);
        },
        {
          Id: order?.id,
          documentType: docType,
        },
      );
    });

    if (isDeliveryNoteHideAllPrices(document)) {
      onClickQuotePdf(order?.id, false);
      return;
    }

    setOpenPrintPDFModal(true);
  };

  const onClickOpenTrackModal = (order) => {
    setSelectedQuoteItemValue(order);
    setOpenCloseAndTrackOrderModal(true);
  };

  const onClickCloseTrackModal = () => {
    setOpenCloseAndTrackOrderModal(false);
  };

  const closeAndTrackOrder = async () => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await closeAndTrackOrderApi(callApi, callBack, {
      documentType: docType,
      documentId: selectedQuoteItemValue?.id,
    });
  };

  // State and functions for receipt cancellation modals

  const [openCancelReceiptModal, setOpenCancelReceiptModal] = useState(false);
  const onOpenCancelReceiptModal = (receipt: any) => {
    setSelectedDocument(receipt);
    setOpenCancelReceiptModal(true);
  };
  const onCloseCancelReceiptModal = () => {
    setSelectedDocument(null);
    setOpenCancelReceiptModal(false);
  };

  const [openCancelReceiptModalRefund, setOpenCancelReceiptModalRefund] =
    useState(false);
  const onOpenCancelReceiptModalRefund = (receipt: any) => {
    setSelectedDocument(receipt);
    setOpenCancelReceiptModalRefund(true);
  };
  const onCloseCancelReceiptModalRefund = () => {
    setSelectedDocument(null);
    setOpenCancelReceiptModalRefund(false);
  };

  // Function to handle receipt cancellation

  const onClickCancelReceipt = async (refundCredit?: boolean | null) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessUpdate();
        getAllQuotes();
      } else {
        alertFaultUpdate();
      }
    };
    await cancelReceiptApi(callApi, callBack, {
      id: selectedDocument?.id,
      refundCredit,
    });
  };

  /*   Most Important   */
  useEffect(() => {
    const q = router.query;
    const hasQuery = Object.keys(q || {}).length > 0;
    if (!hasQuery) {
      // clear all URL-derived filters/state so the inputs look empty
      setAgentId(null);
      setCustomerSupportRepresentativeId(null);
      setCustomerId(null);
      setStatusId(null);
      setApprovalStatusId(null);
      setAccountingStatus(null);
      setPaymentType(null);
      setDeliveryStatus(null);
      setProductionStatus(null);
      setFromDate(null);
      setToDate(null);
      setCheckFromDate(null);
      setCheckToDate(null);
      setResetDatePicker(true);
      setIsCanceledState(false);
      setIsPausedOrders(false);
      setPage(1);
      setMinPrice("");
      setMaxPrice("");
      setProductIds([]);
      setPrintHouseMaterialIds([]);
      setFinalPatternSearch("");
      setPatternSearch("");
      setCurrentSortOrder(null);
      return; // nothing else to hydrate
    }

    const {
      customerId,
      customerName,
      agentId,
      customerSupportRepresentativeId,
      statusId,
      approvalStatusId,
      minPrice,
      maxPrice,
      fromDate,
      toDate,
      checkFromDate,
      checkToDate,
      finalPatternSearch,
      accountingStatus,
      deliveryStatus,
      productionStatus,
      isCanceled,
      pageNumber,
      isPaused,
      isOrdersWithoutJobs,
      productsIds,
      printHouseMaterialIds,
      paymentType,
      currency,
      documentNumber,
    } = q;

    if (productsIds) {
      const productIdsArray = Array.isArray(productsIds)
        ? productsIds
        : [productsIds];
      setProductIds(productIdsArray); // Set the productsIds state if needed
    }

    if (printHouseMaterialIds) {
      const materialIdsArray = Array.isArray(printHouseMaterialIds)
        ? printHouseMaterialIds
        : [printHouseMaterialIds];
      setPrintHouseMaterialIds(materialIdsArray.map(String));
    }

    if (currency && typeof currency === "string") {
      const currencyToSet = currencies.find((curr) => curr.value === currency);
      if (currencyToSet) {
        setCurrencyFilter(currencyToSet);
      }
    }

    if (customerId) {
      setCustomerId({ id: customerId, name: customerName });
    }

    if (agentId) {
      const selectedAgent = agentsCategories?.find(
        (agent: any) => agent?.id === agentId,
      );
      setAgentId(selectedAgent);
    }

    if (customerSupportRepresentativeId) {
      const selectedCsr = customerSupportRepresentativesCategories?.find(
        (employee: any) => employee?.id === customerSupportRepresentativeId,
      );
      setCustomerSupportRepresentativeId(selectedCsr);
    }

    if (statusId) {
      const statusToSet = quoteStatuses.find(
        (status) => status.value === Number(statusId),
      );
      if (statusToSet) {
        setStatusId(statusToSet);
      }
    }

    if (approvalStatusId) {
      const appStatusToSet = approvalStatuses.find(
        (status) => status.value === Number(approvalStatusId),
      );
      if (appStatusToSet) {
        setApprovalStatusId(appStatusToSet);
      }
    }

    if (pageNumber) setPage(Number(pageNumber)); // Convert to number

    if (typeof minPrice === "string") setMinPrice(minPrice);
    if (typeof maxPrice === "string") setMaxPrice(maxPrice);

    if (fromDate && typeof fromDate === "string")
      setFromDate(new Date(fromDate));
    if (toDate && typeof toDate === "string") setToDate(new Date(toDate));

    if (checkFromDate && typeof checkFromDate === "string")
      setCheckFromDate(new Date(checkFromDate));
    if (checkToDate && typeof checkToDate === "string")
      setCheckToDate(new Date(checkToDate));

    // Handle documentNumber from URL - add it to patternSearch
    if (documentNumber && typeof documentNumber === "string") {
      if (
        !(
          searchInputRef &&
          searchInputRef.current &&
          document.activeElement.id == searchInputRef.current.id
        )
      ) {
        setPatternSearch(documentNumber);
        setFinalPatternSearch(documentNumber);
      }
    } else if (finalPatternSearch && typeof finalPatternSearch === "string") {
      if (
        !(
          searchInputRef &&
          searchInputRef.current &&
          document.activeElement.id == searchInputRef.current.id
        )
      ) {
        setPatternSearch(finalPatternSearch);
        setFinalPatternSearch(finalPatternSearch);
      }
    }

    if (accountingStatus) {
      const statusToSet = accountingStatuses.find(
        (status) => status.value === Number(accountingStatus),
      );
      if (statusToSet) {
        setAccountingStatus(
          statusToSet as unknown as {
            label: string;
            value: BoardMissionStatusForFilter;
          },
        ); // Cast to unknown first
      }
    }

    if (paymentType) {
      const typeToSet = typeOfPayments.find(
        (type) => type.value === Number(paymentType),
      );
      if (typeToSet) {
        setPaymentType(
          typeToSet as unknown as {
            label: string;
            value: PAYMENT_TYPE;
          },
        );
      }
    }

    if (deliveryStatus) {
      const deliveryStatusToSet = deliveryStatuses.find(
        (status) => status.value === Number(deliveryStatus),
      );
      if (deliveryStatusToSet) {
        setDeliveryStatus(
          deliveryStatusToSet as unknown as {
            label: string;
            value: BoardMissionStatusForFilter;
          },
        );
      }
    }

    if (productionStatus) {
      const productionStatusToSet = productionStatuses.find(
        (status) => status.value === Number(productionStatus),
      );
      if (productionStatusToSet) {
        setProductionStatus(
          productionStatusToSet as unknown as {
            label: string;
            value: BoardMissionStatusForFilter;
          },
        );
      }
    }

    if (isCanceled) {
      setIsCanceledState(
        Array.isArray(isCanceled)
          ? isCanceled[0] === "true"
          : isCanceled === "true",
      );
    }

    if (isPaused) {
      setIsPausedOrders(
        Array.isArray(isPaused) ? isPaused[0] === "true" : isPaused === "true",
      );
    }

    if (isOrdersWithoutJobs) {
      setIsOrdersWithoutJobs(
        Array.isArray(isOrdersWithoutJobs)
          ? isOrdersWithoutJobs[0] === "true"
          : isOrdersWithoutJobs === "true",
      );
    }
  }, [
    router.query,
    agentsCategories,
    customerSupportRepresentativesCategories,
    currencies,
  ]);

  // Check for openComposer query parameter and open composer if needed
  // This works the same way as the jobs page (board-missions) for consistency
  useEffect(() => {
    // Early returns - don't proceed if conditions aren't met
    if (!router.isReady || !hasFrontDeskFeature || !isOrder) {
      composerOpenedRef.current = false;
      return;
    }

    // Check if openEmailComposer is available
    if (!openEmailComposer || typeof openEmailComposer !== "function") {
      return;
    }

    const shouldOpenComposer = router.query.openComposer === "true";
    const orderIdParam = router.query.orderId;
    const paymentRequestIdParam = router.query.paymentRequestId;
    const isPrepaymentClientParam = router.query.isPrepaymentClient;
    // Get client information from URL parameters (passed from quote)
    const contactEmailParam = router.query.contactEmail;
    const contactPhoneParam = router.query.contactPhone;
    const contactNameParam = router.query.contactName;
    const customerIdParam = router.query.customerId;
    const customerNameParam = router.query.customerName;

    // Only proceed if both conditions are met and we haven't already opened it
    if (!shouldOpenComposer || !orderIdParam) {
      composerOpenedRef.current = false;
      return;
    }

    // Prevent duplicate calls
    if (composerOpenedRef.current) {
      return;
    }

    const orderId = Array.isArray(orderIdParam)
      ? orderIdParam[0]
      : orderIdParam;

    // Validate orderId
    if (!orderId || typeof orderId !== "string" || orderId.trim() === "") {
      return;
    }

    const paymentRequestId = paymentRequestIdParam
      ? Array.isArray(paymentRequestIdParam)
        ? paymentRequestIdParam[0]
        : paymentRequestIdParam
      : undefined;

    // Check if isPrepaymentClient is true in URL
    const isPrepaymentClient = isPrepaymentClientParam
      ? Array.isArray(isPrepaymentClientParam)
        ? isPrepaymentClientParam[0] === "true"
        : String(isPrepaymentClientParam) === "true"
      : false;

    // Mark as opened to prevent duplicate calls
    composerOpenedRef.current = true;

    // Use a flag to prevent state updates after unmount
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const openComposer = async () => {
      if (!isMounted) return;

      try {
        // Extract client information from URL parameters (passed from quote)
        // These are the true values from the quote before conversion to order
        const contactEmail = contactEmailParam
          ? Array.isArray(contactEmailParam)
            ? contactEmailParam[0]
            : String(contactEmailParam)
          : undefined;
        const contactPhone = contactPhoneParam
          ? Array.isArray(contactPhoneParam)
            ? contactPhoneParam[0]
            : String(contactPhoneParam)
          : undefined;
        const contactName = contactNameParam
          ? Array.isArray(contactNameParam)
            ? contactNameParam[0]
            : String(contactNameParam)
          : undefined;
        const customerId = customerIdParam
          ? Array.isArray(customerIdParam)
            ? customerIdParam[0]
            : String(customerIdParam)
          : undefined;
        const customerName = customerNameParam
          ? Array.isArray(customerNameParam)
            ? customerNameParam[0]
            : String(customerNameParam)
          : undefined;

        // Set documentForEmail so FrontDeskEmailComposerWrapper can use it
        setDocumentForEmail({ id: orderId });

        // Wrap the API call in a promise that catches errors (same pattern as jobs page)
        const composerPromise = new Promise<void>((resolve, reject) => {
          // Open email composer with paymentRequestId if available
          // When isPrepaymentClient=true OR paymentRequestId is present, set isFromPaymentPage=true to use document type 18
          // Pass client information from URL parameters (from quote)
          openEmailComposer(
            orderId,
            contactEmail, // contactEmail from URL (from quote)
            contactPhone, // contactPhone from URL (from quote)
            undefined, // messageType
            undefined, // sendMailIfNotVerifiedAgent
            undefined, // sendWithInvoices
            customerId, // customerId from URL (from quote)
            customerName, // customerName from URL (from quote)
            contactName, // contactName from URL (from quote)
            undefined, // managerEmails
            isPrepaymentClient || !!paymentRequestId, // isFromPaymentPage - true when isPrepaymentClient=true OR paymentRequestId exists
            paymentRequestId ? String(paymentRequestId) : undefined, // paymentRequestId
          )
            .then(resolve)
            .catch(reject);
        });

        await composerPromise;

        // Clean up the query parameters only if successful
        if (isMounted) {
          const newQuery = { ...router.query };
          delete newQuery.openComposer;
          delete newQuery.orderId;
          delete newQuery.paymentRequestId;

          router.replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { shallow: true },
          );
        }
      } catch (apiError: any) {
        // Silently handle API errors - don't show unhandled error to user
        console.warn(
          "Failed to open email composer for order:",
          orderId,
          apiError?.message || apiError,
        );

        // Clear the ref on error
        if (isMounted) {
          composerOpenedRef.current = false;
          setDocumentForEmail(null);

          // Clean up query params even on error
          const newQuery = { ...router.query };
          delete newQuery.openComposer;
          delete newQuery.orderId;
          delete newQuery.paymentRequestId;
          delete newQuery.isPrepaymentClient;
          delete newQuery.contactEmail;
          delete newQuery.contactPhone;
          delete newQuery.contactName;
          delete newQuery.customerId;
          delete newQuery.customerName;
          delete newQuery.contactEmail;
          delete newQuery.contactPhone;
          delete newQuery.contactName;
          delete newQuery.customerId;
          delete newQuery.customerName;

          router.replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { shallow: true },
          );
        }
      }
    };

    // Use setTimeout to ensure the page is fully loaded and order is created
    // Same delay as jobs page for consistency
    timeoutId = setTimeout(() => {
      openComposer();
    }, 1000);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router.isReady,
    router.query.openComposer,
    router.query.orderId,
    router.query.paymentRequestId,
    router.query.isPrepaymentClient,
    router.query.contactEmail,
    router.query.contactPhone,
    router.query.contactName,
    router.query.customerId,
    router.query.customerName,
    hasFrontDeskFeature,
    isOrder,
  ]);

  // Clear composer opened ref and documentForEmail when composer closes
  useEffect(() => {
    if (!isEmailComposerOpen) {
      composerOpenedRef.current = false;
      // Don't clear documentForEmail here - it might be used by other parts of the UI
    }
  }, [isEmailComposerOpen]);

  const closeOrderAsInvoiceOrDeliveryNote = useRecoilValue(
    closeOrderAsInvoiceOrDeliveryNoteState,
  );

  const moveOrderToReady = async (isSendMessage: boolean = false) => {
    const orderId =
      selectedDocument?.id ??
      selectedDocument?.orderId ??
      pendingMoveToDoneOrderIdRef.current;

    const orderSnapshot =
      pendingMoveToDoneOrderRef.current ??
      normalizeOrderForPackages(selectedDocument, orderId ?? undefined);

    if (!orderId) {
      alertFaultUpdate();
      return;
    }

    const callBack = async (res) => {
      if (res?.success) {
        getAllQuotes();
        if (isOrder) {
          const orderForPackages = await resolveOrderDocumentForPackages(
            String(orderId),
            finalPatternSearch || patternSearch,
          );
          pendingMoveToDoneOrderRef.current = orderForPackages;
          setSelectedDocument(orderForPackages);

          onOpenPackagesModal(
            orderForPackages,
            closeOrderAsInvoiceOrDeliveryNote ? true : false,
          );
          // Always open email composer after marking ready (if FrontDesk is enabled)
          if (hasFrontDeskFeature && orderId) {
            // Set documentForEmail so FrontDeskEmailComposerWrapper can use it
            setDocumentForEmail(orderForPackages);
            // Get contact info from selectedDocument if available
            const contactEmail =
              orderForPackages?.contactEmail || orderForPackages?.contactMail;
            const contactPhone = orderForPackages?.contactPhone;
            const customerId =
              orderForPackages?.customerId || orderForPackages?.clientId;
            const customerName =
              orderForPackages?.customerName || orderForPackages?.clientName;
            const contactName = orderForPackages?.contactName;

            openEmailComposer(
              orderId,
              contactEmail,
              contactPhone,
              undefined, // messageType - will use default
              undefined, // sendMailIfNotVerifiedAgent - will use default true
              undefined, // sendWithInvoices - will use default false
              customerId,
              customerName,
              contactName,
            );
          }
          return;
        }
        if (isSelectedCLoseAsDeliveryNote) {
          const path = `/deliveryNote?isNewCreation=true&orderId=${orderId}`;
          window.open(path, "_blank");
        } else if (isSelectedCLoseAsProformaInvoice) {
          const path = isOrder
            ? `/proformaInvoice?isNewCreation=true&orderId=${orderId}`
            : `/proformaInvoice?isNewCreation=true&deliveryNoteId=${orderId}`;
          window.open(path, "_blank");
        } else {
          const path = isOrder
            ? `/invoice?isNewCreation=true&orderId=${orderId}`
            : `/invoice?isNewCreation=true&deliveryNoteId=${orderId}`;
          window.open(path, "_blank");
        }
      } else {
        alertFaultUpdate();
      }
    };
    // Mark as ready without sending message - user will decide in email composer
    await moveOrderToReadyApi(callApi, callBack, {
      orderId,
      isSendMessage: isSendMessage, // Don't send automatically - let user decide in composer
    });
  };

  const onChangeDocumentSearch = (value: string) => {
    setPatternSearch(value);
  };

  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);
  const onCloseDuplicateModal = () => {
    setSelectedDocument({});
    setOpenDuplicateModal(false);
  };

  const onOpenDuplicateModal = (mission: any) => {
    setSelectedDocument(mission);
    setOpenDuplicateModal(true);
  };

  const getValueByKey = (statistics: ICard[], key: string) => {
    const card = statistics?.find((item) => item.key === key);
    return card ? card.value : "0";
  };

  // Alert mail not verified modal
  const alertNotVerifiedMailModal = useModal();
  const [alertNotVerifiedModalState, setAlertModalState] = useState<{
    message: string;
    onConfirm?: () => void;
  }>({
    message: "",
  });

  const showAlertModal = ({
    message,
    onConfirm,
  }: {
    message: string;
    onConfirm?: () => void;
  }) => {
    setAlertModalState({ message, onConfirm });
    alertNotVerifiedMailModal.open();
  };

  const removeDraftModal = useModal();

  const onClickOpenRemoveDraftModal = (document) => {
    setSelectedDocument(document);
    removeDraftModal.open();
  };

  const onCloseOpenRemoveDraftModal = () => {
    setSelectedDocument({});
    removeDraftModal.close();
  };

  const handleRemoveDraftBtnClick = async () => {
    const callBack = (res) => {
      if (res?.success) {
        getAllQuotes();
      } else {
        alertFaultDelete();
      }
    };
    await removeDraftDocumentApi(callApi, callBack, {
      documentType: docType,
      documentId: selectedDocument?.id,
    });
  };

  const handleGetTaxConfirmNo = async (documentId: string) => {
    const callBack = (res) => {
      if (res?.success) {
        alertSuccessGetData();
        getAllQuotes();
      } else alertFaultGetData();
    };

    await getTaxConfirmationNumberApi(callApi, callBack, documentId);
  };

  const [isDescOrderBE, setIsDescOrderBE] = useState(false);
  const columnMappings = {
    [DOCUMENT_TYPE.quote]: {
      creationDate: QuotesTableColumns.CreationDate,
      client: QuotesTableColumns.ClientName,
      number: QuotesTableColumns.Number,
      totalPrice: QuotesTableColumns.TotalPrice,
      orderNumber: QuotesTableColumns.OrderNumber,
      totalPriceWithoutVat: QuotesTableColumns.TotalPriceWithoutVat,
      discount: QuotesTableColumns.Discount,
    },
    [DOCUMENT_TYPE.order]: {
      creationDate: OrdersTableColumns.CreationDate,
      deliveryDate: OrdersTableColumns.DeliveryDate,
      orderNumber: OrdersTableColumns.Number,
      client: OrdersTableColumns.ClientName,
      purchaseNumber: OrdersTableColumns.PurchaseNumber,
      jobs: OrdersTableColumns.OrderItemsNumber,
      worksName: OrdersTableColumns.WorksNames,
      cost: OrdersTableColumns.Cost,
      totalPrice: OrdersTableColumns.TotalPrice,
      totalPriceWithoutVat: OrdersTableColumns.TotalPriceWithoutVat,
      discount: OrdersTableColumns.Discount,
    },
    [DOCUMENT_TYPE.deliveryNote]: {
      creationDate: DeliveryNotesTableColumns.CreationDate,
      client: DeliveryNotesTableColumns.ClientName,
      worksName: DeliveryNotesTableColumns.WorksNames,
      notes: DeliveryNotesTableColumns.Notes,
      totalPrice: DeliveryNotesTableColumns.TotalPrice,
      deliveryStatus: DeliveryNotesTableColumns.DeliveryStatus,
      status: DeliveryNotesTableColumns.Status,
      totalPriceWithoutVat: DeliveryNotesTableColumns.TotalPriceWithoutVat,
      discount: DeliveryNotesTableColumns.Discount,
    },
    [DOCUMENT_TYPE.invoice]: {
      creationDate: DeliveryNotesTableColumns.CreationDate,
      client: DeliveryNotesTableColumns.ClientName,
      worksName: DeliveryNotesTableColumns.WorksNames,
      notes: DeliveryNotesTableColumns.Notes,
      totalPrice: DeliveryNotesTableColumns.TotalPrice,
      deliveryStatus: DeliveryNotesTableColumns.DeliveryStatus,
      status: DeliveryNotesTableColumns.Status,
      totalPriceWithoutVat: DeliveryNotesTableColumns.TotalPriceWithoutVat,
      discount: DeliveryNotesTableColumns.Discount,
    },
    default: {
      creationDate: 0,
      orderNumber: 1,
      client: 2,
      worksName: 3,
      notes: 6,
      totalPrice: 7,
      deliveryStatus: 8,
      status: 9,
    },
  };

  // Check if header is sortable
  const isHeaderSortable = (docType: string, header: string) => {
    const headers = columnMappings[docType] || columnMappings.default;
    return headers.hasOwnProperty(header);
  };

  const handleSortBE = (key: string) => {
    const direction = isDescOrderBE ? "desc" : "asc";

    const columnIndex =
      columnMappings[docType]?.[key] ?? columnMappings.default[key];

    if (columnIndex !== undefined) {
      const sortOrder = [
        {
          column: columnIndex,
          dir: direction,
        },
      ];

      // Save the sort order to state
      setCurrentSortOrder(sortOrder);

      getAllQuotes(sortOrder);
      setIsDescOrderBE(!isDescOrderBE);
    } else {
      console.error("Invalid key for sorting:", key);
    }
  };

  const documentTypeToTableKeyMap: Record<number, string> = {
    [DOCUMENT_TYPE.quote]: "quotes",
    [DOCUMENT_TYPE.order]: "orders",
    [DOCUMENT_TYPE.deliveryNote]: "deliveryNotes",
    [DOCUMENT_TYPE.deliveryNoteRefund]: "deliveryNotes",
    [DOCUMENT_TYPE.proformaInvoice]: "proformaInvoices",
    [DOCUMENT_TYPE.invoice]: "invoices",
    [DOCUMENT_TYPE.invoiceRefund]: "invoices",
    [DOCUMENT_TYPE.purchaseOrder]: "purchaseOrders",
    [DOCUMENT_TYPE.purchaseInvoice]: "purchaseInvoices",
    [DOCUMENT_TYPE.purchaseInvoiceRefund]: "purchaseInvoices",
    [DOCUMENT_TYPE.receipt]: "receipts",
    [DOCUMENT_TYPE.supplierPayments]: "supplierPayments",
  };

  const tableKey = documentTypeToTableKeyMap[docType] || "default";

  const [columnOrder] = useRecoilState(columnOrderStateFamily(tableKey));
  const [columnVisibility] = useRecoilState(
    columnVisibilityStateFamily(tableKey),
  );

  const setColumnVisibility = useSetRecoilState(
    columnVisibilityStateFamily(tableKey),
  );
  const setColumnOrder = useSetRecoilState(columnOrderStateFamily(tableKey));
  const [columnsReady, setColumnsReady] = useState(false);

  const visibleHeaders = columnOrder.filter(
    (key) =>
      columnVisibility[key] &&
      (showCustomerSupportRepresentative ||
        key !== "customerSupportRepresentative"),
  );

  return {
    showCustomerModal,
    customerForEdit,
    setCustomerForEdit,
    setShowCustomerModal,
    updateCancelQuote,
    openIrrelevantCancelModal,
    onClickCloseIrrelevantModal,
    openPriceCancelModal,
    openDeliveryTimeCancelModal,
    onClickCloseDeliveryTimeModal,
    openDuplicateCancelModal,
    onClickCloseDuplicateModal,
    onClickClosePriceModal,
    openOtherReasonModal,
    onClickOpenOtherReasonModal,
    onClickCloseOtherReasonModal,
    onClickCancelOffer,
    setReasonText,
    reasonText,
    t,
    patternSearch,
    tableHeaders,
    allQuotes,
    quoteStatuses,
    agentsCategories,
    customerSupportRepresentativesCategories,
    openModal,
    statusId,
    customerId,
    agentId,
    customerSupportRepresentativeId,
    errorColor,
    onClickCloseModal,
    setPatternSearch,
    setStatusId,
    setCustomerId,
    setIsCanceledState,
    isCanceledState,
    setDateRange,
    setAgentId,
    setCustomerSupportRepresentativeId,
    renderOptions,
    checkWhatRenderArray,
    updateQuoteStatus,
    onClickSearchFilter,
    getAllQuotes,
    onClickClearFilter,
    onClickQuotePdf,
    openLogsModal,
    onClickCloseLogsModal,
    logsModalTitle,
    logsTableHeaders,
    documentsLabels,
    documentLabel,
    allDocuments,
    tableHomeHeader,
    tableHomeHeaderModern,
    // Modern home (customer view) list controls
    homePageSize,
    homePagesCount,
    homeTotalItems,
    handleHomePageSizeChange,
    homeStatusOptions,
    homeStatusValue,
    setHomeStatus,
    applyHomeSearch,
    onSelectHomeDates,
    setHomeProducts,
    clearHomeFilters,
    hasHomeFilters,
    pagesCount,
    page,
    setPage,
    allStatistics,
    onclickCreateNew,
    pageSize,
    handlePageSizeChange,
    activeCard,
    handleCardClick,
    handleSecondCardClick,
    onCloseAddRuleModal,
    onOpenAddRuleModal,
    openAddRule,
    openFilterConfirmationModal,
    onCloseFilterConfirmationModal,
    onContinueWithFilters,
    onResetFiltersAndGenerate,
    openScheduledReportsModal,
    onShowScheduledReports,
    onCloseScheduledReportsModal,
    onToggleScheduledReportActive,
    onDeleteScheduledReport,
    navigate,
    documentPath,
    resetDatePicker,
    onSelectDeliveryTimeDates,
    checkFromDate,
    checkToDate,
    onSelectCheckDueDateRange,
    employeeId,
    handleSelectEmployee,
    resetLogsDatePicker,
    onSelectDateRange,
    onClickSearchLogsFilter,
    onClickClearLogsFilter,
    documentLogsData,
    handleMaxPriceChange,
    handleMinPriceChange,
    minPrice,
    maxPrice,
    handleMultiSelectChange,
    productIds,
    printHouseMaterialIds,
    setPrintHouseMaterialIds,
    productsList,
    getAllProducts,
    accountingStatuses,
    accountingStatus,
    productionStatuses,
    productionStatus,
    handleProductionStatusChange,
    handleAccountingStatusChange,
    handleClick,
    handleClose,
    open,
    anchorEl,
    filterData,
    filterDTO,
    openCloseOrderNotesModal,
    onClickCloseCloseOrderNotesModal,
    selectedQuoteItemValue,
    paymentModalTitle,
    openPaymentModal,
    onClickClosePaymentModal,
    onClickCloseCloseOrderModal,
    openCloseOrderModal,
    selectedDocument,
    onClickOpenCloseOrderModal,
    getEmployeeCategories,
    ManuallyCloseDocument,
    documentCloseNumber,
    handleDocumentNumberChange,
    approvalStatuses,
    setApprovalStatusId,
    approvalStatusId,
    openMakePaymentModal,
    onClickCloseMakePaymentModal,
    isOrdersTracking,
    onClickCloseTrackModal,
    openCloseAndTrackOrderModal,
    closeAndTrackOrder,
    onClickClosePrintPDFModal,
    openPrintPDFModal,
    fromDate,
    toDate,
    openCancelReceiptModal,
    onCloseCancelReceiptModal,
    onClickCancelReceipt,
    openCancelReceiptModalRefund,
    onCloseCancelReceiptModalRefund,
    ExportDocumentTableToExcel,
    ExportOrderItemsTableToExcel,
    isFetching,
    openChangeProductionStatusModal,
    isSelectedCLoseAsDeliveryNote,
    isSelectedCLoseAsProformaInvoice,
    onClickCloseChangeProductionStatusModal,
    moveOrderToReady,
    onChangeDocumentSearch,
    isPausedOrders,
    setIsPausedOrders,
    isOrdersWithoutJobs,
    setIsOrdersWithoutJobs,
    openDuplicateModal,
    onCloseDuplicateModal,
    onClickDocumentDuplicate,
    searchInputRef,
    openExportReceiptPdfModal,
    onCloseExportReceiptPdfModal,
    startOrderProduction,
    openStartProductionModal,
    onClickCloseStartProductionModal,
    getValueByKey,
    isOrder,
    isQuote,
    isReceipt,
    isSupplierPayments,
    isDeliveryNote,
    isProformaInvoice,
    isInvoice,
    deliveryStatuses,
    deliveryStatus,
    setDeliveryStatus,
    isPurchaseDocument,
    isSendToClient,
    setFromDate,
    setToDate,
    onClickOpenCustomerModal,
    openInventoryIntakeModal,
    onClickCloseInventoryIntakeModal,
    inventoryData,
    inventoryLoading,
    isPurchaseOrder,
    isMoveToReady,
    handleCheckboxChange,
    direction,
    handleSortBE,
    isDescOrderBE,
    isHeaderSortable,
    onClickCloseReopenDocumentModal,
    setOpenReopenDocModal,
    openReopenDocModal,
    OpenDocument,
    deliveryOrderId,
    deliveryOrderNumber,
    deliveryCustomerId,
    isSendOrderReadyToClient,
    alertNotVerifiedMailModal,
    alertNotVerifiedModalState,
    showAlertModal,
    onClickCloseOrderCallback,
    handleRemoveDraftBtnClick,
    removeDraftModal,
    onCloseOpenRemoveDraftModal,
    MoveOrderToReady,
    onClickCloseMoveOrderToReadyModal,
    onClickPrintPackagingSlip,
    handleQuantityPerPackageChange,
    handleQuantityOfPackagesChange,
    quantityPerPackage,
    quantityOfPackages,
    renderPackageInput,
    onClosePackagesModal,
    openPackagesModal,
    openPrintExportModal,
    setOpenPrintExportModal,
    exportPdf,
    printPackingSlip,
    isPrintHouseHasPackagingSlipPrinter,
    paymentType,
    handlePaymentTypeChange,
    typeOfPayments,
    initialPageSize,
    ExportDocumentTableToPdf,
    currentSortOrder,
    tableKey,
    visibleHeaders,
    columnsReady,
    setColumnsReady,
    setColumnOrder,
    setColumnVisibility,
    callApi,
    onClickOpenMoveOrderToReadyModal,
    moveToReadyOption,
    setMoveToReadyOption,
    handleRadioChange,
    primaryColor,
    currencyFilter,
    setCurrencyFilter,
    currencies,
    isDocumentTableLoading,
    isDocumentTableError,
    isSocketRefreshingDocuments,
    scheduledReports,
    totalCount: scheduledReportsTotalCount,
    pageNumber: scheduledReportsPageNumber,
    pagesCountScheduledReports: scheduledReportsPagesCount,
    isLoading: scheduledReportsIsLoading,
    createScheduledReport,
    handlePageChange: handleScheduledReportsPageChange,
    handlePageSizeChangeScheduledReports: handleScheduledReportsPageSizeChange,
    scheduledReportsPageSize,
    openPurchaseOrderTypeModal,
    onOpenPurchaseOrderTypeModal,
    onClosePurchaseOrderTypeModal,
    handleSelectPurchaseOrderType,
    // Send to Customer handlers
    onClickSendToCustomerEmail,
    onClickSendToCustomerSMS,
    onClickSendToCustomerWhatsApp,
    // Send Payment handlers
    onClickSendPaymentEmail,
    onClickSendPaymentSMS,
    onClickSendPaymentWhatsApp,
    // Email composer state
    isEmailComposerOpen,
    emailData,
    isLoadingEmailData,
    closeEmailComposer,
    openEmailComposer,
    isFromPaymentPage,
    // Send to customer modal state
    hideCheckboxes,
    preSelectedMethod,
    documentForEmail,
    // Intake validation dialog
    showIntakeValidationDialog,
    handleOpenIntakeFromValidation,
    handleCancelIntakeValidation,
    // Undo intake handlers
    handleUndoDocumentIntake,
    handleUndoItemIntake,
    showUndoIntakeDialog,
    handleConfirmUndoIntake,
    handleCancelUndoIntake,
    // Upload files modal
    openUploadFileModal,
    onCloseUploadFilesModal,
    onClickOpenUploadFilesModal,
    quoteForUploadModal,
    getQuoteForUploadModal,
  };
};

export { useQuotes };
